import { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from "react";
import HOTEL_DIRECTORY from "./hotelDirectory";
import { supabase } from './supabase';

async function sbWriteRow(query, label) {
  try {
    const { data, error } = await query;
    if (error) {
      console.error('[sbWriteRow] ' + label + ':', error);
      const { data: _s } = await supabase.auth.getSession();
      if (!_s || !_s.session) {
        alert('სესია ამოიწურა, გთხოვთ ხელახლა შეხვიდეთ');
        if (window._logout) { try { await window._logout(); } catch (e) {} }
        return null;
      }
      alert(label + ' — ვერ შესრულდა: ' + (error.message || error.code || 'უცნობი შეცდომა'));
      return null;
    }
    return data;
  } catch (e) {
    console.error('[sbWriteRow] ' + label + ' thrown:', e);
    alert(label + ' — ქსელის შეცდომა, სცადეთ თავიდან');
    return null;
  }
}
// ეტაპი 0: ყველა Supabase write გადის ამ helper-ზე — error აღარ იკარგება ჩუმად
async function sbWrite(query, label) {
  try {
    const { error } = await query;
    if (error) {
      console.error('[sbWrite] ' + label + ':', error);
      const { data: _s } = await supabase.auth.getSession();
      if (!_s || !_s.session) {
        alert('სესია ამოიწურა, გთხოვთ ხელახლა შეხვიდეთ');
        if (window._logout) { try { await window._logout(); } catch (e) {} }
        return false;
      }
      alert(label + ' — ვერ შესრულდა: ' + (error.message || error.code || 'უცნობი შეცდომა'));
      return false;
    }
    return true;
  } catch (e) {
    console.error('[sbWrite] ' + label + ' thrown:', e);
    alert(label + ' — ქსელის შეცდომა, სცადეთ თავიდან');
    return false;
  }
}


function useLS(key, init) {
  const [val, setVal] = useState(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : init; }
    catch { return init; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} }, [key, val]);
  // Cross-tab sync: poll every 3s + storage event
  useEffect(() => {
    const sync = () => {
      try {
        const s = localStorage.getItem(key);
        if(s) {
          const parsed = JSON.parse(s);
          setVal(prev => {
            const a = JSON.stringify(prev);
            const b = JSON.stringify(parsed);
            return a === b ? prev : parsed;
          });
        }
      } catch {}
    };
    window.addEventListener("storage", sync);
    const id = setInterval(sync, 3000);
    return () => { window.removeEventListener("storage", sync); clearInterval(id); };
  }, [key]);
  return [val, setVal];
}


// ── 24h time formatter (browser-independent) ─────────────
const fmt24 = (d=new Date()) =>
  `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;

// HOTEL_DIRECTORY loaded from hotelDirectory.js
// ── Dark mode ──────────────────────────────────────────────
const DM = {
  light: {
    bg:"#F8FAFC", card:"#ffffff", cardBorder:"rgba(0,0,0,0.06)",
    text:"#111827", sub:"#6B7280", muted:"#9CA3AF",
    input:"#ffffff", inputBorder:"#E5E7EB",
    navBg:"#ffffff", navBorder:"#F1F5F9",
    pageBg:"linear-gradient(160deg,#EBF2FF 0%,#F0EBFF 100%)",
    roleBg:"#ffffff", roleBorder:"#E5E7EB",
  },
  dark: {
    bg:"#0F172A", card:"#1E293B", cardBorder:"rgba(255,255,255,0.06)",
    text:"#F1F5F9", sub:"#94A3B8", muted:"#64748B",
    input:"#1E293B", inputBorder:"#334155",
    navBg:"#1E293B", navBorder:"#334155",
    pageBg:"linear-gradient(160deg,#0F172A 0%,#1E293B 100%)",
    roleBg:"#1E293B", roleBorder:"#334155",
  }
};

const ThemeCtx = createContext({ dark: false, setDark: ()=>{} });
const useTheme = () => useContext(ThemeCtx);

function useBreakpoint() {
  const getMobile = () => window.matchMedia("(max-width: 767px)").matches;
  const getTablet = () => window.matchMedia("(max-width: 1023px)").matches;
  const getSize = () => getMobile() ? "mobile" : getTablet() ? "tablet" : "desktop";
  const [size, setSize] = useState(getSize);
  useEffect(()=>{
    const mq = window.matchMedia("(max-width: 767px)");
    const fn = () => setSize(getSize());
    mq.addEventListener("change", fn);
    window.addEventListener("orientationchange", fn);
    return ()=>{
      mq.removeEventListener("change", fn);
      window.removeEventListener("orientationchange", fn);
    };
  },[]);
  return size;
}

function AppShell({ children }) {
  const { dark } = useTheme();
  return (
    <div style={{
      width:"100%", height:"100dvh",
      background:dark?"#0F172A":"#F8FAFC",
      overflow:"hidden", display:"flex", flexDirection:"column"
    }}>
      {children}
    </div>
  );
}

const THEME = {
  hotel:   { primary:"#1B6EF3", light:"#EBF2FF", dark:"#1355CC", grad:"linear-gradient(135deg,#1B6EF3,#1355CC)" },
  laundry: { primary:"#12B76A", light:"#E6F9F1", dark:"#0A8A4E", grad:"linear-gradient(135deg,#12B76A,#0A8A4E)" },
  driver:  { primary:"#7C3AED", light:"#F0EBFF", dark:"#5B21B6", grad:"linear-gradient(135deg,#7C3AED,#5B21B6)" },
};

const STATUS = {
  PENDING_LAUNDRY_APPROVAL: { label:"ახალი შეკვეთა",     color:"#F59E0B", bg:"#FEF3C7" },
  APPROVED:                 { label:"მიღებულია",          color:"#1B6EF3", bg:"#EBF2FF" },
  REJECTED:                 { label:"უარყოფილი",          color:"#EF4444", bg:"#FEE2E2" },
  DRIVER_ASSIGNED:          { label:"მძღოლი ინიშნება",   color:"#7C3AED", bg:"#F0EBFF" },
  DRIVER_ON_THE_WAY:        { label:"გზაშია",             color:"#1B6EF3", bg:"#EBF2FF" },
  PICKED_UP:                { label:"სასტუმროდან აღებულია", color:"#F97316", bg:"#FFEDD5" },
  RECEIVED_IN_LAUNDRY:      { label:"სამრეცხაოშია",       color:"#12B76A", bg:"#E6F9F1" },
  READY_FOR_DELIVERY:       { label:"მზადაა",             color:"#12B76A", bg:"#E6F9F1" },
  DELIVERY_DRIVER_ASSIGNED: { label:"მძღოლი მიდის",      color:"#7C3AED", bg:"#F0EBFF" },
  DELIVERY_ON_THE_WAY:      { label:"მიტანის გზაზე",     color:"#1B6EF3", bg:"#EBF2FF" },
  DELIVERED:                { label:"ჩაბარდა",            color:"#12B76A", bg:"#E6F9F1" },
  COMPLETED:                { label:"დასრულდა",           color:"#6B7280", bg:"#F3F4F6" },
  PARTIALLY_DELIVERED:      { label:"ნაწილობრივ",          color:"#F59E0B", bg:"#FEF3C7" },
};

const TRACKING_STEPS = [
  { key:"PENDING_LAUNDRY_APPROVAL", label:"🏨 შეკვეთა შეიქმნა" },
  { key:"APPROVED",                 label:"🧺 სამრეცხაომ მიიღო" },
  { key:"DRIVER_ASSIGNED",          label:"🚗 Pickup მძღოლი დაინიშნა" },
  { key:"DRIVER_ON_THE_WAY",        label:"🚗 მძღოლი გზაშია" },
  { key:"PICKED_UP",                label:"📦 სასტუმროდან აღებულია" },
  { key:"RECEIVED_IN_LAUNDRY",      label:"🧺 სამრეცხაოს ჩაბარდა" },
  { key:"READY_FOR_DELIVERY",       label:"✨ მზადაა მისატანად" },
  { key:"DELIVERY_DRIVER_ASSIGNED", label:"🚗 Delivery მძღოლი დაინიშნა" },
  { key:"DELIVERY_ON_THE_WAY",      label:"🚗 მიტანის გზაზე" },
  { key:"DELIVERED",                label:"✅ სასტუმროს ჩაბარდა" },
  { key:"COMPLETED",                label:"🏁 დასრულდა" },
];
const STATUS_ORDER = TRACKING_STEPS.map(s=>s.key);
const ACTIVE_STATUSES = ["PENDING_LAUNDRY_APPROVAL","APPROVED","DRIVER_ASSIGNED","DRIVER_ON_THE_WAY",
  "PICKED_UP","RECEIVED_IN_LAUNDRY","READY_FOR_DELIVERY",
  "DELIVERY_DRIVER_ASSIGNED","DELIVERY_ON_THE_WAY","DELIVERED","PARTIALLY_DELIVERED"];


// Demo seeds removed — all users (hotels, laundries, drivers) load from Supabase

const n = new Date();
// eslint-disable-next-line no-unused-vars
const iso = (d,h=10) => new Date(n.getFullYear(),n.getMonth(),n.getDate()-d,h).toISOString();

const INIT_ORDERS = [];

const INIT_INVOICES = [];

const S = {
  card:  { background:"#fff", borderRadius:12, padding:"10px 12px", boxShadow:"0 1px 6px rgba(0,0,0,0.06)", marginBottom:8 },
  input: { border:"1.5px solid #E5E7EB", borderRadius:10, padding:"10px 14px", fontSize:14, width:"100%", outline:"none", boxSizing:"border-box" },
  lbl:   { display:"flex", flexDirection:"column", gap:5, fontSize:13, fontWeight:600, color:"#374151" },
};

function Badge({ status }) {
  const s = STATUS[status] || { label:status, color:"#6B7280", bg:"#F3F4F6" };
  return <span style={{ background:s.bg, color:s.color, padding:"3px 10px", borderRadius:999, fontSize:11, fontWeight:700, whiteSpace:"nowrap" }}>{s.label}</span>;
}

function Btn({ children, color="#1B6EF3", outline=false, small=false, onClick, style={} }) {
  return (
    <button onClick={onClick} style={{ background:outline?"transparent":color, color:outline?color:"#fff",
      border:`2px solid ${color}`, padding:small?"7px 14px":"11px 18px", borderRadius:10,
      fontWeight:700, fontSize:small?12:14, cursor:"pointer", ...style }}>{children}</button>
  );
}

function Ava({ name, size=36 }) {
  const ini = name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
  return <div style={{ width:size, height:size, borderRadius:"50%", background:"#1B6EF3", color:"#fff",
    display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700,
    fontSize:size*0.35, flexShrink:0 }}>{ini}</div>;
}


// ── OS Notifications ─────────────────────────────────────
function useOSNotifications() {
  const [granted, setGranted] = useState(
    ()=>typeof Notification!=="undefined" && Notification.permission==="granted"
  );

  const request = useCallback(async ()=>{
    if(typeof Notification==="undefined") return false;
    if(Notification.permission==="granted") { setGranted(true); return true; }
    if(Notification.permission==="denied") return false;
    const perm = await Notification.requestPermission();
    const ok = perm==="granted";
    setGranted(ok);
    return ok;
  },[]);

  const send = useCallback((title, body, tag="cpg")=>{
    if(typeof Notification==="undefined") return;
    if(Notification.permission!=="granted") return;
    try {
      if(navigator.serviceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.ready.then(reg=>{
          reg.showNotification(title, {
            body, tag, icon:"/logo192.png",
            badge:"/logo192.png",
            vibrate:[200,100,200],
            requireInteraction:false,
          });
        });
      } else {
        new Notification(title, {body, icon:"/logo192.png", tag});
      }
    } catch(e) {}
  },[]);

  return {granted, request, send};
}
function NotifPermBanner({ onRequest }) {
  const [dismissed, setDismissed] = useState(false);
  if(dismissed || typeof Notification==="undefined" || Notification.permission!=="default") return null;
  return (
    <div style={{background:"#EBF2FF",padding:"8px 16px",
      display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
      <span style={{fontSize:12,color:"#1B6EF3",fontWeight:600}}>
        🔔 შეტყობინებები ჩართე — ახალი შეკვეთებისთვის
      </span>
      <div style={{display:"flex",gap:6}}>
        <button onClick={()=>{onRequest();setDismissed(true);}} style={{
          background:"#1B6EF3",color:"#fff",border:"none",borderRadius:8,
          padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>ჩართვა</button>
        <button onClick={()=>setDismissed(true)} style={{
          background:"none",border:"none",color:"#9CA3AF",fontSize:16,cursor:"pointer"}}>✕</button>
      </div>
    </div>
  );
}
function TrialBanner({ user }) {
  if(!user?.trial_end || user?.sub_status==="active" || user?.sub_status==="suspended") return null;
  const daysLeft = Math.ceil((new Date(user.trial_end)-new Date())/(1000*60*60*24));
  if(daysLeft > 7) return null;
  const urgent = daysLeft <= 3;
  return (
    <div style={{
      background:urgent?"#FEE2E2":"#FEF3C7",
      padding:"6px 16px",
      display:"flex",alignItems:"center",justifyContent:"space-between",gap:8
    }}>
      <span style={{fontSize:12,fontWeight:700,color:urgent?"#991B1B":"#92400E"}}>
        {urgent?"🔴":"🟡"} Trial {daysLeft > 0 ? `კიდევ ${daysLeft} დღე` : "დასრულდა"}
      </span>
      <span style={{fontSize:11,color:urgent?"#991B1B":"#92400E"}}>📞 577 111 725</span>
    </div>
  );
}
function PWAInstallBtn() {
  const {canInstall, install} = usePWAInstall();
  if(!canInstall) return null;
  return (
    <button onClick={install} style={{
      background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)",
      borderRadius:8, padding:"5px 10px", cursor:"pointer",
      color:"#fff", fontSize:11, fontWeight:600, display:"flex", alignItems:"center", gap:5
    }}>
      ⬇️ Install
    </button>
  );
}
function Header({ color, grad, icon, title, sub, onBell, bellBadge=0 }) {
  const { dark, setDark } = useTheme();
  return (
    <div style={{ background:grad, padding:"14px 20px 16px", flexShrink:0, position:"relative", overflow:"hidden" }}>
      {/* CPG watermark */}
      <div style={{
        position:"absolute", right:-10, top:"50%", transform:"translateY(-50%)",
        opacity:0.08, pointerEvents:"none", userSelect:"none",
        fontFamily:"Georgia,serif", fontSize:80, fontWeight:"bold",
        color:"#fff", letterSpacing:-4, lineHeight:1 }}>
        CPG
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", position:"relative" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:18 }}>{icon}</span>
          <span style={{ color:"#fff", fontWeight:700, fontSize:15 }}>{title}</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <button onClick={()=>setDark(v=>!v)} style={{
            background:"rgba(255,255,255,0.18)",border:"none",borderRadius:10,
            width:36,height:36,cursor:"pointer",fontSize:16,
            display:"flex",alignItems:"center",justifyContent:"center"}}
            title={dark?"Light Mode":"Dark Mode"}>
            {dark?"☀️":"🌙"}
          </button>
          <div style={{position:"relative"}}>
            <button onClick={onBell} style={{ background:"rgba(255,255,255,0.18)", border:"none",
              borderRadius:10, width:36, height:36, cursor:"pointer", fontSize:18,
              display:"flex", alignItems:"center", justifyContent:"center" }}>🔔</button>
            {bellBadge>0 && <span style={{position:"absolute",top:-4,right:-4,background:"#EF4444",
              color:"#fff",borderRadius:999,fontSize:9,fontWeight:800,
              padding:"1px 5px",minWidth:16,textAlign:"center"}}>{bellBadge}</span>}
          </div>
        </div>
      </div>
      {sub && <div style={{ marginTop:12 }}>{sub}</div>}
    </div>
  );
}

function BottomNav({ tabs, active, onChange, color, badges={} }) {
  const { dark } = useTheme();
  return (
    <div style={{ background:dark?"#1E293B":"#fff", borderTop:`1px solid ${dark?"#334155":"#F1F5F9"}`, display:"flex", flexShrink:0,
      boxShadow:"0 -2px 12px rgba(0,0,0,0.06)" }}>
      {tabs.map(t => (
        <button key={t.key} onClick={() => onChange(t.key)} style={{
          flex:1, padding:"10px 4px 8px", border:"none", background:"none", cursor:"pointer",
          display:"flex", flexDirection:"column", alignItems:"center", gap:2, position:"relative" }}>
          <span style={{ fontSize:19 }}>{t.icon}</span>
          {badges[t.key] > 0 && (
            <span style={{ position:"absolute", top:7, right:"50%", marginRight:-14,
              background:"#EF4444", color:"#fff", borderRadius:999,
              fontSize:9, fontWeight:800, padding:"1px 5px", minWidth:16, textAlign:"center" }}>
              {badges[t.key]}
            </span>
          )}
          <span style={{ fontSize:9, fontWeight:600, color:active===t.key?color:dark?"#64748B":"#9CA3AF" }}>{t.label}</span>
          {active===t.key && <div style={{ width:18, height:3, borderRadius:999, background:color }}/>}
        </button>
      ))}
    </div>
  );
}


function SideBar({ tabs, active, onChange, color, badges={}, isMobile=false, pinned=[], onTogglePin }) {
  const { dark } = useTheme();
  const bg = dark?"#1E293B":"#fff";
  const border = dark?"#334155":"#F1F5F9";
  const defaultW = isMobile ? 56 : 230;
  const [width, setWidth] = useState(defaultW);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(defaultW);

  useEffect(()=>{ setWidth(isMobile?56:230); },[isMobile]);

  const onMouseDown = (e) => {
    dragging.current = true;
    startX.current = e.clientX;
    startW.current = width;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  const onTouchStart = (e) => {
    dragging.current = true;
    startX.current = e.touches[0].clientX;
    startW.current = width;
  };

  useEffect(()=>{
    const onMove = (e) => {
      if(!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const newW = Math.min(320, Math.max(56, startW.current + x - startX.current));
      setWidth(newW);
    };
    const onUp = () => {
      dragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, {passive:true});
    window.addEventListener("touchend", onUp);
    return ()=>{
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  },[]);

  const groups = [];
  let current = [];
  tabs.forEach(t=>{
    if(t.divider){ groups.push(current); current=[]; }
    else current.push(t);
  });
  groups.push(current);

  const showLabel = width > 100;

  return (
    <div style={{
      width, flexShrink:0, position:"relative",
      background:bg,
      borderRight:`1px solid ${border}`,
      display:"flex", flexDirection:"column",
      overflowY:"auto", overflowX:"hidden",
      boxShadow:"2px 0 10px rgba(0,0,0,0.06)",
    }}>
      {groups.map((group,gi)=>(
        <div key={gi}>
          {gi>0 && showLabel && <div style={{height:1,background:border,margin:"4px 12px"}}/>}
          {group.map(t=>{
            const isActive = active===t.key;
            return (
              <div key={t.key} style={{display:"flex",alignItems:"center",position:"relative",
                borderLeft:`3px solid ${isActive?color:"transparent"}`,
                background:isActive?(dark?"rgba(255,255,255,0.07)":`${color}12`):"transparent",
                transition:"background 0.15s"}}>
                <button onClick={()=>onChange(t.key)}
                  onContextMenu={e=>{e.preventDefault();onTogglePin&&onTogglePin(t.key);}}
                  style={{
                  display:"flex", alignItems:"center",
                  gap:showLabel?12:0,
                  padding:showLabel?"11px 14px":"12px 0",
                  justifyContent:"center",
                  flex:1,
                  border:"none", cursor:"pointer",
                  background:"transparent",
                }}>
                  <span style={{fontSize:showLabel?18:20}}>{t.icon}</span>
                  {showLabel && (
                    <span style={{
                      fontSize:13, fontWeight:isActive?700:500,
                      color:isActive?color:dark?"#94A3B8":"#4B5563",
                      flex:1, textAlign:"left", whiteSpace:"nowrap",
                      overflow:"hidden", textOverflow:"ellipsis"
                    }}>{t.label}</span>
                  )}
                  {showLabel && badges[t.key]>0 && (
                    <span style={{
                      background:"#EF4444", color:"#fff",
                      borderRadius:999, fontSize:10, fontWeight:800,
                      padding:"1px 7px", minWidth:18, textAlign:"center"
                    }}>{badges[t.key]}</span>
                  )}
                  {!showLabel && badges[t.key]>0 && (
                    <span style={{
                      position:"absolute", top:6, right:4,
                      background:"#EF4444", color:"#fff",
                      borderRadius:999, fontSize:9, fontWeight:800,
                      padding:"1px 4px", minWidth:14, textAlign:"center"
                    }}>{badges[t.key]}</span>
                  )}
                </button>
                {showLabel && onTogglePin && t.key!=="პროფილი" && t.key!=="help" && t.key!=="სხვა" && (
                  <button onClick={e=>{e.stopPropagation();onTogglePin(t.key);}} style={{
                    background:"none", border:"none", cursor:"pointer",
                    padding:"0 10px 0 0", fontSize:14,
                    color:pinned.includes(t.key)?"#F59E0B":"#D1D5DB",
                    transition:"color 0.15s, transform 0.15s",
                    transform:pinned.includes(t.key)?"scale(1.2)":"scale(1)"
                  }}>{pinned.includes(t.key)?"⭐":"☆"}</button>
                )}
                {!showLabel && pinned.includes(t.key) && t.key!=="პროფილი" && t.key!=="help" && t.key!=="სხვა" && (
                  <span style={{
                    position:"absolute", bottom:3, right:3,
                    fontSize:8, lineHeight:1, pointerEvents:"none", zIndex:5
                  }}>⭐</span>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Drag handle */}
      <div
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        style={{
          position:"absolute", top:0, right:0,
          width:6, height:"100%",
          cursor:"col-resize",
          background:"transparent",
          zIndex:10,
        }}
        onMouseEnter={e=>e.currentTarget.style.background=`${color}44`}
        onMouseLeave={e=>e.currentTarget.style.background="transparent"}
      />
    </div>
  );
}

function NavBar({ tabs, bottomTabs, active, onChange, color, badges={}, pinned=[], onTogglePin, userCode="" }) {
  const bp = useBreakpoint();
  const isMobile = bp === "mobile";
  return <SideBar tabs={tabs} active={active} onChange={onChange} color={color} badges={badges} isMobile={isMobile} pinned={pinned} onTogglePin={onTogglePin}/>;
}

function InviteModal({ laundry, hotel, onClose }) {
  const bp = useBreakpoint();
  const {pos:iPos, onMouseDown:iMD, onTouchStart:iTS} = useDraggable(0, 0);
  const isMob = bp==="mobile";
  const defaultMsg = `გამარჯობა! 🧺 ${laundry.name} გიწვევთ Connective Platform Georgia-ზე.\n\nჩვენ გთავაზობთ:\n✅ სწრაფ მომსახურებას\n✅ მაღალ ხარისხს\n✅ კონკურენტულ ფასებს\n\nდარეგისტრირდით და გახდით ჩვენი პარტნიორი:`;
  const [msg, setMsg] = useState(defaultMsg);
  const [copied, setCopied] = useState(false);
  const { dark } = useTheme();

  const inviteLink = `${window.location.origin}${window.location.pathname}?invite=${laundry.code}&iname=${encodeURIComponent(laundry.name)}${hotel?.code?`&for=${hotel.code}`:""}`;
  const fullText = `${msg}\n${inviteLink}`;

  const copy = () => {
    navigator.clipboard.writeText(fullText).then(()=>{
      setCopied(true);
      setTimeout(()=>setCopied(false), 2000);
    });
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",
      display:"flex",alignItems:isMob?"flex-end":"center",justifyContent:"center",zIndex:500}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{width:"100%",maxWidth:480,background:dark?"#1E293B":"#fff",
        borderRadius:isMob?"18px 18px 0 0":16,padding:"20px 18px 32px",boxSizing:"border-box",
        transform:(!isMob&&(iPos.x||iPos.y))?`translate(${iPos.x}px,${iPos.y}px)`:undefined,
        boxShadow:"0 8px 32px rgba(0,0,0,0.25)"}}>
        <div
          onMouseDown={!isMob?iMD:undefined}
          onTouchStart={!isMob?iTS:undefined}
          style={{display:"flex",justifyContent:"space-between",alignItems:"center",
            marginBottom:14,cursor:isMob?"default":"grab",userSelect:"none"}}>
          <h3 style={{margin:0,fontSize:15,fontWeight:800,color:dark?"#F1F5F9":"#111827"}}>
            📩 მოწვევის გაგზავნა
          </h3>
          <button onClick={onClose} style={{background:"#F3F4F6",border:"none",
            borderRadius:"50%",width:28,height:28,cursor:"pointer",fontSize:14}}>✕</button>
        </div>

        {hotel && (
          <div style={{background:dark?"#0F172A":"#F8FAFC",borderRadius:10,
            padding:"8px 12px",marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:16}}>🏨</span>
            <div>
              <p style={{margin:0,fontSize:12,fontWeight:700,color:dark?"#F1F5F9":"#111827"}}>{hotel.name}</p>
              <p style={{margin:0,fontSize:11,color:"#6B7280"}}>{hotel.code}</p>
            </div>
          </div>
        )}

        <label style={{display:"block",marginBottom:6,fontSize:12,fontWeight:600,
          color:dark?"#94A3B8":"#374151"}}>შეტყობინების ტექსტი:</label>
        <textarea
          value={msg}
          onChange={e=>setMsg(e.target.value)}
          rows={6}
          style={{width:"100%",boxSizing:"border-box",borderRadius:10,
            border:dark?"1px solid #334155":"1px solid #E5E7EB",
            background:dark?"#0F172A":"#F8FAFC",color:dark?"#F1F5F9":"#111827",
            padding:"10px 12px",fontSize:12,fontFamily:"inherit",resize:"vertical",
            outline:"none",lineHeight:1.5}}/>

        <div style={{background:dark?"#0F172A":"#EFF6FF",borderRadius:10,
          padding:"8px 12px",margin:"10px 0",wordBreak:"break-all"}}>
          <p style={{margin:0,fontSize:11,color:"#1B6EF3",fontWeight:500}}>{inviteLink}</p>
        </div>

        <button onClick={copy} style={{
          width:"100%",padding:"13px",borderRadius:12,border:"none",cursor:"pointer",
          background:copied?"#16A34A":"#1B6EF3",color:"#fff",
          fontWeight:700,fontSize:14,transition:"background 0.2s"}}>
          {copied ? "✅ კოპირებულია!" : "📋 ტექსტი + ლინკი კოპირება"}
        </button>
        <p style={{textAlign:"center",fontSize:11,color:"#9CA3AF",margin:"8px 0 0"}}>
          WhatsApp / SMS-ში ჩასვი და გაგზავნე
        </p>
      </div>
    </div>
  );
}
function PartnersPage({ user, hotels, laundries, partnerRequests, onSendRequest, onPartnerResponse, priceLists={} }) {
  const [showAdd, setShowAdd] = useState(false);
  const [inputCode, setInputCode] = useState("");
  const [sendErr, setSendErr] = useState("");
  const [sendOk, setSendOk] = useState("");

  const accepted = partnerRequests.filter(r=>
    (r.from_code===user.code || r.to_code===user.code) && r.status==="accepted"
  );
  const incoming = partnerRequests.filter(r=>r.to_code===user.code && r.status==="pending");
  const outgoing = partnerRequests.filter(r=>r.from_code===user.code && r.status==="pending");

  const partnerCode = (r) => r.from_code===user.code ? r.to_code : r.from_code;
  const partnerName = (r) => r.from_code===user.code ? r.to_name : r.from_name;

  const getPartnerInfo = (code) => {
    return hotels.find(h=>h.code===code) || laundries.find(l=>l.code===code);
  };

  const doSend = async () => {
    setSendErr(""); setSendOk("");
    const res = await onSendRequest(user, inputCode);
    if (res.error) { setSendErr(res.error); return; }
    setSendOk(`✅ მოთხოვნა გაიგზავნა — ${res.to_name}`);
    setInputCode("");
    setTimeout(()=>setSendOk(""),3000);
  };

  const color = user.role==="hotel" ? THEME.hotel.primary : THEME.laundry.primary;
  const partnerLabel = user.role==="hotel" ? "სამრეცხაო" : "სასტუმრო";

  return (
    <div style={{padding:"14px 14px 14px"}}>
      {incoming.length > 0 && (
        <div style={{marginBottom:14}}>
          <p style={{margin:"0 0 8px",fontWeight:800,fontSize:14}}>
            📬 შემომავალი მოთხოვნები
            <span style={{marginLeft:6,background:"#EF4444",color:"#fff",borderRadius:999,
              padding:"1px 7px",fontSize:11,fontWeight:700}}>{incoming.length}</span>
          </p>
          {incoming.map(r=>(
            <div key={r.id} style={{...S.card,marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                <Ava name={r.from_name} size={36}/>
                <div style={{flex:1}}>
                  <p style={{margin:0,fontWeight:700,fontSize:13}}>{r.from_name}</p>
                  <p style={{margin:"2px 0 0",fontSize:11,color:"#6B7280"}}>{r.from_code}</p>
                </div>
              </div>
              <div style={{display:"flex",gap:8}}>
                <Btn color={color} small onClick={()=>onPartnerResponse(r.id,true)} style={{flex:1}}>✅ დათანხმება</Btn>
                <Btn color="#EF4444" outline small onClick={()=>onPartnerResponse(r.id,false)} style={{flex:1}}>❌ უარი</Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      {outgoing.length > 0 && (
        <div style={{marginBottom:14}}>
          <p style={{margin:"0 0 8px",fontWeight:700,fontSize:13,color:"#6B7280"}}>⏳ მოლოდინში</p>
          {outgoing.map(r=>(
            <div key={r.id} style={{...S.card,marginBottom:8,display:"flex",alignItems:"center",gap:10}}>
              <Ava name={r.to_name} size={34}/>
              <div>
                <p style={{margin:0,fontWeight:700,fontSize:13}}>{r.to_name}</p>
                <p style={{margin:"2px 0 0",fontSize:11,color:"#F59E0B"}}>მოლოდინშია...</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <p style={{margin:0,fontWeight:800,fontSize:14}}>
          პარტნიორი {partnerLabel}ები
          <span style={{marginLeft:6,background:color,color:"#fff",borderRadius:999,
            padding:"1px 7px",fontSize:11,fontWeight:700}}>{accepted.length}</span>
        </p>
        <button onClick={()=>{setShowAdd(true);setSendErr("");setSendOk("");}} style={{
          background:color,color:"#fff",border:"none",borderRadius:8,
          width:30,height:30,fontSize:20,cursor:"pointer",
          display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700}}>+</button>
      </div>

      {accepted.length===0 && !showAdd && (
        <div style={{textAlign:"center",padding:"30px 0",color:"#9CA3AF"}}>
          <p style={{fontSize:32,margin:"0 0 8px"}}>🤝</p>
          <p style={{fontSize:13,margin:0}}>პარტნიორი {partnerLabel} არ არის</p>
          <p style={{fontSize:12,margin:"4px 0 0"}}>+ ღილაკით დაამატე</p>
        </div>
      )}

      {accepted.map(r=>{
        const info = getPartnerInfo(partnerCode(r));
        return (
          <div key={r.id} style={{...S.card}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
              <Ava name={partnerName(r)} size={40}/>
              <div style={{flex:1}}>
                <p style={{margin:0,fontWeight:700,fontSize:13}}>{partnerName(r)}</p>
                <p style={{margin:"2px 0 0",fontSize:11,color:"#6B7280"}}>{partnerCode(r)}</p>
                {info?.city       && <p style={{margin:"2px 0 0",fontSize:11,color:"#6B7280"}}>🏙️ {info.city}</p>}
                {info?.address    && <p style={{margin:"2px 0 0",fontSize:11,color:"#9CA3AF"}}>📍 {info.address}</p>}
                {info?.rooms_count && <p style={{margin:"2px 0 0",fontSize:11,color:"#6B7280"}}>🛏️ {info.rooms_count} ნომერი</p>}
                {info?.phone      && <p style={{margin:"2px 0 0",fontSize:11,color:"#374151"}}>📞 {info.phone}</p>}
              </div>
              <span style={{background:"#D1FAE5",color:"#065F46",padding:"3px 8px",
                borderRadius:999,fontSize:11,fontWeight:600}}>აქტიური</span>
            </div>
            {user.role==="hotel" && (()=>{
              const lCode = r.from_role==="laundry"?r.from_code:r.to_code;
              const pl = priceLists[`${lCode}_${user.code}`];
              if(!pl||pl.items.length===0) return <p style={{margin:0,fontSize:11,color:"#F59E0B"}}>⚠️ ფასები ჯერ არ დაუყენებია სამრეცხაოს</p>;
              return (
                <div style={{background:"#F0FDF4",borderRadius:8,padding:"8px 10px"}}>
                  <p style={{margin:"0 0 5px",fontSize:11,fontWeight:700,color:"#065F46"}}>💰 ჩემი ფასები · {pl.updatedAt}</p>
                  <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                    {pl.items.map((it,i)=>(
                      <span key={i} style={{background:"#D1FAE5",color:"#065F46",
                        padding:"2px 8px",borderRadius:999,fontSize:10,fontWeight:600}}>
                        {it.name}: ₾{it.price}/{it.unit}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })}

      {showAdd && (
        <div style={{...S.card,marginTop:4}}>
          <p style={{margin:"0 0 10px",fontWeight:700,fontSize:13}}>
            {user.role==="hotel" ? "სამრეცხაოს" : "სასტუმროს"} კოდი
          </p>
          {sendErr && <p style={{color:"#EF4444",fontSize:12,margin:"0 0 8px"}}>{sendErr}</p>}
          {sendOk  && <p style={{color:"#12B76A",fontSize:12,margin:"0 0 8px"}}>{sendOk}</p>}
          <div style={{display:"flex",gap:8}}>
            <input
              placeholder={user.role==="hotel"?"LND-XXXX":"HTL-XXXX"}
              value={inputCode}
              onChange={e=>setInputCode(e.target.value.toUpperCase())}
              style={{...S.input,flex:1}}
            />
            <Btn color={color} small onClick={doSend}>გაგზავნა</Btn>
          </div>
          <button onClick={()=>setShowAdd(false)} style={{background:"none",border:"none",
            color:"#9CA3AF",fontSize:12,cursor:"pointer",marginTop:8,padding:0}}>გაუქმება</button>
        </div>
      )}
    </div>
  );
}



function usePWAInstall() {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  useEffect(()=>{
    const handler = (e)=>{ e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", ()=>setInstalled(true));
    return()=>window.removeEventListener("beforeinstallprompt", handler);
  },[]);
  const install = async ()=>{
    if(!prompt) return;
    prompt.prompt();
    const {outcome} = await prompt.userChoice;
    if(outcome==="accepted") setInstalled(true);
    setPrompt(null);
  };
  return {canInstall: !!prompt && !installed, install};
}
function useDraggable(initX=null, initY=null) {
  const [pos, setPos] = useState({x:initX, y:initY});
  const dragging = useRef(false);
  const start = useRef({mx:0,my:0,ox:0,oy:0});

  const onMouseDown = useCallback((e)=>{
    if(e.button!==0) return;
    dragging.current=true;
    start.current={mx:e.clientX,my:e.clientY,ox:pos.x||0,oy:pos.y||0};
    document.body.style.userSelect="none";
    document.body.style.cursor="grabbing";
  },[pos]);

  const onTouchStart = useCallback((e)=>{
    dragging.current=true;
    start.current={mx:e.touches[0].clientX,my:e.touches[0].clientY,ox:pos.x||0,oy:pos.y||0};
  },[pos]);

  useEffect(()=>{
    const move=(e)=>{
      if(!dragging.current) return;
      const cx=e.touches?e.touches[0].clientX:e.clientX;
      const cy=e.touches?e.touches[0].clientY:e.clientY;
      setPos({x:start.current.ox+(cx-start.current.mx), y:start.current.oy+(cy-start.current.my)});
    };
    const up=()=>{
      dragging.current=false;
      document.body.style.userSelect="";
      document.body.style.cursor="";
    };
    window.addEventListener("mousemove",move);
    window.addEventListener("mouseup",up);
    window.addEventListener("touchmove",move,{passive:true});
    window.addEventListener("touchend",up);
    return()=>{
      window.removeEventListener("mousemove",move);
      window.removeEventListener("mouseup",up);
      window.removeEventListener("touchmove",move);
      window.removeEventListener("touchend",up);
    };
  },[]);
  return {pos, onMouseDown, onTouchStart};
}
function ProfileEditModal({ user, onClose, onSave }) {
  const [form, setForm] = useState({
    name:    user.name    || "",
    phone:   user.phone   || "",
    email:   user.email   || "",
    contact: user.contact || "",
    address: user.address || "",
    city:    user.city    || "",
    rooms_count: user.rooms_count || "",
  });
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  return (
    <Modal title="მონაცემების რედაქტირება" onClose={onClose}>
      <div style={{display:"flex",flexDirection:"column",gap:11}}>
        <label style={S.lbl}>სახელი / კომპანია
          <input value={form.name} onChange={e=>set("name",e.target.value)} style={S.input}/>
        </label>
        <label style={S.lbl}>საკონტაქტო პირი (სახელი გვარი)
          <input placeholder="მაგ. გიორგი ბერიძე" value={form.contact}
            onChange={e=>set("contact",e.target.value)} style={S.input}/>
        </label>
        <label style={S.lbl}>ტელეფონის ნომერი
          <input placeholder="+995 5XX XX XX XX" value={form.phone}
            onChange={e=>set("phone",e.target.value)} style={S.input}/>
        </label>
        <label style={S.lbl}>ელ-ფოსტა
          <input type="email" placeholder="example@gmail.com" value={form.email}
            onChange={e=>set("email",e.target.value)} style={S.input}/>
        </label>
        <label style={S.lbl}>ქალაქი
          <input placeholder="თბილისი" value={form.city}
            onChange={e=>set("city",e.target.value)} style={S.input}/>
        </label>
        <label style={S.lbl}>მისამართი
          <input placeholder="ქ. თბილისი, ..." value={form.address}
            onChange={e=>set("address",e.target.value)} style={S.input}/>
        </label>
        <label style={S.lbl}>ნომრების რაოდენობა
          <input type="number" placeholder="მაგ. 45" value={form.rooms_count}
            onChange={e=>set("rooms_count",e.target.value)} style={S.input}/>
        </label>
        <Btn color="#1B6EF3" onClick={()=>onSave(form)} style={{width:"100%",marginTop:4}}>
          ✅ შენახვა
        </Btn>
      </div>
    </Modal>
  );
}

function ProfileCard({ user, onEdit, color, extra, contacts, setContacts }) {
  const [showAddContact, setShowAddContact] = useState(false);
  const [cName, setCName] = useState("");
  const [cPhone, setCPhone] = useState("");

  const addContact = () => {
    if(!cName.trim()) return;
    setContacts(p=>[...p, {id:Date.now(), name:cName.trim(), phone:cPhone.trim()}]);
    setCName(""); setCPhone(""); setShowAddContact(false);
  };

  const [showPw, setShowPw] = useState(false);
  return (
    <div style={{padding:"14px 14px 14px"}}>
      <div style={{...S.card, textAlign:"center", padding:24}}>
        <Ava name={user.name} size={56}/>
        <h2 style={{margin:"10px 0 3px",fontWeight:800,fontSize:16}}>{user.name}</h2>
        <p style={{margin:0,color:"#6B7280",fontSize:12}}>{user.code}</p>
        {user.contact && <p style={{margin:"3px 0 0",fontSize:12,color:"#374151"}}>👤 {user.contact}</p>}
        {user.phone   && <p style={{margin:"3px 0 0",fontSize:12,color:"#374151"}}>📞 {user.phone}</p>}
        {user.email   && <p style={{margin:"3px 0 0",fontSize:12,color:"#374151"}}>✉️ {user.email}</p>}
        {user.city    && <p style={{margin:"3px 0 0",fontSize:12,color:"#6B7280"}}>🏙️ {user.city}</p>}
        {user.address && <p style={{margin:"3px 0 0",fontSize:12,color:"#6B7280"}}>📍 {user.address}</p>}
        <button onClick={onEdit} style={{marginTop:12,background:color,color:"#fff",border:"none",
          borderRadius:8,padding:"8px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
          ✏️ მონაცემების შეცვლა
        </button>
        <button onClick={()=>setShowPw(true)} style={{marginTop:8,marginLeft:8,background:"transparent",
          color:color,border:"1px solid "+color,borderRadius:8,padding:"8px 20px",
          fontWeight:700,fontSize:13,cursor:"pointer"}}>
          🔑 პაროლის შეცვლა
        </button>
      </div>
      {showPw && <PasswordChangeModal onClose={()=>setShowPw(false)}/>}

      {/* Contacts section */}
      <div style={{...S.card,marginBottom:10}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <p style={{margin:0,fontWeight:700,fontSize:13}}>👥 საკონტაქტო პირები</p>
          <button onClick={()=>setShowAddContact(v=>!v)} style={{
            background:color,color:"#fff",border:"none",borderRadius:8,
            width:26,height:26,fontSize:18,cursor:"pointer",
            display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700}}>+</button>
        </div>
        {showAddContact && (
          <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:10,
            background:"#F8FAFC",borderRadius:10,padding:10}}>
            <input placeholder="სახელი გვარი" value={cName} onChange={e=>setCName(e.target.value)} style={S.input}/>
            <input placeholder="+995 5XX XX XX XX" value={cPhone} onChange={e=>setCPhone(e.target.value)} style={S.input}/>
            <div style={{display:"flex",gap:8}}>
              <Btn color={color} small onClick={addContact} style={{flex:1}}>შენახვა</Btn>
              <Btn color="#6B7280" outline small onClick={()=>setShowAddContact(false)} style={{flex:1}}>გაუქმება</Btn>
            </div>
          </div>
        )}
        {(contacts||[]).length===0 && !showAddContact && (
          <p style={{margin:0,fontSize:12,color:"#9CA3AF"}}>საკონტაქტო პირი არ არის</p>
        )}
        {(contacts||[]).map(c=>(
          <div key={c.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",
            padding:"6px 0",borderBottom:"1px solid #F1F5F9"}}>
            <div>
              <p style={{margin:0,fontWeight:600,fontSize:13}}>👤 {c.name}</p>
              {c.phone && <p style={{margin:"1px 0 0",fontSize:12,color:"#6B7280"}}>📞 {c.phone}</p>}
            </div>
            <button onClick={()=>setContacts(p=>p.filter(x=>x.id!==c.id))}
              style={{background:"none",border:"none",color:"#EF4444",cursor:"pointer",fontSize:16}}>✕</button>
          </div>
        ))}
      </div>

      {extra}
      <Btn color="#EF4444" outline onClick={()=>window._logout()} style={{width:"100%"}}>გამოსვლა</Btn>
    </div>
  );
}

function HotelProfile({ user, color }) {
  const [showEdit, setShowEdit] = useState(false);
  const [contacts, setContacts] = useLS(`contacts_${user.code}`, []);
  const [, forceUpdate] = useState(0);
  return (
    <>
      <ProfileCard user={user} color={color} onEdit={()=>setShowEdit(true)}
        contacts={contacts} setContacts={setContacts}
        extra={user.rooms_count ? <div style={{...S.card,marginBottom:10,textAlign:"center"}}>
          <p style={{margin:"0 0 3px",fontSize:12,color:"#6B7280"}}>სასტუმროს ნომრები</p>
          <p style={{margin:0,fontWeight:800,fontSize:22}}>{user.rooms_count}</p>
        </div> : null}
      />
      {showEdit && <ProfileEditModal user={user} onClose={()=>setShowEdit(false)}
        onSave={async d=>{const okPf=await sbWrite(supabase.from('users').update({name:d.name,phone:d.phone,email:d.email,contact:d.contact,address:d.address,city:d.city,rooms_count:d.rooms_count||""}).eq('code',user.code), 'პროფილის შენახვა');if(!okPf)return;Object.assign(user,d);setShowEdit(false);forceUpdate(n=>n+1);}}/>}
    </>
  );
}

function LaundryProfile({ user, color, myHotels }) {
  const [showEdit, setShowEdit] = useState(false);
  const [contacts, setContacts] = useLS(`contacts_${user.code}`, []);
  const [, forceUpdate] = useState(0);
  return (
    <>
      <ProfileCard user={user} color={color} onEdit={()=>setShowEdit(true)}
        contacts={contacts} setContacts={setContacts}
        extra={
          <div style={{...S.card,marginBottom:10}}>
            <p style={{margin:"0 0 3px",fontSize:12,color:"#6B7280"}}>დაკავშირებული სასტუმროები</p>
            <p style={{margin:0,fontWeight:800,fontSize:22}}>{myHotels.length}</p>
            <p style={{margin:"5px 0 0",fontSize:12,color:"#9CA3AF"}}>
              გასაზიარებელი კოდი: <strong>{user.code}</strong>
            </p>
          </div>
        }
      />
      {showEdit && <ProfileEditModal user={user} onClose={()=>setShowEdit(false)}
        onSave={async d=>{const okPf=await sbWrite(supabase.from('users').update({name:d.name,phone:d.phone,email:d.email,contact:d.contact,address:d.address,city:d.city,rooms_count:d.rooms_count||""}).eq('code',user.code), 'პროფილის შენახვა');if(!okPf)return;Object.assign(user,d);setShowEdit(false);forceUpdate(n=>n+1);}}/>}
    </>
  );
}

function DriverProfile({ user, color }) {
  const [showEdit, setShowEdit] = useState(false);
  const [contacts, setContacts] = useLS(`contacts_${user.code}`, []);
  const [, forceUpdate] = useState(0);
  return (
    <>
      <ProfileCard user={user} color={color} onEdit={()=>setShowEdit(true)}
        contacts={contacts} setContacts={setContacts}/>
      {showEdit && <ProfileEditModal user={user} onClose={()=>setShowEdit(false)}
        onSave={async d=>{const okPf=await sbWrite(supabase.from('users').update({name:d.name,phone:d.phone,email:d.email,contact:d.contact,address:d.address,city:d.city,rooms_count:d.rooms_count||""}).eq('code',user.code), 'პროფილის შენახვა');if(!okPf)return;Object.assign(user,d);setShowEdit(false);forceUpdate(n=>n+1);}}/>}
    </>
  );
}

function Modal({ title, onClose, children }) {
  const { dark } = useTheme();
  const bp = useBreakpoint();
  const {pos, onMouseDown, onTouchStart} = useDraggable(0, 0);
  const isMob = bp==="mobile";

  const transform = (!isMob && (pos.x||pos.y)) ? `translate(${pos.x}px,${pos.y}px)` : undefined;

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)",
      display:"flex", alignItems:isMob?"flex-end":"center", justifyContent:"center", zIndex:400 }}>
      <div style={{ background:dark?"#1E293B":"#fff",
        borderRadius:isMob?"16px 16px 0 0":16, padding:"12px 14px 16px",
        width:"100%", maxWidth:480, maxHeight:"80vh", overflowY:"auto",
        transform, transition: "none",
        boxShadow:"0 8px 32px rgba(0,0,0,0.25)" }}>
        <div
          onMouseDown={!isMob ? onMouseDown : undefined}
          onTouchStart={!isMob ? onTouchStart : undefined}
          style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
            marginBottom:10, cursor:isMob?"default":"grab", userSelect:"none" }}>
          <h2 style={{ fontWeight:800, fontSize:15, margin:0, color:dark?"#F1F5F9":"#111827" }}>{title}</h2>
          <button onClick={onClose} style={{ background:"#F3F4F6", border:"none", borderRadius:"50%",
            width:26, height:26, cursor:"pointer", fontSize:13, fontWeight:700 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Tracking({ order }) {
  const ci = STATUS_ORDER.indexOf(order.status);
  return (
    <div>
      {TRACKING_STEPS.filter((_,i)=>i<=Math.max(ci,0)).map((step,i,arr)=>{
        const done    = STATUS_ORDER.indexOf(step.key) <= ci;
        const current = step.key === order.status;
        return (
          <div key={step.key} style={{ display:"flex", gap:10 }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
              <div style={{ width:13,height:13,borderRadius:"50%",marginTop:2,flexShrink:0,
                background:current?"#1B6EF3":done?"#12B76A":"#E5E7EB",
                border:current?"3px solid #93C5FD":"none" }}/>
              {i<arr.length-1 && <div style={{ width:2,height:22,background:"#E5E7EB",margin:"2px 0" }}/>}
            </div>
            <div style={{ paddingBottom:i<arr.length-1?18:0 }}>
              <p style={{ margin:0, fontSize:12, fontWeight:current?700:500,
                color:current?"#1B6EF3":done?"#111827":"#9CA3AF" }}>{step.label}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function StatBlocks({ stats, onFilter }) {
  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
      {stats.map(s => (
        <div key={s.label} onClick={() => s.orders?.length && onFilter(s)}
          style={{ background:"rgba(255,255,255,0.15)", borderRadius:12, padding:"10px 12px",
            cursor:s.orders?.length?"pointer":"default" }}>
          <div style={{ display:"flex", alignItems:"baseline", gap:6 }}>
            <span style={{ fontWeight:800, fontSize:22, color:"#fff" }}>{s.n}</span>
            <span style={{ fontSize:12, color:"rgba(255,255,255,0.85)", fontWeight:500 }}>{s.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}



// ── Duplicate check utility ──────────────────────────────
function checkDuplicates(data, allUsers) {
  const norm = s => (s||"").trim().toLowerCase().replace(/\s+/g," ");
  const matches = [];
  allUsers.forEach(u => {
    const fields = [];
    if(data.id_number && u.id_number && norm(data.id_number)===norm(u.id_number))
      fields.push("პირადი ნომერი");
    if(data.phone && u.phone && norm(data.phone.replace(/[-\s]/g,""))===norm((u.phone||"").replace(/[-\s]/g,"")))
      fields.push("ტელეფონი");
    if(data.company_name && u.company_name && norm(data.company_name)===norm(u.company_name))
      fields.push("შპს სახელი");
    if(data.hotel_name && u.name && norm(data.hotel_name)===norm(u.name))
      fields.push("სასტუმროს სახელი");
    if(data.name && u.name && norm(data.name)===norm(u.name) && u.role!=="hotel")
      fields.push("სამრეცხაო/მძღოლის სახელი");
    if(data.contact && u.contact && norm(data.contact)===norm(u.contact))
      fields.push("საკონტაქტო პირი");
    if(data.email && u.email && norm(data.email)===norm(u.email))
      fields.push("ელ-ფოსტა");
    if(fields.length > 0) matches.push({user:u, fields});
  });
  return matches;
}
// ── Excel Generator (SheetJS via CDN) ─────────────────────
async function loadXLSX() {
  if(window.XLSX) return window.XLSX;
  return new Promise((res,rej)=>{
    const s=document.createElement("script");
    s.src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
    s.onload=()=>res(window.XLSX);
    s.onerror=rej;
    document.head.appendChild(s);
  });
}

async function generateInvoiceExcel(invoices, groupName, period, groupBy) {
  const byHotel = groupBy === "hotel";
  const XLSX = await loadXLSX();
  const num = (v)=>{ const n=parseFloat(v); return isNaN(n)?0:Math.round(n*100)/100; };
  const wb = XLSX.utils.book_new();
  const summary = [];

  // Group by laundry
  const laundryGroups = {};
  invoices.forEach(inv => {
    const key = byHotel
      ? (inv.hotel_name || "სასტუმრო")
      : (inv.laundry_name || "სამრეცხაო");
    if(!laundryGroups[key]) laundryGroups[key] = [];
    laundryGroups[key].push(inv);
  });

  Object.entries(laundryGroups).forEach(([laundryName, invList]) => {
    const rows = [];
    // Header
    rows.push(["ინვოისი", "თარიღი", "სასტუმრო", "სამრეცხაო", "ნივთი", "რ-ნობა", "ფასი", "ჯამი"]);

    invList.forEach(inv => {
      if(inv.lineItems && inv.lineItems.length > 0) {
        inv.lineItems.forEach((item, idx) => {
          rows.push([
            idx===0 ? inv.invoice_number : "",
            idx===0 ? inv.created_at : "",
            idx===0 ? (inv.hotel_name||"სასტუმრო") : "",
            idx===0 ? (inv.laundry_name||"სამრეცხაო") : "",
            item.name||item.label||"",
            item.qty||item.quantity||1,
            num(item.price||item.unitPrice||0),
            num(item.total||item.lineTotal||0),
          ]);
        });
        // Subtotal row
        rows.push(["","","","","","","სულ ინვ.:"+inv.total.toFixed(2),num(inv.total)]);
        rows.push([]); // empty row
      } else {
        rows.push([
          inv.invoice_number, inv.created_at,
          inv.hotel_name||"სასტუმრო", inv.laundry_name||"სამრეცხაო",
          "","","",num(inv.total)
        ]);
      }
    });

    // Grand total
    const grandTotal = invList.reduce((s,i)=>s+i.total,0);
    rows.push([]);
    rows.push(["","","","","","","სულ:",num(grandTotal)]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    // Column widths
    ws["!cols"] = [
      {wch:16},{wch:14},{wch:20},{wch:20},{wch:22},{wch:8},{wch:12},{wch:12}
    ];
    const safeName = laundryName.replace(/[\/:*?"<>|]/g,"").substring(0,31);
    XLSX.utils.book_append_sheet(wb, ws, safeName);
    const itemNames = [];
    invList.forEach(iv=>(iv.lineItems||[]).forEach(it=>{
      const nm = it.name||it.label||"";
      if(nm && itemNames.indexOf(nm)===-1) itemNames.push(nm);
    }));
    if(itemNames.length>0) summary.push({laundryName, safeName, itemNames});
  });

  if(summary.length > 0) {
    const q = String.fromCharCode(39);
    const srows = [];
    srows.push([byHotel ? "სასტუმრო" : "სამრეცხაო","ნივთი","რ-ნობა","ფასი ჯამში"]);
    const subCells = [];
    summary.forEach(grp => {
      const ref = q + grp.safeName.split(q).join(q+q) + q;
      const start = srows.length + 1;
      grp.itemNames.forEach((nm, i) => {
        const r = srows.length + 1;
        srows.push([
          i===0 ? grp.laundryName : "",
          nm,
          {t:"n", f:"SUMIF(" + ref + "!E:E,$B" + r + "," + ref + "!F:F)"},
          {t:"n", f:"SUMIF(" + ref + "!E:E,$B" + r + "," + ref + "!H:H)"}
        ]);
      });
      const end = srows.length;
      const subRow = srows.length + 1;
      srows.push(["","","სულ:",{t:"n", f:"SUM(D" + start + ":D" + end + ")"}]);
      subCells.push("D" + subRow);
      srows.push([]);
    });
    if(subCells.length > 1) {
      srows.push(["","","სულ ჯამში:",{t:"n", f:"SUM(" + subCells.join(",") + ")"}]);
    }
    const sws = XLSX.utils.aoa_to_sheet(srows);
    sws["!cols"] = [{wch:20},{wch:24},{wch:12},{wch:14}];
    XLSX.utils.book_append_sheet(wb, sws, "ჯამი");
  }
  const periodLabel = period==="month"?"თვე":period==="quarter"?"კვარტ":period==="year"?"წელი":"სულ";
  const filename = `CPG_ინვოისები_${periodLabel}_${new Date().toLocaleDateString("ka-GE").replace(/\./g,"-")}.xlsx`;
  XLSX.writeFile(wb, filename);
}
function InvoiceCard({ inv }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={S.card}>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
        <div>
          <p style={{ margin:0, fontWeight:700, fontSize:13 }}>{inv.invoice_number}</p>
          <p style={{ margin:"2px 0", fontSize:11, color:"#6B7280" }}>
            მომსახ. გამწევი: <strong>{inv.laundry_name||"სამრეცხაო"}</strong>
          </p>
          <p style={{ margin:"2px 0", fontSize:11, color:"#6B7280" }}>
            მომსახ. მიმღები: <strong>{inv.hotel_name||"სასტუმრო"}</strong>
          </p>
          <p style={{ margin:"2px 0 0", fontSize:11, color:"#9CA3AF" }}>
            შეკვ. #{inv.order_number} · {inv.created_at}
          </p>
        </div>
        <div style={{ textAlign:"right" }}>
          <p style={{ margin:"0 0 6px", fontWeight:800, fontSize:16 }}>₾{inv.total.toFixed(2)}</p>
        </div>
      </div>
      {/* Toggle line items */}
      {inv.lineItems && inv.lineItems.length>0 && (
        <>
          <button onClick={()=>setExpanded(v=>!v)} style={{
            background:"none",border:"none",color:"#1B6EF3",fontSize:11,
            cursor:"pointer",padding:"6px 0 0",fontWeight:600}}>
            {expanded?"▲ დახურვა":"▼ დეტალები"}
          </button>
          {expanded && (
            <div style={{background:"#F8FAFC",borderRadius:8,padding:"8px 10px",marginTop:6}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr auto auto auto",gap:"4px 10px",
                marginBottom:4}}>
                {["ნივთი","რაოდ.","ფასი","ჯამი"].map(h=>(
                  <p key={h} style={{margin:0,fontSize:10,fontWeight:700,color:"#9CA3AF"}}>{h}</p>
                ))}
              </div>
              {inv.lineItems.map((li,i)=>(
                <div key={i} style={{display:"grid",gridTemplateColumns:"1fr auto auto auto",
                  gap:"4px 10px",padding:"3px 0",borderTop:"1px solid #E5E7EB"}}>
                  <p style={{margin:0,fontSize:12}}>{li.name}</p>
                  <p style={{margin:0,fontSize:12,color:"#6B7280"}}>{li.qty} {li.unit}</p>
                  <p style={{margin:0,fontSize:12,color:"#6B7280"}}>₾{li.price!=="?"?parseFloat(li.price).toFixed(2):"?"}</p>
                  <p style={{margin:0,fontSize:12,fontWeight:700}}>₾{li.total!==undefined?li.total.toFixed(2):"?"}</p>
                </div>
              ))}
              <div style={{borderTop:"2px solid #E5E7EB",marginTop:6,paddingTop:6,
                display:"flex",justifyContent:"flex-end"}}>
                <p style={{margin:0,fontSize:13,fontWeight:800}}>სულ: ₾{inv.subtotal.toFixed(2)}</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function InvoicesPage({ invoices, userId, userRole, laundries, setNotifs, setPriceNotifs, user, partnerRequests=[], hotels=[] }) {
  const [period, setPeriod] = useState("all");
  const [statusFilter, setStatusFilter] = useState(null);
  const [collapsedInvLaundries, setCollapsedInvLaundries] = useState([]);
  const [showReminder, setShowReminder] = useState(false);
  const [reminderText, setReminderText] = useState("გთხოვთ დაფაროთ დავალიანება. გმადლობთ თანამშრომლობისთვის.");
  const [reminderSent, setReminderSent] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const {pos:rPos, onMouseDown:rMD, onTouchStart:rTS} = useDraggable(0, 0);
  const isMobR = useBreakpoint()==="mobile";
  const { dark } = useTheme();
  const now2 = new Date();
  const mine = invoices.filter(i => userRole==="hotel" ? i.hotel_code===user?.code : i.laundry_code===user?.code);
  // Hotels: combine invoice hotels + accepted partner hotels
  const invoiceHotelCodes = [...new Set(mine.map(i=>i.hotel_code).filter(Boolean))];
  const partnerHotelList = hotels.filter(h=>
    partnerRequests.some(r=>r.status==="accepted"&&
      ((r.from_code===user?.code&&r.to_code===h.code)||(r.to_code===user?.code&&r.from_code===h.code)))
  );
  const allHotelCodes = [...new Set([
    ...invoiceHotelCodes,
    ...partnerHotelList.map(h=>h.code)
  ])];
  const myPartnerHotels = allHotelCodes.map(code=>{
    const h = hotels.find(x=>x.code===code);
    return h || {code, name:mine.find(i=>i.hotel_code===code)?.hotel_name||code, id:code};
  }).filter(h=>h.name&&h.code);

  const filterByPeriod = (list) => list.filter(inv => {
    const d = inv.date instanceof Date ? inv.date : new Date(inv.created_at);
    if (period==="month")   return d.getMonth()===now2.getMonth() && d.getFullYear()===now2.getFullYear();
    if (period==="quarter") return Math.floor(d.getMonth()/3)===Math.floor(now2.getMonth()/3);
    if (period==="year")    return d.getFullYear()===now2.getFullYear();
    return true;
  });

  const periodFil = filterByPeriod(mine);

  const applyStatusFilter = (list) => {
    if(!statusFilter || statusFilter==="სულ") return list;
    if(statusFilter==="გადახდილია")   return list.filter(i=>i.status==="გადახდილია");
    if(statusFilter==="გადასახდელია") return list.filter(i=>i.status!=="გადახდილია");
    return list;
  };

  const fil = applyStatusFilter(periodFil);
  const total  = periodFil.reduce((s,i)=>s+i.total,0);
  const paid   = periodFil.filter(i=>i.status==="გადახდილია").reduce((s,i)=>s+i.total,0);
  const unpaid = periodFil.filter(i=>i.status!=="გადახდილია").reduce((s,i)=>s+i.total,0);

  const periods = [{k:"month",l:"თვე"},{k:"quarter",l:"კვარტალი"},{k:"year",l:"წელი"},{k:"all",l:"ყველა"}];

  const statCards = [
    { key:"სულ",         v:`₾${total.toFixed(2)}`,  n:periodFil.length,                                     bg:"#EBF2FF", c:"#1B6EF3" },

    { key:"გადახდა %",    v:`${total>0?Math.round(paid/total*100):0}%`, n:null,                               bg:"#F0EBFF", c:"#7C3AED" },
  ];

  return (
    <div style={{ padding:"16px 16px 16px" }}>

      {/* Payment Reminder Modal */}
      {showReminder && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",
          display:"flex",alignItems:isMobR?"flex-end":"center",justifyContent:"center",zIndex:500}}
          onClick={e=>{if(e.target===e.currentTarget)setShowReminder(false);}}>
          <div style={{width:"100%",maxWidth:480,background:dark?"#1E293B":"#fff",
            borderRadius:isMobR?"18px 18px 0 0":16,padding:"20px 18px 32px",
            boxSizing:"border-box",
            transform:(!isMobR&&(rPos.x||rPos.y))?`translate(${rPos.x}px,${rPos.y}px)`:undefined,
            boxShadow:"0 8px 32px rgba(0,0,0,0.25)"}}>
            <div
              onMouseDown={!isMobR?rMD:undefined}
              onTouchStart={!isMobR?rTS:undefined}
              style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                marginBottom:14,cursor:isMobR?"default":"grab",userSelect:"none"}}>
              <h3 style={{margin:0,fontSize:15,fontWeight:800,color:dark?"#F1F5F9":"#111827"}}>
                📨 გადახდის შეხსენება
              </h3>
              <button onClick={()=>{setShowReminder(false);setReminderSent(false);}}
                style={{background:"#F3F4F6",border:"none",borderRadius:"50%",
                  width:28,height:28,cursor:"pointer",fontSize:14}}>✕</button>
            </div>
            {reminderSent ? (
              <div style={{textAlign:"center",padding:"20px 0"}}>
                <p style={{fontSize:36,margin:"0 0 10px"}}>✅</p>
                <p style={{fontWeight:700,fontSize:14,color:"#16A34A"}}>შეხსენება გაიგზავნა!</p>
                <p style={{fontSize:12,color:"#6B7280",marginTop:4}}>
                  Notification მიუვიდა {selectedHotel?.name||"სასტუმროს"} პლატფორმაზე
                </p>
                <p style={{fontSize:11,color:"#9CA3AF",marginTop:8}}>📧 Email / 📱 SMS — Backend-ის შემდეგ</p>
                <button onClick={()=>{setShowReminder(false);setReminderSent(false);setSelectedHotel(null);}}
                  style={{marginTop:16,background:"#1B6EF3",color:"#fff",border:"none",
                    borderRadius:10,padding:"10px 24px",fontWeight:700,cursor:"pointer"}}>
                  დახურვა
                </button>
              </div>
            ) : (
              <>
                {myPartnerHotels.length > 1 && (
                  <div style={{marginBottom:12}}>
                    <label style={{display:"block",fontSize:12,fontWeight:600,
                      color:dark?"#94A3B8":"#374151",marginBottom:6}}>სასტუმრო:</label>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {myPartnerHotels.map(h=>(
                        <button key={h.id} onClick={()=>setSelectedHotel(h)}
                          style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",
                            borderRadius:10,border:`1.5px solid ${selectedHotel?.id===h.id?"#1B6EF3":"#E5E7EB"}`,
                            background:selectedHotel?.id===h.id?"#EBF2FF":"#F8FAFC",
                            cursor:"pointer",textAlign:"left"}}>
                          <span style={{fontSize:18}}>🏨</span>
                          <div>
                            <p style={{margin:0,fontSize:13,fontWeight:600,
                              color:selectedHotel?.id===h.id?"#1B6EF3":"#111827"}}>{h.name}</p>
                            <p style={{margin:0,fontSize:11,color:"#6B7280"}}>{h.code}</p>
                          </div>
                          {selectedHotel?.id===h.id&&<span style={{marginLeft:"auto",color:"#1B6EF3",fontSize:16}}>✓</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <label style={{display:"block",fontSize:12,fontWeight:600,
                  color:dark?"#94A3B8":"#374151",marginBottom:6}}>შეტყობინების ტექსტი:</label>
                <textarea value={reminderText} onChange={e=>setReminderText(e.target.value)} rows={4}
                  style={{width:"100%",boxSizing:"border-box",borderRadius:10,
                    border:dark?"1px solid #334155":"1px solid #E5E7EB",
                    background:dark?"#0F172A":"#F8FAFC",color:dark?"#F1F5F9":"#111827",
                    padding:"10px 12px",fontSize:13,fontFamily:"inherit",
                    resize:"vertical",outline:"none",lineHeight:1.6}}/>
                <div style={{display:"flex",gap:8,marginTop:14}}>
                  <button onClick={()=>{
                    const target = myPartnerHotels.length===1 
                      ? myPartnerHotels[0] 
                      : selectedHotel || (myPartnerHotels.length===0 ? null : null);
                    if(!target) { return; }
                    if(setPriceNotifs){
                      setPriceNotifs(p=>[{
                        id:Date.now(),
                        hotelCode:target.code,
                        msg:`📨 ${reminderText}`,
                        read:false,
                        time:fmt24(),
                        type:"reminder"
                      },...p]);
                    }
                    setSelectedHotel(target);
                    setReminderSent(true);
                  }} disabled={myPartnerHotels.length>1&&!selectedHotel}
                  style={{flex:1,background:myPartnerHotels.length>1&&!selectedHotel?"#E2E8F0":"#1B6EF3",
                    color:myPartnerHotels.length>1&&!selectedHotel?"#9CA3AF":"#fff",
                    border:"none",borderRadius:10,padding:"12px",fontWeight:700,fontSize:14,
                    cursor:myPartnerHotels.length>1&&!selectedHotel?"not-allowed":"pointer"}}>
                    📨 გაგზავნა
                  </button>
                  <button onClick={()=>{setShowReminder(false);setSelectedHotel(null);}}
                    style={{flex:1,background:"#F3F4F6",color:"#374151",border:"none",
                      borderRadius:10,padding:"12px",fontWeight:700,fontSize:14,cursor:"pointer"}}>
                    გაუქმება
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Period filter + Excel button + Reminder button */}
      <div style={{ display:"flex", gap:6, marginBottom:14 }}>
        {periods.map(p=>(
          <button key={p.k} onClick={()=>setPeriod(p.k)} style={{
            flex:1, padding:"9px 0", borderRadius:10, border:"none", cursor:"pointer",
            background:period===p.k?"#1E293B":"#E2E8F0",
            color:period===p.k?"#fff":"#475569", fontWeight:700, fontSize:13 }}>{p.l}</button>
        ))}
        <button
          disabled={fil.length===0}
          onClick={()=>generateInvoiceExcel(fil, "CPG", period, userRole==="laundry"?"hotel":"laundry")}
          style={{
            padding:"9px 12px", borderRadius:10, border:"none", cursor:fil.length===0?"not-allowed":"pointer",
            background:fil.length===0?"#E2E8F0":"#16A34A",
            color:fil.length===0?"#9CA3AF":"#fff",
            fontWeight:700, fontSize:12, whiteSpace:"nowrap",
            display:"flex", alignItems:"center", gap:5,
            opacity:fil.length===0?0.6:1
          }}>
          📊 Excel
        </button>
        {userRole==="laundry" && (
          <button onClick={()=>setShowReminder(true)} style={{
            padding:"9px 12px",borderRadius:10,border:"none",cursor:"pointer",
            background:"#FEF3C7",color:"#92400E",
            fontWeight:700,fontSize:12,whiteSpace:"nowrap",
            display:"flex",alignItems:"center",gap:5
          }}>
            📨 შეხსენება
          </button>
        )}
      </div>

      {/* Stats — clickable filter (except %) */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
        {statCards.map(r => {
          const isActive = statusFilter===r.key;
          const clickable = r.key !== "გადახდა %";
          return (
            <div key={r.key}
              onClick={()=>{ if(!clickable) return; setStatusFilter(isActive ? null : r.key); }}
              style={{ background: isActive ? r.c : r.bg, borderRadius:14, padding:14,
                cursor: clickable ? "pointer" : "default",
                outline: isActive ? `2.5px solid ${r.c}` : "none",
                transition:"background 0.15s" }}>
              <p style={{ margin:0, fontSize:11, color: isActive?"#fff":r.c, fontWeight:600 }}>{r.key}</p>
              <p style={{ margin:"4px 0 2px", fontWeight:800, fontSize:20, color: isActive?"#fff":r.c }}>{r.v}</p>
              {r.n !== null && <p style={{ margin:0, fontSize:11, color: isActive?"rgba(255,255,255,0.8)":r.c, opacity:isActive?1:0.7 }}>{r.n} ინვ.</p>}
            </div>
          );
        })}
      </div>

      {/* Active filter badge */}
      {statusFilter && statusFilter !== "სულ" && (
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
          <span style={{fontSize:12,color:"#374151"}}>ფილტრი: <strong>{statusFilter}</strong></span>
          <button onClick={()=>setStatusFilter(null)} style={{background:"none",border:"none",
            color:"#9CA3AF",cursor:"pointer",fontSize:12,padding:0}}>✕ გასუფთავება</button>
        </div>
      )}

      {fil.length===0 && <p style={{ color:"#9CA3AF", textAlign:"center", marginTop:30, fontSize:14 }}>ინვოისი არ არის</p>}

      {/* Grouped by laundry if 2+ partners */}
      {userRole==="hotel" && laundries && laundries.length >= 2 ? (
        laundries.map(l => {
          const lInvs = fil.filter(i=>i.laundry_id===l.id);
          if(lInvs.length===0) return null;
          const collapsed = collapsedInvLaundries.includes(l.id);
          const lTotal = lInvs.reduce((s,i)=>s+i.total,0);
          return (
            <div key={l.id} style={{marginBottom:12}}>
              <div onClick={()=>setCollapsedInvLaundries(p=>p.includes(l.id)?p.filter(x=>x!==l.id):[...p,l.id])}
                style={{display:"flex",alignItems:"center",gap:8,marginBottom:collapsed?0:8,
                  background:"#F0EBFF",borderRadius:10,padding:"8px 12px",cursor:"pointer"}}>
                <span style={{fontSize:13}}>🧾</span>
                <span style={{fontWeight:700,fontSize:13,color:"#7C3AED",flex:1}}>{l.name}</span>
                <span style={{fontSize:11,color:"#9CA3AF"}}>₾{lTotal.toFixed(2)} · {lInvs.length} ინვ.</span>
                <span style={{fontSize:16,fontWeight:700,color:"#7C3AED",marginLeft:4}}>{collapsed?"＋":"－"}</span>
              </div>
              {!collapsed && lInvs.map(inv=><InvoiceCard key={inv.id} inv={inv}/>)}
            </div>
          );
        })
      ) : (
        fil.map(inv=><InvoiceCard key={inv.id} inv={inv}/>)
      )}
    </div>
  );
}

function BarChart({ data, color, label, unit="" }) {
  const max = Math.max(...data.map(d=>d.v), 1);
  return (
    <div>
      <p style={{margin:"0 0 8px",fontWeight:700,fontSize:12}}>{label}</p>
      <div style={{display:"flex",alignItems:"flex-end",gap:4,height:70}}>
        {data.map((d,i)=>(
          <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
            <p style={{margin:0,fontSize:8,color:color,fontWeight:700}}>{d.v>0?(unit?`${d.v}${unit}`:d.v):""}</p>
            <div style={{width:"100%",background:color,borderRadius:"3px 3px 0 0",
              height:`${Math.max((d.v/max)*52,d.v>0?3:0)}px`,transition:"height 0.3s"}}/>
            <p style={{margin:0,fontSize:8,color:"#9CA3AF",textAlign:"center",lineHeight:1.2}}>{d.k}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportsPage({ orders, invoices, userId, userCode, userRole, color }) {
  // ── ALL HOOKS MUST BE AT TOP — no conditionals ──
  const [period, setPeriod] = useState("month");
  const [activeSeries, setActiveSeries] = useState(["total","done","failed","avgtime"]);
  const toggle = k => setActiveSeries(p=>p.includes(k)?p.filter(x=>x!==k):[...p,k]);

  const mine = orders.filter(o => userRole==="hotel"?(o.hotel_code===userCode||o.hotel_id===userId) : userRole==="laundry"?(o.laundry_code===userCode||o.laundry_id===userId) : (o.driver_id===userId||o.driver_id===userCode));
  const done = mine.filter(o=>o.status==="COMPLETED");
  const rejected = mine.filter(o=>o.status==="REJECTED");
  const myInvs = (invoices||[]).filter(i=>userRole==="hotel"?(i.hotel_code===userCode||i.hotel_id===userId):(i.laundry_code===userCode||i.laundry_id===userId));

  const now = new Date();

  const filterByPeriod = (list, dateKey) => list.filter(o=>{
    const d = new Date(o[dateKey]||o.created_at);
    if(period==="month")   return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();
    if(period==="quarter") return Math.floor(d.getMonth()/3)===Math.floor(now.getMonth()/3)&&d.getFullYear()===now.getFullYear();
    if(period==="year")    return d.getFullYear()===now.getFullYear();
    return true;
  });

  const periods = [{k:"month",l:"თვე"},{k:"quarter",l:"კვარტ."},{k:"year",l:"წელი"},{k:"all",l:"სულ"}];

  // ── DRIVER REPORT ──
  if(userRole==="driver"){
    const periodDone = filterByPeriod(done,"pickup_date");

    // Avg time: DRIVER_ASSIGNED → DELIVERED (use pickup_date as proxy)
    const calcAvg = (list) => {
      const ts = list.map(o=>{
        const start = new Date(o.created_at);
        const end   = new Date(o.pickup_date);
        const h = Math.abs(end-start)/36e5;
        return h>0&&h<200?h:null;
      }).filter(Boolean);
      return ts.length>0?(ts.reduce((a,b)=>a+b,0)/ts.length):0;
    };

    const avgH = calcAvg(periodDone);
    const avgMin = Math.round(avgH*60);

    // Orders by month (last 6)
    const monthMap = {};
    done.forEach(o=>{
      const d=new Date(o.pickup_date);
      const k=d.toLocaleDateString("ka-GE",{month:"short"});
      if(!monthMap[k]) monthMap[k]={total:0,done:0};
      monthMap[k].total++;
      monthMap[k].done++;
    });
    const monthKeys = Object.keys(monthMap).slice(-6);

    // Time of day distribution
    const tod = {morning:0, afternoon:0, evening:0};
    done.forEach(o=>{
      const h = new Date(o.pickup_date).getHours();
      if(h>=6&&h<12)  tod.morning++;
      else if(h>=12&&h<16) tod.afternoon++;
      else tod.evening++;
    });
    const todTotal = tod.morning+tod.afternoon+tod.evening||1;

    return (
      <div style={{padding:"14px",overflowY:"auto"}}>
        {/* Period */}
        <div style={{display:"flex",gap:6,marginBottom:14}}>
          {periods.map(p=>(
            <button key={p.k} onClick={()=>setPeriod(p.k)} style={{
              flex:1,padding:"8px 0",borderRadius:10,border:"none",cursor:"pointer",
              background:period===p.k?color:"#E2E8F0",
              color:period===p.k?"#fff":"#475569",fontWeight:700,fontSize:12}}>{p.l}</button>
          ))}
        </div>

        {/* Stats */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
          {[
            {l:"სულ შეკვ.",  v:filterByPeriod(mine,"created_at").length, c:"#1B6EF3",bg:"#EBF2FF"},
            {l:"დასრულდა",  v:periodDone.length, c:"#12B76A",bg:"#E6F9F1"},
            {l:"საშ. დრო",  v:avgMin>0?`${avgMin}წთ`:"—", c:"#7C3AED",bg:"#F0EBFF"},
            {l:"წარუმ.",    v:filterByPeriod(rejected,"created_at").length, c:"#EF4444",bg:"#FEE2E2"},
          ].map(s=>(
            <div key={s.l} style={{background:s.bg,borderRadius:14,padding:14}}>
              <p style={{margin:0,fontSize:11,color:s.c,fontWeight:600}}>{s.l}</p>
              <p style={{margin:"4px 0 0",fontWeight:800,fontSize:22,color:s.c}}>{s.v}</p>
            </div>
          ))}
        </div>

        {/* Chart 1: orders by month */}
        <div style={{...S.card,marginBottom:12}}>
          <BarChart
            label="📊 შეკვეთები თვეების მიხედვით"
            color={color}
            data={monthKeys.map(k=>({k,v:monthMap[k].total}))}
          />
        </div>

        {/* Chart 2: time of day */}
        <div style={S.card}>
          <p style={{margin:"0 0 8px",fontWeight:700,fontSize:12}}>⏰ დატვირთვა დღის მონაკვეთებით</p>
          {[
            {l:"06:00–12:00",v:tod.morning,  c:"#F59E0B"},
            {l:"12:01–16:00",v:tod.afternoon,c:"#1B6EF3"},
            {l:"16:01–24:00",v:tod.evening,  c:"#7C3AED"},
          ].map(t=>(
            <div key={t.l} style={{marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                <span style={{fontSize:11,color:"#374151"}}>{t.l}</span>
                <span style={{fontSize:11,fontWeight:700,color:t.c}}>{t.v} ({Math.round(t.v/todTotal*100)}%)</span>
              </div>
              <div style={{background:"#F1F5F9",borderRadius:999,height:8,overflow:"hidden"}}>
                <div style={{background:t.c,height:"100%",borderRadius:999,
                  width:`${(t.v/todTotal)*100}%`,transition:"width 0.3s"}}/>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── HOTEL REPORT ──
  if(userRole==="hotel"){
    const periodInvs = filterByPeriod(myInvs,"created_at");
    const totalSpend = periodInvs.reduce((s,i)=>s+i.total,0);
    const periodOrds = filterByPeriod(mine,"created_at");

    // Chart 1: items quantities per period
    const itemTotals = {};
    filterByPeriod(mine,"created_at").forEach(o=>{
      if(o.sheets_count>0) itemTotals["თეთრეული"]=(itemTotals["თეთრეული"]||0)+o.sheets_count;
      if(o.towels_kg>0)    itemTotals["პირსახოცი"]=(itemTotals["პირსახოცი"]||0)+o.towels_kg;
      if(o.covers_kg>0)    itemTotals["გადასაფარ."]=(itemTotals["გადასაფარ."]||0)+o.covers_kg;
      (o.custom_items||[]).forEach(ci=>{
        itemTotals[ci.name]=(itemTotals[ci.name]||0)+ci.qty;
      });
    });

    // Chart 2: invoice amounts by month
    const invByMonth = {};
    myInvs.forEach(inv=>{
      const d=new Date(inv.date||inv.created_at);
      const k=d.toLocaleDateString("ka-GE",{month:"short"});
      invByMonth[k]=(invByMonth[k]||0)+inv.total;
    });
    const invMonthKeys = Object.keys(invByMonth).slice(-6);

    return (
      <div style={{padding:"14px",overflowY:"auto"}}>
        {/* Period */}
        <div style={{display:"flex",gap:6,marginBottom:14}}>
          {periods.map(p=>(
            <button key={p.k} onClick={()=>setPeriod(p.k)} style={{
              flex:1,padding:"8px 0",borderRadius:10,border:"none",cursor:"pointer",
              background:period===p.k?color:"#E2E8F0",
              color:period===p.k?"#fff":"#475569",fontWeight:700,fontSize:12}}>{p.l}</button>
          ))}
        </div>

        {/* Stats */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
          {[
            {l:"შეკვეთები",   v:periodOrds.length,            c:"#1B6EF3",bg:"#EBF2FF"},
            {l:"ინვოისები",   v:periodInvs.length,            c:"#7C3AED",bg:"#F0EBFF"},
            {l:"სულ ხარჯი",  v:`₾${totalSpend.toFixed(0)}`,  c:"#F59E0B",bg:"#FEF3C7"},
            {l:"საშ. ინვ.",  v:periodInvs.length>0?`₾${(totalSpend/periodInvs.length).toFixed(0)}`:"—", c:"#12B76A",bg:"#E6F9F1"},
          ].map(s=>(
            <div key={s.l} style={{background:s.bg,borderRadius:14,padding:14}}>
              <p style={{margin:0,fontSize:11,color:s.c,fontWeight:600}}>{s.l}</p>
              <p style={{margin:"4px 0 0",fontWeight:800,fontSize:20,color:s.c}}>{s.v}</p>
            </div>
          ))}
        </div>

        {/* Chart 1: items */}
        {Object.keys(itemTotals).length>0 && (
          <div style={{...S.card,marginBottom:12}}>
            <BarChart
              label="📦 ნივთები (რაოდენობა)"
              color={color}
              data={Object.entries(itemTotals).map(([k,v])=>({k:k.slice(0,6),v}))}
            />
          </div>
        )}

        {/* Chart 2: invoice amounts */}
        <div style={S.card}>
          <BarChart
            label="🧾 ინვოისების თანხა (₾)"
            color="#7C3AED"
            data={invMonthKeys.map(k=>({k,v:Math.round(invByMonth[k])}))}
            unit="₾"
          />
        </div>
      </div>
    );
  }

  // ── LAUNDRY REPORT ──
  const timings = done.map(o=>Math.abs(new Date(o.pickup_date)-new Date(o.created_at))/36e5).filter(h=>h>0&&h<200);
  const avg = timings.length>0?(timings.reduce((s,t)=>s+t,0)/timings.length).toFixed(1):"—";

  const monthData = {};
  mine.forEach(o=>{
    const d = new Date(o.created_at);
    const key = d.toLocaleDateString("ka-GE",{year:"2-digit",month:"short"});
    if(!monthData[key]) monthData[key]={total:0,done:0,failed:0,times:[]};
    monthData[key].total++;
    if(o.status==="COMPLETED") monthData[key].done++;
    if(o.status==="REJECTED") monthData[key].failed++;
    const t=Math.abs(new Date(o.pickup_date)-new Date(o.created_at))/36e5;
    if(t>0&&t<200) monthData[key].times.push(t);
  });
  const mkeys = Object.keys(monthData).slice(-6);
  const maxTotal = Math.max(...mkeys.map(m=>monthData[m].total),1);
  const maxAvg   = Math.max(...mkeys.map(m=>monthData[m].times.length>0?(monthData[m].times.reduce((a,b)=>a+b,0)/monthData[m].times.length):0),1);
  const SERIES = [
    { key:"total",   label:"სულ შეკვ.", c:"#1B6EF3", bg:"#EBF2FF", stat:mine.length,
      getH:m=>(monthData[m].total/maxTotal)*56, getV:m=>monthData[m].total },
    { key:"done",    label:"დასრულდა",  c:"#12B76A", bg:"#E6F9F1", stat:done.length,
      getH:m=>(monthData[m].done/maxTotal)*56,  getV:m=>monthData[m].done },
    { key:"failed",  label:"წარუმატ.",  c:"#EF4444", bg:"#FEE2E2", stat:rejected.length,
      getH:m=>(monthData[m].failed/maxTotal)*56,getV:m=>monthData[m].failed },
    { key:"avgtime", label:"საშ. დრო",  c:"#7C3AED", bg:"#F0EBFF",
      stat:avg==="—"?"—":`${avg}სთ`,
      getH:m=>{ const ts=monthData[m].times; const a=ts.length>0?(ts.reduce((x,y)=>x+y,0)/ts.length):0; return (a/maxAvg)*56; },
      getV:m=>{ const ts=monthData[m].times; return ts.length>0?`${(ts.reduce((x,y)=>x+y,0)/ts.length).toFixed(1)}სთ`:"—"; } },
  ];

  // ── NEW: hotel-grouped charts (respect period filter) ──
  const periodInvsL = filterByPeriod(myInvs, "created_at");
  const periodMineL = filterByPeriod(mine, "created_at");
  const truncLabel = k => (k && k.length > 20) ? k.slice(0, 20) + "…" : (k || "უცნობი");

  const hotelAmountMap = {};
  periodInvsL.forEach(inv => {
    const h = inv.hotel_name || "უცნობი";
    hotelAmountMap[h] = (hotelAmountMap[h] || 0) + (inv.total || 0);
  });
  const hotelAmountData = Object.entries(hotelAmountMap)
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => ({ k: truncLabel(k), v: Math.round(v) }));

  const hotelKgMap = {};
  periodMineL.forEach(o => {
    const h = o.hotel_name || "უცნობი";
    let kg = (o.towels_kg || 0) + (o.covers_kg || 0);
    (o.custom_items || []).forEach(ci => { if (ci.unit === "კგ") kg += (ci.qty || 0); });
    if (kg > 0) hotelKgMap[h] = (hotelKgMap[h] || 0) + kg;
  });
  const hotelKgData = Object.entries(hotelKgMap)
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => ({ k: truncLabel(k), v: Math.round(v * 10) / 10 }));

  return (
    <div style={{ padding:"16px 16px 16px" }}>
      <div style={{ display:"flex", gap:6, marginBottom:14 }}>
        {periods.map(p=>(
          <button key={p.k} onClick={()=>setPeriod(p.k)} style={{
            flex:1,padding:"8px 0",borderRadius:10,border:"none",cursor:"pointer",
            background:period===p.k?color:"#E2E8F0",
            color:period===p.k?"#fff":"#475569",fontWeight:700,fontSize:12}}>{p.l}</button>
        ))}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
        {SERIES.map(s=>{
          const active = activeSeries.includes(s.key);
          return (
            <div key={s.key} onClick={()=>toggle(s.key)}
              style={{ background:active?s.c:s.bg, borderRadius:14, padding:14,
                cursor:"pointer", transition:"background 0.15s",
                outline:active?`2.5px solid ${s.c}`:"none" }}>
              <p style={{ margin:0, fontSize:11, color:active?"#fff":s.c, fontWeight:600 }}>{s.label}</p>
              <p style={{ margin:"4px 0 0", fontWeight:800, fontSize:22, color:active?"#fff":s.c }}>{s.stat}</p>
              {active && <p style={{margin:"4px 0 0",fontSize:9,color:"rgba(255,255,255,0.75)"}}>✓ დიაგრამაზე</p>}
            </div>
          );
        })}
      </div>
      <div style={S.card}>
        <p style={{ margin:"0 0 12px", fontWeight:700, fontSize:13 }}>📊 დინამიკა თვეების მიხედვით</p>
        {mkeys.length===0 && <p style={{color:"#9CA3AF",fontSize:12}}>მონაცემი არ არის</p>}
        <div style={{display:"flex",alignItems:"flex-end",gap:6}}>
          {mkeys.map(m=>(
            <div key={m} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"flex-end",gap:2,height:60,marginBottom:4}}>
                {SERIES.filter(s=>activeSeries.includes(s.key)).map(s=>(
                  <div key={s.key} style={{width:10,background:s.c,borderRadius:"3px 3px 0 0",
                    height:`${Math.max(s.getH(m),2)}px`,transition:"height 0.3s"}}
                    title={`${s.label}: ${s.getV(m)}`}/>
                ))}
              </div>
              <p style={{margin:0,fontSize:9,color:"#9CA3AF",textAlign:"center"}}>{m}</p>
            </div>
          ))}
        </div>
        <div style={{display:"flex",gap:10,marginTop:10,flexWrap:"wrap"}}>
          {SERIES.filter(s=>activeSeries.includes(s.key)).map(s=>(
            <div key={s.key} style={{display:"flex",alignItems:"center",gap:4}}>
              <span style={{width:8,height:8,borderRadius:"50%",background:s.c,display:"inline-block"}}/>
              <span style={{fontSize:10,color:s.c,fontWeight:600}}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{...S.card,marginTop:12,overflowX:"auto"}}>
        {hotelAmountData.length===0 && <p style={{margin:"0 0 8px",fontWeight:700,fontSize:12}}>🏨 დინამიკა სასტუმროების მიხედვით — თანხა (₾)</p>}
        {hotelAmountData.length===0 && <p style={{color:"#9CA3AF",fontSize:12}}>მონაცემი არ არის</p>}
        {hotelAmountData.length>0 && (
          <div style={{minWidth:Math.max(hotelAmountData.length*70,280)}}>
            <BarChart label="🏨 დინამიკა სასტუმროების მიხედვით — თანხა (₾)" color="#F59E0B" data={hotelAmountData} unit="₾"/>
          </div>
        )}
      </div>

      <div style={{...S.card,marginTop:12,overflowX:"auto"}}>
        {hotelKgData.length===0 && <p style={{margin:"0 0 8px",fontWeight:700,fontSize:12}}>🏨 დინამიკა სასტუმროების მიხედვით — კგ</p>}
        {hotelKgData.length===0 && <p style={{color:"#9CA3AF",fontSize:12}}>მონაცემი არ არის</p>}
        {hotelKgData.length>0 && (
          <div style={{minWidth:Math.max(hotelKgData.length*70,280)}}>
            <BarChart label="🏨 დინამიკა სასტუმროების მიხედვით — კგ" color="#12B76A" data={hotelKgData} unit="კგ"/>
          </div>
        )}
      </div>
    </div>
  );
}

function OrderDetailModal({ order, onClose, userRole, color, onAction, drivers, onDelete, priceLists={}, onEditPriceList }) {
  const bp = useBreakpoint();
  const {pos:dPos, onMouseDown:dMD, onTouchStart:dTS} = useDraggable(0, 0);
  const isMob = bp==="mobile";
  const [sel,setSel]=useState(null);
  const [eta,setEta]=useState("");
  const showPrices = userRole !== "driver";
  const [readyItems, setReadyItems] = useState({});

  return (
    <Modal title={`#${order.order_number}`} onClose={onClose}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
        <div>
          <p style={{ margin:0, fontSize:11, color:"#6B7280" }}>
            {new Date(order.pickup_date).toLocaleDateString("ka-GE")}
            {order.pickup_eta && ` · ${order.pickup_eta}`}
          </p>
          {order.driver_name  && <p style={{ margin:"1px 0 0", fontSize:11, color:"#6B7280" }}>🚗 {order.driver_name}</p>}
          {order.laundry_name && <p style={{ margin:"1px 0 0", fontSize:11, color:"#6B7280" }}>🧺 {order.laundry_name}</p>}
        </div>
        <Badge status={order.status}/>
      </div>

      <div style={{ background:"#F8FAFC", borderRadius:8, padding:"8px 10px", marginBottom:8 }}>
        <p style={{margin:"0 0 6px",fontSize:11,fontWeight:700,color:"#6B7280"}}>ნივთები</p>
        {[
          ...[
            order.sheets_count>0&&{name:"თეთრეული", qty:order.sheets_count, unit:order.sheets_unit||"ც.", pieces:order.sheets_pieces},
            order.towels_kg>0   &&{name:"პირსახოცი", qty:order.towels_kg,    unit:order.towels_unit||"კგ", pieces:order.towels_pieces},
            order.covers_kg>0   &&{name:"გადასაფარებელი", qty:order.covers_kg, unit:order.covers_unit||"კგ", pieces:order.covers_pieces},
          ].filter(Boolean),
          ...(order.custom_items||[]),
        ].map((it,i)=>(
          <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"3px 0",
            borderBottom:"1px solid #F1F5F9"}}>
            <span style={{fontSize:12,color:"#374151"}}>{it.name}</span>
            <span style={{fontWeight:700,fontSize:12}}>{it.pieces>0&&<span style={{color:"#9CA3AF",fontWeight:500,marginRight:6}}>{it.pieces} ც.</span>}{it.qty} {it.unit}</span>
          </div>
        ))}
      </div>
      {order.notes && (
        <div style={{marginTop:8,padding:"8px 10px",background:"#FFFBEB",borderRadius:8}}>
          <p style={{margin:0,fontSize:11,fontWeight:700,color:"#92400E"}}>კომენტარი</p>
          <p style={{margin:"2px 0 0",fontSize:12,color:"#374151",whiteSpace:"pre-wrap"}}>{order.notes}</p>
        </div>
      )}

      <p style={{ margin:"0 0 4px", fontSize:11, fontWeight:700, color:"#374151" }}>სტატუსის ისტორია</p>
      <div style={{ maxHeight:120, overflowY:"auto", paddingRight:4 }}>
        <Tracking order={order}/>
      </div>

      <div style={{ marginTop:16, display:"flex", flexDirection:"column", gap:8 }}>
        {userRole==="laundry" && order.status==="PENDING_LAUNDRY_APPROVAL" && (<>
          <Btn color={color} onClick={()=>{onAction(order.id,"APPROVED");onClose()}} style={{width:"100%"}}>✅ შეკვეთის დადასტურება</Btn>
          <Btn color="#EF4444" outline onClick={()=>{onAction(order.id,"REJECTED");onClose()}} style={{width:"100%"}}>❌ უარყოფა</Btn>
        </>)}
        {userRole==="laundry" && order.status==="APPROVED" && drivers && (<>
          <p style={{ margin:"4px 0 6px", fontSize:13, fontWeight:700 }}>🚗 Pickup მძღოლის დანიშვნა</p>
          {drivers.map(d=>(
            <div key={d.id} onClick={()=>setSel(d.id)}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:12,
                border:sel===d.id?`2px solid ${color}`:"2px solid #E5E7EB",
                cursor:"pointer", background:sel===d.id?THEME.laundry.light:"#fff", marginBottom:6 }}>
              <Ava name={d.name} size={34}/>
              <div style={{ flex:1 }}>
                <p style={{ margin:0, fontWeight:700, fontSize:13 }}>{d.name}</p>
                <p style={{ margin:0, fontSize:11, color:d.available?"#12B76A":"#EF4444" }}>
                  {d.available?"ხელმისაწვდომი":"დაკავებულია"}
                </p>
              </div>
              {sel===d.id && <span style={{ color, fontSize:18 }}>✔</span>}
            </div>
          ))}
          <input placeholder="Pickup ETA (14:30)" value={eta} onChange={e=>setEta(e.target.value)} style={{...S.input,marginBottom:8}}/>
          <Btn color={color} onClick={()=>{
            if(!sel)return;
            const dr=drivers.find(x=>x.id===sel);
            onAction(order.id,"DRIVER_ASSIGNED",{driver_id:sel,driver_name:dr.name,pickup_eta:eta});
            onClose();
          }} style={{width:"100%"}}>🚗 Pickup მძღოლის დანიშვნა</Btn>
          <Btn color="#6B7280" outline onClick={()=>{onAction(order.id,"RECEIVED_IN_LAUNDRY");onClose()}} style={{width:"100%",marginTop:8}}>მივიღე მძღოლის გარეშე</Btn>
        </>)}
        {userRole==="laundry" && order.status==="PARTIALLY_DELIVERED" && (
          <div style={{background:"#FEF3C7",borderRadius:12,padding:"12px 14px",marginBottom:8}}>
            <p style={{margin:"0 0 8px",fontSize:13,fontWeight:700,color:"#92400E"}}>⏳ დარჩენილი:</p>
            {(order.remaining_items||[]).map((ri,i)=>(
              <p key={i} style={{margin:"2px 0",fontSize:12,color:"#92400E"}}>{ri.name}: {ri.pieces>0?`${ri.pieces} ც. `:""}{ri.qty} {ri.unit}</p>
            ))}
            <Btn color={color} onClick={()=>{
              onAction(order.id,"READY_FOR_DELIVERY",{
                ready_items:(order.remaining_items||[]).map(ri=>({name:ri.name,ready:ri.qty,orig:ri.qty,unit:ri.unit,ready_pieces:ri.pieces||0,orig_pieces:ri.pieces||0})),
                remaining_items:null
              });
              onClose();
            }} style={{width:"100%",marginTop:10}}>✨ მზადაა (დარჩენილი)</Btn>
          </div>
        )}
        {userRole==="laundry" && order.status==="RECEIVED_IN_LAUNDRY" && (()=>{
          const orderItems=[
            order.sheets_count>0&&{name:"თეთრეული",orig:order.sheets_count,unit:order.sheets_unit||"ც.",orig_pieces:order.sheets_pieces||0},
            order.towels_kg>0&&{name:"პირსახოცი",orig:order.towels_kg,unit:order.towels_unit||"კგ",orig_pieces:order.towels_pieces||0},
            order.covers_kg>0&&{name:"გადასაფარებელი",orig:order.covers_kg,unit:order.covers_unit||"კგ",orig_pieces:order.covers_pieces||0},
            ...((order.custom_items||[]).map(ci=>({name:ci.name,orig:ci.qty,unit:ci.unit||"ც.",orig_pieces:ci.pieces||0}))),
          ].filter(Boolean);

          // Uncounted orders: items come from the laundry's price list (priced items only)
          const uncPlKey = `${order.laundry_code}_${order.hotel_code}`;
          const uncPl = priceLists[uncPlKey];
          const uncountedAdded = order.is_uncounted
            ? (uncPl?.items||[]).filter(it=>it.price!==""&&it.price!=null&&parseFloat(it.price)>0).map(it=>({name:it.name,unit:it.unit}))
            : [];
          const displayItems = orderItems.length>0 ? orderItems : uncountedAdded;

          // For uncounted: step1=counting, step2=partial selection
          const isCounted = !!readyItems.__counted;

          // Step 2 — partial selection
          if(order.is_uncounted && isCounted){
            const countedItems = displayItems.map(item=>({
              ...item,
              orig: readyItems[`tot_${item.name}`]||0,
              orig_pieces: readyItems[`totp_${item.name}`]||0
            })).filter(i=>i.orig>0);
            const allReady = countedItems.every(i=>(readyItems[`ri_${i.name}`]??i.orig)>=i.orig);
            return (
              <div style={{background:"#F8FAFC",borderRadius:12,padding:"12px 14px",marginBottom:8,border:"1px solid #E5E7EB"}}>
                {/* Totals summary */}
                <div style={{background:"#E6F9F1",borderRadius:8,padding:"8px 10px",marginBottom:12}}>
                  <p style={{margin:"0 0 4px",fontSize:11,fontWeight:700,color:"#065F46"}}>✅ დათვლილია:</p>
                  {countedItems.map((item,i)=>(
                    <p key={i} style={{margin:"1px 0",fontSize:12,color:"#065F46"}}>
                      {item.name}: <strong>{item.orig_pieces>0?`${item.orig_pieces} ც. `:""}{item.orig} {item.unit}</strong>
                    </p>
                  ))}
                  <button onClick={()=>setReadyItems(p=>({...p,__counted:false}))}
                    style={{background:"none",border:"none",color:"#9CA3AF",fontSize:11,cursor:"pointer",padding:"4px 0 0"}}>
                    ✏️ თავიდან დათვლა
                  </button>
                </div>
                <p style={{margin:"0 0 8px",fontSize:13,fontWeight:700,color:"#374151"}}>✨ ახლა მზადა:</p>
                {countedItems.map((item,idx)=>{
                  const rk=`ri_${item.name}`;
                  const cur=readyItems[rk]!=null?readyItems[rk]:item.orig;
                  const remaining=parseFloat((item.orig-cur).toFixed(1));
                  const pk=`rp_${item.name}`;
                  const curP=readyItems[pk]!=null?readyItems[pk]:(item.orig_pieces||0);
                  const remP=(item.orig_pieces||0)-curP;
                  return (
                    <div key={idx} style={{marginBottom:8}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{flex:1,fontSize:12,fontWeight:600,color:"#374151"}}>{item.name}</span>
                        {item.orig_pieces>0&&(<>
                          <input type="number" value={curP} onChange={e=>setReadyItems(p=>({...p,[pk]:Math.min(item.orig_pieces,Math.max(0,parseFloat(e.target.value)||0))}))} min="0"
                            style={{...S.input,width:50,textAlign:"center",padding:"6px 8px"}} placeholder="0"/>
                          <span style={{fontSize:12,color:"#9CA3AF",width:16,textAlign:"left"}}>ც.</span>
                        </>)}
                        <input type="number" value={cur} onChange={e=>setReadyItems(p=>({...p,[rk]:Math.min(item.orig,Math.max(0,parseFloat(e.target.value)||0))}))} min="0"
                          style={{...S.input,width:50,textAlign:"center",padding:"6px 8px"}} placeholder="0"/>
                        <span style={{fontSize:12,color:"#9CA3AF",width:24,textAlign:"left"}}>{item.unit}</span>
                      </div>
                      {(remaining>0||remP>0)&&<p style={{margin:"2px 0 0",fontSize:10,color:"#F59E0B"}}>⏳ დარჩება: {remP>0?`${remP} ც. `:""}{remaining>0?`${remaining} ${item.unit}`:""}</p>}
                    </div>
                  );
                })}
                <Btn color={color} onClick={()=>{
                  const ready=countedItems.map(item=>({name:item.name,ready:readyItems[`ri_${item.name}`]??item.orig,orig:item.orig,unit:item.unit,ready_pieces:readyItems[`rp_${item.name}`]??(item.orig_pieces||0),orig_pieces:item.orig_pieces||0}));
                  const remaining=ready.filter(i=>(i.ready<i.orig)||(i.orig_pieces>0&&i.ready_pieces<i.orig_pieces)).map(i=>({name:i.name,qty:parseFloat((i.orig-i.ready).toFixed(1)),unit:i.unit,pieces:Math.max(0,(i.orig_pieces||0)-(i.ready_pieces||0))}));
                  onAction(order.id,"READY_FOR_DELIVERY",{ready_items:ready,remaining_items:remaining.length>0?remaining:null});
                  onClose();
                }} style={{width:"100%",marginTop:4}}>✨ მზადაა</Btn>
              </div>
            );
          }

          // Step 1 — counting
          const canCount = order.is_uncounted
            ? uncountedAdded.some(item=>(readyItems[`tot_${item.name}`]||0)>0)
            : true;

          return (
            <div style={{background:"#F8FAFC",borderRadius:12,padding:"12px 14px",marginBottom:8,border:"1px solid #E5E7EB"}}>
              <p style={{margin:"0 0 10px",fontSize:13,fontWeight:700,color:"#374151"}}>
                {order.is_uncounted ? "📦 დათვლა:" : "✨ მზადა რაოდენობა:"}
                {order.is_uncounted&&<span style={{marginLeft:8,background:"#FEF3C7",color:"#92400E",
                  fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:999}}>დაუთვლელი</span>}
              </p>

              {/* Uncounted: dynamic add-item UI */}
              {order.is_uncounted ? (uncountedAdded.length===0 ? (
                <div style={{textAlign:"center",padding:"6px 0"}}>
                  <p style={{margin:"0 0 12px",fontSize:12,color:"#92400E",lineHeight:1.5}}>⚠️ ამ სასტუმროსთვის შევსებული პრაის-ლისტი არ არის.<br/>ჯერ შეავსე ფასები — შემდეგ დათვლა შესაძლებელი იქნება.</p>
                  <Btn color={color} onClick={()=>{onEditPriceList&&onEditPriceList();onClose();}} style={{width:"100%"}}>💰 პრაის-ლისტის შევსება</Btn>
                </div>
              ) : (<>
                {uncountedAdded.map((item,idx)=>{
                  const rk=`tot_${item.name}`;
                  const pk=`totp_${item.name}`;
                  return (
                    <div key={idx} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                      <span style={{flex:1,fontSize:12,fontWeight:600,color:"#374151"}}>{item.name} <span style={{color:"#9CA3AF"}}>({item.unit})</span></span>
                      {item.unit!=="ც."&&(<>
                        <input type="number" value={readyItems[pk]||""} onChange={e=>setReadyItems(p=>({...p,[pk]:Math.max(0,parseFloat(e.target.value)||0)}))} min="0"
                          style={{...S.input,width:50,textAlign:"center",padding:"6px 8px"}} placeholder="0"/>
                        <span style={{fontSize:12,color:"#9CA3AF",width:16,textAlign:"left"}}>ც.</span>
                      </>)}
                      <input type="number" value={readyItems[rk]||""} onChange={e=>setReadyItems(p=>({...p,[rk]:Math.max(0,parseFloat(e.target.value)||0)}))} min="0"
                        style={{...S.input,width:50,textAlign:"center",padding:"6px 8px"}} placeholder="0"/>
                      <span style={{fontSize:12,color:"#9CA3AF",width:24,textAlign:"left"}}>{item.unit}</span>
                      <button onClick={()=>setReadyItems(p=>({...p,[`tot_${item.name}`]:0,[`totp_${item.name}`]:0,__items:(p.__items||[]).filter((_,i)=>i!==idx)}))}
                        style={{background:"none",border:"none",color:"#EF4444",cursor:"pointer",fontSize:14,padding:0}}>✕</button>
                    </div>
                  );
                })}
                {/* Add item row */}
                {!canCount&&<p style={{fontSize:11,color:"#EF4444",margin:"0 0 8px"}}>⚠️ მინიმუმ ერთ ნივთს რაოდენობა შეუვსე</p>}
                <Btn color={canCount?color:"#9CA3AF"} onClick={()=>{if(!canCount) return;setReadyItems(p=>({...p,__counted:true}));}}
                  style={{width:"100%",marginTop:4,cursor:canCount?"pointer":"not-allowed"}}>
                  დათვლა დასრულდა →
                </Btn>
              </>)) : (
                // Counted order: standard adjuster
                displayItems.map((item,idx)=>{
                  const rk=`ri_${item.name}`;
                  const cur=readyItems[rk]!=null?readyItems[rk]:item.orig;
                  const pk=`rp_${item.name}`;
                  const curP=readyItems[pk]!=null?readyItems[pk]:(item.orig_pieces||0);
                  return (
                    <div key={idx} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                      <span style={{flex:1,fontSize:12,fontWeight:600,color:"#374151"}}>
                        {item.name} <span style={{color:"#9CA3AF"}}>({item.orig_pieces>0?`${item.orig_pieces} ც. `:""}{item.orig} {item.unit})</span>
                      </span>
                      {item.orig_pieces>0&&(<>
                        <input type="number" value={curP} onChange={e=>setReadyItems(p=>({...p,[pk]:Math.min(item.orig_pieces,Math.max(0,parseFloat(e.target.value)||0))}))} min="0"
                          style={{...S.input,width:50,textAlign:"center",padding:"6px 8px"}} placeholder="0"/>
                        <span style={{fontSize:12,color:"#9CA3AF",width:16,textAlign:"left"}}>ც.</span>
                      </>)}
                      <input type="number" value={cur} onChange={e=>setReadyItems(p=>({...p,[rk]:Math.min(item.orig,Math.max(0,parseFloat(e.target.value)||0))}))} min="0"
                        style={{...S.input,width:50,textAlign:"center",padding:"6px 8px"}} placeholder="0"/>
                      <span style={{fontSize:12,color:"#9CA3AF",width:24,textAlign:"left"}}>{item.unit}</span>
                    </div>
                  );
                }).concat(
                  <Btn key="submit" color={color} onClick={()=>{
                    const ready=displayItems.map(item=>({name:item.name,ready:readyItems[`ri_${item.name}`]??item.orig,orig:item.orig,unit:item.unit,ready_pieces:readyItems[`rp_${item.name}`]??(item.orig_pieces||0),orig_pieces:item.orig_pieces||0}));
                    const remaining=ready.filter(i=>(i.orig>0&&i.ready<i.orig)||(i.orig_pieces>0&&i.ready_pieces<i.orig_pieces)).map(i=>({name:i.name,qty:parseFloat((i.orig-i.ready).toFixed(1)),unit:i.unit,pieces:Math.max(0,(i.orig_pieces||0)-(i.ready_pieces||0))}));
                    onAction(order.id,"READY_FOR_DELIVERY",{ready_items:ready.length>0?ready:null,remaining_items:remaining.length>0?remaining:null});
                    onClose();
                  }} style={{width:"100%",marginTop:4}}>✨ მზადაა</Btn>
                )
              )}
            </div>
          );
        })()}
        {userRole==="laundry" && order.status==="READY_FOR_DELIVERY" && drivers && (<>
          <p style={{ margin:"4px 0 6px", fontSize:13, fontWeight:700 }}>🚗 Delivery მძღოლის დანიშვნა</p>
          {drivers.map(d=>(
            <div key={d.id} onClick={()=>setSel(d.id)}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:12,
                border:sel===d.id?`2px solid ${color}`:"2px solid #E5E7EB",
                cursor:"pointer", background:sel===d.id?THEME.laundry.light:"#fff", marginBottom:6 }}>
              <Ava name={d.name} size={34}/>
              <div style={{ flex:1 }}>
                <p style={{ margin:0, fontWeight:700, fontSize:13 }}>{d.name}</p>
                <p style={{ margin:0, fontSize:11, color:d.available?"#12B76A":"#EF4444" }}>
                  {d.available?"ხელმისაწვდომი":"დაკავებულია"}
                </p>
              </div>
              {sel===d.id && <span style={{ color, fontSize:18 }}>✔</span>}
            </div>
          ))}
          <input placeholder="Delivery ETA (16:00)" value={eta} onChange={e=>setEta(e.target.value)} style={{...S.input,marginBottom:8}}/>
          <Btn color={color} onClick={()=>{
            if(!sel)return;
            const dr=drivers.find(x=>x.id===sel);
            onAction(order.id,"DELIVERY_DRIVER_ASSIGNED",{driver_id:sel,driver_name:dr.name,delivery_eta:eta});
            onClose();
          }} style={{width:"100%"}}>🚗 Delivery მძღოლის დანიშვნა</Btn>
          <Btn color="#6B7280" outline onClick={()=>{onAction(order.id,"DELIVERED");onClose()}} style={{width:"100%",marginTop:8}}>ჩავაბარე მძღოლის გარეშე</Btn>
        </>)}
        {userRole==="driver" && order.status==="DRIVER_ASSIGNED" && (
          <Btn color={color} onClick={()=>{onAction(order.id,"DRIVER_ON_THE_WAY");onClose()}} style={{width:"100%"}}>🚗 გავემგზავრე (Pickup)</Btn>
        )}
        {userRole==="driver" && order.status==="DRIVER_ON_THE_WAY" && (
          <Btn color={color} onClick={()=>{onAction(order.id,"PICKED_UP");onClose()}} style={{width:"100%"}}>📦 ავიღე სასტუმროდან</Btn>
        )}
        {userRole==="driver" && order.status==="PICKED_UP" && (
          <Btn color={color} onClick={()=>{onAction(order.id,"RECEIVED_IN_LAUNDRY");onClose()}} style={{width:"100%"}}>🧺 ჩავაბარე სამრეცხაოს</Btn>
        )}
        {userRole==="driver" && order.status==="DELIVERY_DRIVER_ASSIGNED" && (
          <Btn color={color} onClick={()=>{onAction(order.id,"DELIVERY_ON_THE_WAY");onClose()}} style={{width:"100%"}}>🚗 გავემგზავრე (Delivery)</Btn>
        )}
        {userRole==="driver" && order.status==="DELIVERY_ON_THE_WAY" && (
          <Btn color={color} onClick={()=>{onAction(order.id,"DELIVERED");onClose()}} style={{width:"100%"}}>📦 ჩავაბარე სასტუმროს</Btn>
        )}
        {userRole==="hotel" && order.status==="DELIVERED" && (
          <div>
            {(order.ready_items||[]).length>0&&(
              <div style={{marginBottom:8}}>
                <p style={{margin:"0 0 4px",fontSize:12,fontWeight:700,color:"#374151"}}>📦 მიტანილი:</p>
                {order.ready_items.map((it,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"3px 0",borderBottom:"1px solid #F3F4F6"}}>
                    <span style={{fontSize:12,color:"#374151"}}>{it.name}</span>
                    <span style={{fontSize:12,fontWeight:700,color:"#16A34A"}}>{it.ready_pieces>0?`${it.ready_pieces} ც. `:""}{it.ready} {it.unit}</span>
                  </div>
                ))}
                {(order.remaining_items||[]).length>0&&(
                  <div style={{background:"#FEF3C7",borderRadius:8,padding:"6px 10px",marginTop:6}}>
                    <p style={{margin:"0 0 3px",fontSize:11,fontWeight:700,color:"#92400E"}}>⏳ სამრეცხაოში რჩება:</p>
                    {order.remaining_items.map((ri,i)=>(
                      <p key={i} style={{margin:"1px 0",fontSize:11,color:"#92400E"}}>{ri.name}: {ri.pieces>0?`${ri.pieces} ც. `:""}{ri.qty} {ri.unit}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Btn color={color} onClick={()=>{
              const hasRemaining=(order.remaining_items||[]).length>0;
              onAction(order.id,hasRemaining?"PARTIALLY_DELIVERED":"COMPLETED",{
                confirmed_items:order.ready_items,remaining_items:order.remaining_items||[]
              });
              onClose();
            }} style={{width:"100%"}}>
              ✅ ჩავიბარე{(order.remaining_items||[]).length>0?" (ნაწილობრივ)":""}
            </Btn>
          </div>
        )}
        {userRole==="hotel" && order.status==="PARTIALLY_DELIVERED" && (
          <div style={{background:"#FEF3C7",borderRadius:10,padding:"10px 12px"}}>
            <p style={{margin:"0 0 4px",fontSize:12,fontWeight:700,color:"#92400E"}}>⏳ სამრეცხაოში რჩება:</p>
            {(order.remaining_items||[]).map((ri,i)=>(
              <p key={i} style={{margin:"2px 0",fontSize:12,color:"#92400E"}}>{ri.name}: {ri.pieces>0?`${ri.pieces} ც. `:""}{ri.qty} {ri.unit}</p>
            ))}
          </div>
        )}
        {userRole==="hotel" && order.status==="PENDING_LAUNDRY_APPROVAL" && (
          <Btn color="#EF4444" outline onClick={()=>{if(window.confirm("შეკვეთა გაუქმდება. დარწმუნებული ხარ?"))onDelete&&onDelete(order.id);onClose();}} style={{width:"100%"}}>🗑 შეკვეთის გაუქმება</Btn>
        )}
      </div>
    </Modal>
  );
}

function NewOrderModal({ onClose, onSubmit, user, laundries, partnerDrivers=[], priceLists={}, onRequestPrices }) {
  const bp = useBreakpoint();
  const {pos:nPos, onMouseDown:nMD, onTouchStart:nTS} = useDraggable(0, 0);
  const isMob = bp==="mobile";

  const defaultId = laundries.length===1 ? String(laundries[0].id) :
                    (user?.laundry_id ? String(user.laundry_id) : "");
  const [laundryId, setLaundryId] = useState(defaultId);
  const todayDate = new Date();
  const defaultDate = todayDate.toISOString().slice(0,10);
  const defaultTime = `${String(todayDate.getHours()).padStart(2,"0")}:${String(todayDate.getMinutes()).padStart(2,"0")}`;
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState(defaultTime);
  const [notes, setNotes] = useState("");
  const [isUncounted, setIsUncounted] = useState(false);

  // Get price list items for selected laundry
  const selLaundry = laundries.find(l=>String(l.id)===String(laundryId));
  const plKey = selLaundry ? `${selLaundry.code}_${user.code}` : null;
  const pl = plKey ? priceLists[plKey] : null;

  const buildItems = () => {
    if(pl && pl.items.length>0) {
      return pl.items.filter(it=>it.price!==""&&it.price!=null&&parseFloat(it.price)>0)
        .map(it=>({name:it.name, unit:it.unit, value:"", pieces:"", price:it.price}));
    }
    return [];
  };

  const [items, setItems] = useState(buildItems);

  // Rebuild items when laundry changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{
    setItems(buildItems());
  }, [laundryId]);

  const upd = (i,v) => setItems(p=>p.map((x,j)=>j===i?{...x,value:v}:x));
  const updPieces = (i,v) => setItems(p=>p.map((x,j)=>j===i?{...x,pieces:v}:x));
  const removeItem = i => setItems(p=>p.filter((_,j)=>j!==i));

  const doSubmit = () => {
    const sel = laundries.find(l=>String(l.id)===String(laundryId));
    if (!sel) { alert("სამრეცხაო აირჩიე"); return; }
    if (!date||!time) { alert("Pickup თარიღი და დრო შეიყვანე"); return; }

    // Map to legacy fields + custom_items
    const find = name => items.find(it=>it.name===name);
    const sheet = find("თეთრეული");
    const towel = find("პირსახოცი");
    const cover = find("გადასაფარებელი");
    const custom = items.filter(it=>!["თეთრეული","პირსახოცი","გადასაფარებელი"].includes(it.name)&&it.value!==""&&parseFloat(it.value)>0);

    // Validation: if not uncounted, at least one item must have qty > 0
    if(!isUncounted){
      const totalQty=(parseFloat(sheet?.value)||0)+(parseFloat(towel?.value)||0)+(parseFloat(cover?.value)||0)+custom.length;
      if(totalQty===0){alert("⚠️ რაოდენობა შეიყვანე ან მონიშნე \"დაუთვლელია\"");return;}
    }

    onSubmit({
      pickup_date: date+"T"+time,
      sheets_count: sheet ? parseFloat(sheet.value)||0 : 0,
      sheets_unit:  sheet?.unit||"ც.",
      sheets_pieces: sheet ? parseFloat(sheet.pieces)||0 : 0,
      towels_kg:    towel ? parseFloat(towel.value)||0 : 0,
      towels_unit:  towel?.unit||"კგ",
      towels_pieces: towel ? parseFloat(towel.pieces)||0 : 0,
      covers_kg:    cover ? parseFloat(cover.value)||0 : 0,
      covers_unit:  cover?.unit||"კგ",
      covers_pieces: cover ? parseFloat(cover.pieces)||0 : 0,
      custom_items: custom.map(ci=>({name:ci.name,qty:parseFloat(ci.value)||0,unit:ci.unit,pieces:parseFloat(ci.pieces)||0})),
      notes,
      is_uncounted: isUncounted,
      laundry_id:   sel.id,
      laundry_name: sel.name,
      laundry_code: sel.code,
    });
  };

  return (
    <Modal title="შეკვეთის შექმნა" onClose={onClose}>
      {/* სამრეცხაო */}
      <div style={{marginBottom:12}}>
        <p style={{margin:"0 0 6px",fontSize:13,fontWeight:700,color:"#374151"}}>სამრეცხაო</p>
        {laundries.length===0 ? (
          <div style={{background:"#FEF3C7",borderRadius:10,padding:12,fontSize:13,color:"#92400E"}}>
            ⚠️ პარტნიორი სამრეცხაო არ არის.
          </div>
        ) : laundries.length===1 ? (
          <div style={{background:THEME.hotel.light,borderRadius:10,padding:10,
            display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:16}}>🧺</span>
            <div>
              <p style={{margin:0,fontWeight:700,fontSize:13}}>{laundries[0].name}</p>
              <p style={{margin:0,fontSize:11,color:"#6B7280"}}>{laundries[0].code}</p>
            </div>
          </div>
        ) : (
          <select value={laundryId} onChange={e=>setLaundryId(e.target.value)} style={S.input}>
            <option value="">-- სამრეცხაო აირჩიე --</option>
            {laundries.map(l=>(
              <option key={l.id} value={String(l.id)}>{l.name} · {l.code}</option>
            ))}
          </select>
        )}
      </div>

      {/* ნივთები */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <p style={{margin:0,fontSize:13,fontWeight:700,color:"#374151"}}>ნივთები</p>
        {pl && <span style={{fontSize:10,color:"#12B76A",fontWeight:600}}>✓ ფასების სია ჩატვირთულია</span>}
        {!pl && <span style={{fontSize:10,color:"#9CA3AF"}}>ნაგულისხმევი</span>}
      </div>

      {items.length===0 ? (
        <div style={{padding:"4px 0"}}>
          <div style={{background:"#FEF3C7",borderRadius:10,padding:"12px 14px",marginBottom:12}}>
            <p style={{margin:0,fontSize:12,color:"#92400E",lineHeight:1.5}}>
              {selLaundry ? "⚠️ ამ სამრეცხაოს ჯერ არ აქვს ფასები დაყენებული. შეკვეთის გასაკეთებლად ჯერ ფასები უნდა შეთანხმდეს." : "👆 ჯერ აირჩიე სამრეცხაო"}
            </p>
          </div>
          {selLaundry && <Btn color={THEME.hotel.primary} onClick={()=>{onRequestPrices&&onRequestPrices(selLaundry.code,selLaundry.name);onClose();}} style={{width:"100%"}}>📩 ფასების მოთხოვნა</Btn>}
        </div>
      ) : (<>
      {items.map((it,i)=>(
        <div key={i} style={{display:"flex",alignItems:"center",gap:8,
          background:"#F8FAFC",borderRadius:10,padding:"8px 10px",marginBottom:6}}>
          <div style={{flex:1}}>
            <p style={{margin:0,fontSize:13,fontWeight:500}}>{it.name}</p>
            {it.price && <p style={{margin:"1px 0 0",fontSize:10,color:"#12B76A"}}>₾{it.price}/{it.unit}</p>}
          </div>
          <input type="number" value={isUncounted?"":it.pieces} onChange={e=>updPieces(i,Math.max(0,e.target.value))} min="0" disabled={isUncounted}
            style={{...S.input,width:50,textAlign:"center",padding:"6px 8px",background:isUncounted?"#F3F4F6":undefined,cursor:isUncounted?"not-allowed":"text"}} placeholder="0"/>
          <span style={{fontSize:12,color:"#9CA3AF",width:16,textAlign:"left"}}>ც.</span>
          <input type="number" value={isUncounted?"":it.value} onChange={e=>upd(i,Math.max(0,e.target.value))} min="0" disabled={isUncounted}
            style={{...S.input,width:50,textAlign:"center",padding:"6px 8px",background:isUncounted?"#F3F4F6":undefined,cursor:isUncounted?"not-allowed":"text"}} placeholder="0"/>
          <span style={{fontSize:12,color:"#9CA3AF",width:24,textAlign:"left"}}>{it.unit}</span>
          <button onClick={()=>removeItem(i)}
            style={{background:"none",border:"none",color:"#EF4444",cursor:"pointer",fontSize:14,padding:0}}>✕</button>
        </div>
      ))}

      {/* Pickup */}
      <p style={{margin:"0 0 8px",fontSize:13,fontWeight:700}}>📅 Delivery — თარიღი და დრო</p>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
        <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={S.input}/>
        <input type="time" value={time} onChange={e=>setTime(e.target.value)}
          style={S.input} step="60"/>
      </div>
      <label style={{...S.lbl,marginBottom:14}}>სპეციალური მითითება
        <textarea rows={2} value={notes} onChange={e=>setNotes(e.target.value)}
          placeholder="შეიყვანე..." style={{...S.input,resize:"none"}}/>
      </label>

      {/* დაუთვლელია checkbox */}
      <div onClick={()=>setIsUncounted(v=>{const nv=!v;if(nv)setItems(p=>p.map(it=>({...it,value:"",pieces:""})));return nv;})}
        style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",
          borderRadius:12,marginBottom:14,cursor:"pointer",userSelect:"none",
          background:isUncounted?"#FEF3C7":"#F8FAFC",
          border:`1.5px solid ${isUncounted?"#F59E0B":"#E5E7EB"}`,
          transition:"all 0.15s"}}>
        <div style={{width:20,height:20,borderRadius:5,flexShrink:0,
          background:isUncounted?"#F59E0B":"#fff",
          border:`2px solid ${isUncounted?"#F59E0B":"#D1D5DB"}`,
          display:"flex",alignItems:"center",justifyContent:"center"}}>
          {isUncounted&&<span style={{color:"#fff",fontSize:13,fontWeight:700}}>✓</span>}
        </div>
        <div>
          <p style={{margin:0,fontSize:13,fontWeight:700,
            color:isUncounted?"#92400E":"#374151"}}>დაუთვლელია</p>
          <p style={{margin:0,fontSize:11,color:"#9CA3AF"}}>
            სამრეცხაო დაითვლის და ჩაწერს რაოდენობას
          </p>
        </div>
      </div>

      <Btn color={THEME.hotel.primary} onClick={doSubmit} style={{width:"100%"}}>გაგზავნა</Btn>
      </>)}
    </Modal>
  );
}


function CleaningTab({ rooms, setRooms, cleaningLog, setCleaningLog, color }) {
  const today = new Date().toISOString().slice(0,10);
  const [editMode, setEditMode] = useState(rooms.length === 0);
  const [roomInput, setRoomInput] = useState("");
  const [editingRoom, setEditingRoom] = useState(null); // {old, newVal}

  const todayChecked = cleaningLog[today] || [];

  const toggleRoom = (num) => {
    setCleaningLog(prev => {
      const cur = prev[today] || [];
      const next = cur.includes(num) ? cur.filter(r=>r!==num) : [...cur, num];
      return {...prev, [today]: next};
    });
  };

  const addRooms = () => {
    if(!roomInput.trim()) return;
    const parts = roomInput.split(/[,\s]+/).map(s=>s.trim()).filter(Boolean);
    const expanded = [];
    parts.forEach(p => {
      if(p.includes("-")) {
        const [a,b] = p.split("-").map(Number);
        if(!isNaN(a)&&!isNaN(b)) for(let i=a;i<=b;i++) expanded.push(String(i));
      } else {
        expanded.push(p);
      }
    });
    const unique = [...new Set([...rooms, ...expanded])].sort((a,b)=>Number(a)-Number(b)||a.localeCompare(b));
    setRooms(unique);
    setRoomInput("");
  };

  const removeRoom = (num) => setRooms(p=>p.filter(r=>r!==num));
  const resetAll   = () => setCleaningLog(prev=>({...prev,[today]:[]}));

  const renameRoom = (oldName, newName) => {
    const trimmed = newName.trim();
    if(!trimmed || trimmed===oldName) { setEditingRoom(null); return; }
    setRooms(p=>p.map(r=>r===oldName?trimmed:r));
    setCleaningLog(prev=>{
      const updated={};
      Object.keys(prev).forEach(d=>{
        updated[d]=prev[d].map(r=>r===oldName?trimmed:r);
      });
      return updated;
    });
    setEditingRoom(null);
  };

  const cleaned   = rooms.filter(r=>todayChecked.includes(r));
  const remaining = rooms.filter(r=>!todayChecked.includes(r));

  // ── EDIT MODE ──
  if(editMode) return (
    <div style={{padding:"14px 14px 14px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <p style={{margin:0,fontWeight:800,fontSize:15}}>⚙️ ოთახების რედაქტირება</p>
        {rooms.length>0 && (
          <button onClick={()=>setEditMode(false)} style={{background:color,color:"#fff",
            border:"none",borderRadius:8,padding:"6px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
            ✅ დასრულება
          </button>
        )}
      </div>

      {/* Add new rooms */}
      <div style={{...S.card,marginBottom:14}}>
        <p style={{margin:"0 0 6px",fontSize:13,fontWeight:700}}>ოთახების დამატება</p>
        <p style={{margin:"0 0 8px",fontSize:11,color:"#9CA3AF"}}>
          მძიმით, სფეისით ან დიაპაზონით: 101-110
        </p>
        <div style={{display:"flex",gap:8}}>
          <input value={roomInput} onChange={e=>setRoomInput(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&addRooms()}
            placeholder="101, 102, 201-210..." style={{...S.input,flex:1}}/>
          <button onClick={addRooms} style={{background:color,color:"#fff",border:"none",
            borderRadius:10,padding:"0 16px",fontWeight:700,fontSize:18,cursor:"pointer"}}>+</button>
        </div>
      </div>

      {/* Room list with rename & delete */}
      {rooms.length>0 && (
        <>
          <p style={{margin:"0 0 8px",fontWeight:700,fontSize:13}}>ოთახები ({rooms.length})</p>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {rooms.map(r=>(
              <div key={r} style={{...S.card,marginBottom:0,display:"flex",alignItems:"center",gap:8}}>
                {editingRoom?.old===r ? (
                  <>
                    <input autoFocus value={editingRoom.newVal}
                      onChange={e=>setEditingRoom(p=>({...p,newVal:e.target.value}))}
                      onKeyDown={e=>{
                        if(e.key==="Enter") renameRoom(r,editingRoom.newVal);
                        if(e.key==="Escape") setEditingRoom(null);
                      }}
                      style={{...S.input,flex:1,padding:"6px 10px",fontSize:13}}/>
                    <button onClick={()=>renameRoom(r,editingRoom.newVal)}
                      style={{background:color,color:"#fff",border:"none",borderRadius:8,
                        padding:"6px 12px",fontWeight:700,fontSize:12,cursor:"pointer"}}>შენახვა</button>
                    <button onClick={()=>setEditingRoom(null)}
                      style={{background:"none",border:"none",color:"#9CA3AF",cursor:"pointer",fontSize:16}}>✕</button>
                  </>
                ) : (
                  <>
                    <span style={{flex:1,fontWeight:700,fontSize:14}}>{r}</span>
                    <button onClick={()=>setEditingRoom({old:r,newVal:r})}
                      style={{background:"#F0F9FF",color:"#0369A1",border:"none",borderRadius:8,
                        padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>✏️ გადარქმევა</button>
                    <button onClick={()=>removeRoom(r)}
                      style={{background:"#FEF2F2",color:"#EF4444",border:"none",borderRadius:8,
                        padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>🗑 წაშლა</button>
                  </>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {rooms.length===0 && (
        <div style={{textAlign:"center",padding:"30px 0",color:"#9CA3AF"}}>
          <p style={{fontSize:32,margin:"0 0 8px"}}>🚪</p>
          <p style={{fontSize:13}}>ოთახი არ არის — დაამატე ზემოთ</p>
        </div>
      )}
    </div>
  );

  // ── DAILY VIEW ──
  return (
    <div style={{padding:"14px 14px 14px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
        <p style={{margin:0,fontWeight:800,fontSize:15}}>🧹 დასუფთავება</p>
        <button onClick={()=>setEditMode(true)} style={{background:"#F0FDF4",border:"none",
          borderRadius:8,color:"#16A34A",fontSize:11,fontWeight:600,cursor:"pointer",padding:"5px 10px"}}>
          ⚙️ ოთახების რედაქტირება
        </button>
      </div>
      <p style={{margin:"0 0 12px",fontSize:11,color:"#9CA3AF"}}>
        {new Date().toLocaleDateString("ka-GE",{weekday:"long",year:"numeric",month:"long",day:"numeric"})}
      </p>

      {/* Progress bar */}
      <div style={{...S.card,marginBottom:12}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
          <span style={{fontSize:12,fontWeight:700,color:"#16A34A"}}>✅ {cleaned.length} დასუფთავდა</span>
          <span style={{fontSize:12,color:"#9CA3AF"}}>🔲 {remaining.length} დარჩა</span>
        </div>
        <div style={{background:"#E5E7EB",borderRadius:999,height:8,overflow:"hidden"}}>
          <div style={{background:"#16A34A",height:"100%",borderRadius:999,
            width:`${rooms.length>0?(cleaned.length/rooms.length)*100:0}%`,
            transition:"width 0.3s"}}/>
        </div>
        {cleaned.length>0 && (
          <button onClick={resetAll} style={{background:"none",border:"none",
            color:"#9CA3AF",fontSize:11,cursor:"pointer",marginTop:6,padding:0}}>
            ↺ ყველა გასუფთავება
          </button>
        )}
      </div>

      {/* Remaining rooms */}
      {remaining.length>0 && (
        <>
          <p style={{margin:"0 0 8px",fontSize:12,fontWeight:700,color:"#374151"}}>
            🔲 სასუფთავი ოთახები
          </p>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:14}}>
            {remaining.map(r=>(
              <div key={r} onClick={()=>toggleRoom(r)}
                style={{background:"#fff",border:"2px solid #E5E7EB",borderRadius:12,
                  padding:"14px 8px",textAlign:"center",cursor:"pointer",
                  transition:"transform 0.1s"}}
                onTouchStart={e=>e.currentTarget.style.transform="scale(0.94)"}
                onTouchEnd={e=>e.currentTarget.style.transform="scale(1)"}
                onMouseDown={e=>e.currentTarget.style.transform="scale(0.94)"}
                onMouseUp={e=>e.currentTarget.style.transform="scale(1)"}>
                <p style={{margin:0,fontWeight:800,fontSize:16}}>{r}</p>
                <p style={{margin:"2px 0 0",fontSize:9,color:"#9CA3AF"}}>სასუფთავი</p>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Cleaned rooms */}
      {cleaned.length>0 && (
        <>
          <p style={{margin:"0 0 8px",fontSize:12,fontWeight:700,color:"#16A34A"}}>
            ✅ დასუფთავებული
          </p>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
            {cleaned.map(r=>(
              <div key={r} onClick={()=>toggleRoom(r)}
                style={{background:"#F0FDF4",border:"2px solid #86EFAC",borderRadius:12,
                  padding:"14px 8px",textAlign:"center",cursor:"pointer",
                  transition:"transform 0.1s"}}
                onTouchStart={e=>e.currentTarget.style.transform="scale(0.94)"}
                onTouchEnd={e=>e.currentTarget.style.transform="scale(1)"}
                onMouseDown={e=>e.currentTarget.style.transform="scale(0.94)"}
                onMouseUp={e=>e.currentTarget.style.transform="scale(1)"}>
                <p style={{margin:0,fontWeight:800,fontSize:16,color:"#16A34A"}}>✓ {r}</p>
                <p style={{margin:"2px 0 0",fontSize:9,color:"#16A34A",opacity:0.7}}>დასუფთავდა</p>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── History Section ── */}
      {Object.keys(cleaningLog).filter(d=>d!==today).length > 0 && (
        <div style={{marginTop:20}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
            <div style={{flex:1,height:1,background:"#E5E7EB"}}/>
            <p style={{margin:0,fontSize:12,fontWeight:700,color:"#9CA3AF"}}>📅 ისტორია</p>
            <div style={{flex:1,height:1,background:"#E5E7EB"}}/>
          </div>
          {Object.keys(cleaningLog)
            .filter(d=>d!==today && cleaningLog[d]?.length > 0)
            .sort((a,b)=>b.localeCompare(a))
            .slice(0,30)
            .map(date=>{
              const cleanedThen = cleaningLog[date]||[];
              const notCleaned  = rooms.filter(r=>!cleanedThen.includes(r));
              const dateObj = new Date(date);
              const label = dateObj.toLocaleDateString("ka-GE",{day:"numeric",month:"long",year:"numeric"});
              return (
                <div key={date} style={{...S.card,marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <p style={{margin:0,fontSize:12,fontWeight:700,color:"#374151"}}>📅 {label}</p>
                    <span style={{fontSize:11,color:"#9CA3AF"}}>
                      ✅ {cleanedThen.length} · ❌ {notCleaned.length}
                    </span>
                  </div>
                  {cleanedThen.length>0 && (
                    <div style={{marginBottom:notCleaned.length>0?6:0}}>
                      <p style={{margin:"0 0 4px",fontSize:10,fontWeight:600,color:"#16A34A"}}>✅ დასუფთავდა:</p>
                      <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                        {cleanedThen.map(r=>(
                          <span key={r} style={{background:"#F0FDF4",border:"1px solid #86EFAC",
                            borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700,color:"#16A34A"}}>
                            {r}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {notCleaned.length>0 && (
                    <div>
                      <p style={{margin:"0 0 4px",fontSize:10,fontWeight:600,color:"#EF4444"}}>❌ არ დასუფთავდა:</p>
                      <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                        {notCleaned.map(r=>(
                          <span key={r} style={{background:"#FEF2F2",border:"1px solid #FECACA",
                            borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700,color:"#EF4444"}}>
                            {r}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          }
        </div>
      )}
    </div>
  );
}

function TechServiceTab({ color, userCode }) {
  const [requests, setRequests] = useLS(`tech_${userCode}`, []);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({type:"", desc:"", urgent:false});

  const TYPES = ["სანტექნიკა","ელექტრო","კონდ./გათბობა","ლიფტი","ინტერნეტი","კარ-ფანჯარა","სხვა"];

  const submit = () => {
    if(!form.type||!form.desc.trim()) return;
    setRequests(p=>[{
      id:Date.now(), type:form.type, desc:form.desc,
      urgent:form.urgent, status:"ახალი",
      created:new Date().toLocaleString("ka-GE",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})
    },...p]);
    setForm({type:"",desc:"",urgent:false});
    setShowForm(false);
  };

  const statusColor = s => s==="შესრულდა"?"#12B76A":s==="მიმდინარე"?"#1B6EF3":"#F59E0B";
  const statusBg    = s => s==="შესრულდა"?"#E6F9F1":s==="მიმდინარე"?"#EBF2FF":"#FEF3C7";

  return (
    <div style={{padding:"14px 14px 14px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <p style={{margin:0,fontWeight:800,fontSize:15}}>🔧 ტექნიკური სერვ. გამოძახება</p>
        <button onClick={()=>setShowForm(v=>!v)} style={{
          background:color,color:"#fff",border:"none",borderRadius:10,
          padding:"7px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>+ ახალი</button>
      </div>

      {showForm && (
        <div style={{...S.card,marginBottom:14,background:"#FEF2F2"}}>
          <p style={{margin:"0 0 10px",fontWeight:700,fontSize:13}}>გამოძახების ფორმა</p>
          <p style={{margin:"0 0 6px",fontSize:12,fontWeight:600,color:"#374151"}}>სერვისის სახეობა</p>
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
            {TYPES.map(t=>(
              <button key={t} onClick={()=>setForm(p=>({...p,type:t}))} style={{
                padding:"5px 10px",borderRadius:999,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,
                background:form.type===t?color:"#F3F4F6",
                color:form.type===t?"#fff":"#374151"}}>
                {t}
              </button>
            ))}
          </div>
          <textarea rows={3} placeholder="პრობლემის აღწერა..." value={form.desc}
            onChange={e=>setForm(p=>({...p,desc:e.target.value}))}
            style={{...S.input,resize:"none",marginBottom:10}}/>
          <label style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,cursor:"pointer"}}>
            <input type="checkbox" checked={form.urgent}
              onChange={e=>setForm(p=>({...p,urgent:e.target.checked}))}/>
            <span style={{fontSize:13,fontWeight:600,color:"#DC2626"}}>🚨 სასწრაფოა</span>
          </label>
          <div style={{display:"flex",gap:8}}>
            <Btn color={color} small onClick={submit} style={{flex:1}}>გაგზავნა</Btn>
            <Btn color="#6B7280" outline small onClick={()=>setShowForm(false)} style={{flex:1}}>გაუქმება</Btn>
          </div>
        </div>
      )}

      {requests.length===0 && !showForm && (
        <div style={{textAlign:"center",padding:"30px 0",color:"#9CA3AF"}}>
          <p style={{fontSize:32,margin:"0 0 8px"}}>🔧</p>
          <p style={{fontSize:13}}>გამოძახება არ არის</p>
        </div>
      )}

      {requests.map(r=>(
        <div key={r.id} style={{...S.card}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontWeight:700,fontSize:13}}>{r.type}</span>
              {r.urgent&&<span style={{background:"#FEE2E2",color:"#DC2626",fontSize:10,fontWeight:700,
                padding:"2px 7px",borderRadius:999}}>🚨 სასწრაფო</span>}
            </div>
            <span style={{background:statusBg(r.status),color:statusColor(r.status),
              padding:"3px 10px",borderRadius:999,fontSize:11,fontWeight:700}}>{r.status}</span>
          </div>
          <p style={{margin:"0 0 4px",fontSize:12,color:"#374151"}}>{r.desc}</p>
          <p style={{margin:0,fontSize:10,color:"#9CA3AF"}}>{r.created}</p>
          <div style={{display:"flex",gap:6,marginTop:8}}>
            {["ახალი","მიმდინარე","შესრულდა"].map(s=>(
              <button key={s} onClick={()=>setRequests(p=>p.map(x=>x.id===r.id?{...x,status:s}:x))}
                style={{flex:1,padding:"5px 0",border:"none",borderRadius:8,cursor:"pointer",
                  fontSize:11,fontWeight:600,
                  background:r.status===s?statusColor(s):"#F3F4F6",
                  color:r.status===s?"#fff":"#6B7280"}}>{s}</button>
            ))}
            <button onClick={()=>setRequests(p=>p.filter(x=>x.id!==r.id))}
              style={{padding:"5px 8px",border:"none",borderRadius:8,cursor:"pointer",
                background:"#FEE2E2",color:"#DC2626",fontSize:12}}>🗑</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function PurchasesTab({ color, userCode }) {
  const [items, setItems] = useLS(`purchases_${userCode}`, []);
  const [text, setText] = useState("");
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("ც.");

  const add = () => {
    if(!text.trim()) return;
    setItems(p=>[{id:Date.now(), text:text.trim(), qty:qty||"1", unit, done:false},...p]);
    setText(""); setQty("");
  };

  const toggle = id => setItems(p=>p.map(x=>x.id===id?{...x,done:!x.done}:x));
  const remove = id => setItems(p=>p.filter(x=>x.id!==id));
  const clearDone = () => setItems(p=>p.filter(x=>!x.done));

  const undone = items.filter(x=>!x.done);
  const done   = items.filter(x=>x.done);

  return (
    <div style={{padding:"14px 14px 14px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <p style={{margin:0,fontWeight:800,fontSize:15}}>🛒 შესყიდვები</p>
        <p style={{margin:0,fontSize:12,color:"#9CA3AF"}}>{undone.length} დარჩა</p>
      </div>

      {/* Add form */}
      <div style={{...S.card,marginBottom:14}}>
        <div style={{display:"flex",gap:8,marginBottom:8}}>
          <input value={text} onChange={e=>setText(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&add()}
            placeholder="პროდუქტი / ნივთი..." style={{...S.input,flex:1}}/>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <input value={qty} onChange={e=>setQty(e.target.value)}
            placeholder="რაოდ." type="number"
            style={{...S.input,width:70,textAlign:"center"}}/>
          <select value={unit} onChange={e=>setUnit(e.target.value)}
            style={{...S.input,width:70}}>
            {["ც.","კგ","ლ.","კოლ.","პაკ."].map(u=><option key={u}>{u}</option>)}
          </select>
          <button onClick={add} style={{background:color,color:"#fff",border:"none",
            borderRadius:10,padding:"10px 16px",fontWeight:700,fontSize:18,cursor:"pointer"}}>+</button>
        </div>
      </div>

      {items.length===0 && (
        <div style={{textAlign:"center",padding:"20px 0",color:"#9CA3AF"}}>
          <p style={{fontSize:32,margin:"0 0 8px"}}>🛒</p>
          <p style={{fontSize:13}}>სია ცარიელია</p>
        </div>
      )}

      {/* Undone */}
      {undone.map(x=>(
        <div key={x.id} style={{...S.card,display:"flex",alignItems:"center",gap:10}}>
          <button onClick={()=>toggle(x.id)} style={{
            width:22,height:22,borderRadius:"50%",border:"2px solid #D1D5DB",
            background:"transparent",cursor:"pointer",flexShrink:0,
            display:"flex",alignItems:"center",justifyContent:"center"}}/>
          <p style={{margin:0,flex:1,fontSize:13,fontWeight:500}}>{x.text}</p>
          <span style={{fontSize:12,color:"#9CA3AF"}}>{x.qty} {x.unit}</span>
          <button onClick={()=>remove(x.id)} style={{background:"none",border:"none",
            color:"#EF4444",cursor:"pointer",fontSize:16,padding:0}}>✕</button>
        </div>
      ))}

      {/* Divider */}
      {undone.length>0&&done.length>0&&(
        <div style={{display:"flex",alignItems:"center",gap:8,margin:"10px 0 8px"}}>
          <div style={{flex:1,height:1,background:"#E5E7EB"}}/>
          <span style={{fontSize:11,color:"#9CA3AF"}}>შეძენილი</span>
          <div style={{flex:1,height:1,background:"#E5E7EB"}}/>
        </div>
      )}

      {/* Done */}
      {done.map(x=>(
        <div key={x.id} style={{...S.card,display:"flex",alignItems:"center",gap:10,opacity:0.6}}>
          <button onClick={()=>toggle(x.id)} style={{
            width:22,height:22,borderRadius:"50%",border:`2px solid ${color}`,
            background:color,cursor:"pointer",flexShrink:0,
            display:"flex",alignItems:"center",justifyContent:"center"}}>
            <span style={{color:"#fff",fontSize:12}}>✓</span>
          </button>
          <p style={{margin:0,flex:1,fontSize:13,textDecoration:"line-through",color:"#9CA3AF"}}>{x.text}</p>
          <span style={{fontSize:12,color:"#9CA3AF"}}>{x.qty} {x.unit}</span>
          <button onClick={()=>remove(x.id)} style={{background:"none",border:"none",
            color:"#EF4444",cursor:"pointer",fontSize:16,padding:0}}>✕</button>
        </div>
      ))}

      {done.length>0&&(
        <button onClick={clearDone} style={{background:"none",border:"none",
          color:"#9CA3AF",fontSize:11,cursor:"pointer",marginTop:6,padding:0}}>
          🗑 შეძენილი წაშლა
        </button>
      )}
    </div>
  );
}

function NoteTab({ notes, setNotes, color }) {
  const [text, setText] = useState("");
  const [alertTime, setAlertTime] = useState("");
  const [alertDate, setAlertDate] = useState(new Date().toISOString().slice(0,10));
  const [showAlertInput, setShowAlertInput] = useState(false);
  const [firing, setFiring] = useState(null);

  const notesRef = useRef(notes);
  useEffect(()=>{ notesRef.current = notes; }, [notes]);

  useEffect(() => {
    const check = () => {
      const now = new Date();
      const nowStr = `${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`;
      const today = now.toISOString().slice(0,10);
      notesRef.current.forEach(n => {
        if(n.alertTime && n.alertDate===today && n.alertTime===nowStr && !n.done && !n.alertFired) {
          setNotes(p=>p.map(x=>x.id===n.id?{...x,alertFired:true}:x));
          setFiring(n.id);
          playAlertSound();
        }
      });
    };
    const timer = setInterval(check, 30000);
    check();
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const playAlertSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const playBeep = (freq, start, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = "sine";
        gain.gain.setValueAtTime(0, ctx.currentTime + start);
        gain.gain.linearRampToValueAtTime(0.4, ctx.currentTime + start + 0.05);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + start + dur);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + dur + 0.05);
      };
      playBeep(880, 0,    0.18);
      playBeep(880, 0.22, 0.18);
      playBeep(1100,0.44, 0.3);
    } catch(e) {}
  };

  const addNote = () => {
    if(!text.trim()) return;
    setNotes(p=>[{
      id:Date.now(), text:text.trim(), done:false,
      created:fmt24(),
      alertTime: alertTime||null,
      alertDate: alertTime ? alertDate : null,
      alertFired: false,
    },...p]);
    setText(""); setAlertTime(""); setAlertDate(new Date().toISOString().slice(0,10)); setShowAlertInput(false);
  };

  const toggle    = (id) => setNotes(p=>p.map(n=>n.id===id?{...n,done:!n.done}:n));
  const remove    = (id) => setNotes(p=>p.filter(n=>n.id!==id));
  const clearDone = ()   => setNotes(p=>p.filter(n=>!n.done));
  const dismissAlert = (id) => { setFiring(null); setNotes(p=>p.map(n=>n.id===id?{...n,alertFired:true}:n)); };

  const undone = notes.filter(n=>!n.done);
  const done   = notes.filter(n=>n.done);

  const [editingNote, setEditingNote] = useState(null);
  const [editText, setEditText] = useState("");
  const [editTime, setEditTime] = useState("");
  const [editDate, setEditDate] = useState("");

  const startEdit = (n) => {
    setEditingNote(n.id);
    setEditText(n.text);
    setEditTime(n.alertTime||"");
    setEditDate(n.alertDate||new Date().toISOString().slice(0,10));
  };
  const saveEdit = (id) => {
    setNotes(p=>p.map(n=>n.id===id?{...n,text:editText,alertTime:editTime||null,alertDate:editTime?editDate:null,alertFired:false}:n));
    setEditingNote(null);
  };

  const NoteItem = ({n}) => (
    <div style={{...S.card,
      background: firing===n.id ? "#FEF3C7" : S.card.background,
      border: firing===n.id ? "2px solid #F59E0B" : "none"}}>
      {editingNote===n.id ? (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <input value={editText} onChange={e=>setEditText(e.target.value)}
            style={{...S.input,fontSize:13}}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            <input type="date" value={editDate} onChange={e=>setEditDate(e.target.value)} style={S.input}/>
            <input type="time" value={editTime} onChange={e=>setEditTime(e.target.value)} style={S.input}/>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn color={color} small onClick={()=>saveEdit(n.id)} style={{flex:1}}>✅ შენახვა</Btn>
            <Btn color="#6B7280" outline small onClick={()=>setEditingNote(null)} style={{flex:1}}>გაუქმება</Btn>
          </div>
        </div>
      ) : (
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button onClick={()=>toggle(n.id)} style={{
            border:`2px solid ${n.done?"#12B76A":"#D1D5DB"}`,
            borderRadius:"50%",width:22,height:22,cursor:"pointer",flexShrink:0,
            background:n.done?"#12B76A":"transparent",
            display:"flex",alignItems:"center",justifyContent:"center"}}>
            {n.done && <span style={{color:"#fff",fontSize:12}}>✓</span>}
          </button>
          <div style={{flex:1,minWidth:0}}>
            <p style={{margin:0,fontSize:13,
              textDecoration:n.done?"line-through":"none",
              color:n.done?"#9CA3AF":"#111827"}}>{n.text}</p>
            {n.alertTime && !n.done && (
              <p style={{margin:"2px 0 0",fontSize:10,
                color: firing===n.id ? "#92400E" : "#9CA3AF"}}>
                {firing===n.id ? "🔔 შეხსენება!" : `⏰ ${n.alertDate} · ${n.alertTime}`}
              </p>
            )}
          </div>
          {firing===n.id && (
            <button onClick={()=>dismissAlert(n.id)} style={{
              background:"#F59E0B",color:"#fff",border:"none",
              borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
              OK
            </button>
          )}
          {!n.done && <button onClick={()=>startEdit(n)} style={{background:"none",border:"none",
            color:"#6B7280",cursor:"pointer",fontSize:13,padding:0}}>✏️</button>}
          <button onClick={()=>remove(n.id)} style={{background:"none",border:"none",
            color:"#EF4444",cursor:"pointer",fontSize:16,padding:0}}>✕</button>
        </div>
      )}
    </div>
  );

  return (
    <div style={{padding:"14px 14px 14px"}}>
      <p style={{margin:"0 0 12px",fontWeight:800,fontSize:15}}>📝 Note — ჩანიშვნები</p>

      {/* Add form */}
      <div style={{...S.card,marginBottom:14}}>
        <div style={{display:"flex",gap:8,marginBottom:showAlertInput?10:0}}>
          <input value={text} onChange={e=>setText(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&addNote()}
            placeholder="ახალი ტასკი..." style={{...S.input,flex:1}}/>
          <button onClick={()=>setShowAlertInput(v=>!v)} style={{
            background:showAlertInput?"#FEF3C7":"#F3F4F6",
            border:`1.5px solid ${showAlertInput?"#F59E0B":"#E5E7EB"}`,
            borderRadius:10,padding:"0 12px",cursor:"pointer",fontSize:16}}
            title="ალერტის დაყენება">⏰</button>
          <button onClick={addNote} style={{background:color,color:"#fff",border:"none",
            borderRadius:10,padding:"0 16px",fontWeight:700,fontSize:18,cursor:"pointer"}}>+</button>
        </div>
        {showAlertInput && (
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <div>
                <p style={{margin:"0 0 4px",fontSize:11,fontWeight:600,color:"#92400E"}}>📅 თარიღი</p>
                <input type="date" value={alertDate||new Date().toISOString().slice(0,10)}
                  onChange={e=>setAlertDate(e.target.value)}
                  style={{...S.input,fontSize:12}}/>
              </div>
              <div>
                <p style={{margin:"0 0 4px",fontSize:11,fontWeight:600,color:"#92400E"}}>⏰ დრო</p>
                <input type="time" value={alertTime} onChange={e=>setAlertTime(e.target.value)}
                  style={{...S.input,fontSize:12}}/>
              </div>
            </div>
            {(alertTime||alertDate) && (
              <button onClick={()=>{setAlertTime("");setAlertDate("");}}
                style={{background:"none",border:"none",color:"#9CA3AF",
                  cursor:"pointer",fontSize:11,textAlign:"left",padding:0}}>
                ✕ ალერტის გაუქმება
              </button>
            )}
          </div>
        )}
      </div>

      {notes.length===0 && (
        <div style={{textAlign:"center",padding:"30px 0",color:"#9CA3AF"}}>
          <p style={{fontSize:32,margin:"0 0 8px"}}>📋</p>
          <p style={{fontSize:13}}>ჩანიშვნა არ არის</p>
        </div>
      )}

      {/* შესასრულებელი */}
      {undone.length>0 && (
        <>
          <p style={{margin:"0 0 8px",fontSize:11,fontWeight:700,color:"#374151",
            textTransform:"uppercase",letterSpacing:0.5}}>
            შესასრულებელი — {undone.length}
          </p>
          {undone.map(n=><NoteItem key={n.id} n={n}/>)}
        </>
      )}

      {/* გამყოფი */}
      {undone.length>0 && done.length>0 && (
        <div style={{display:"flex",alignItems:"center",gap:8,margin:"14px 0 10px"}}>
          <div style={{flex:1,height:1,background:"#E5E7EB"}}/>
          <span style={{fontSize:11,color:"#9CA3AF",whiteSpace:"nowrap"}}>შესრულებული</span>
          <div style={{flex:1,height:1,background:"#E5E7EB"}}/>
        </div>
      )}

      {/* შესრულებული */}
      {done.length>0 && (
        <>
          {done.length>0 && undone.length===0 && (
            <p style={{margin:"0 0 8px",fontSize:11,fontWeight:700,color:"#9CA3AF",
              textTransform:"uppercase",letterSpacing:0.5}}>შესრულებული — {done.length}</p>
          )}
          {done.map(n=><NoteItem key={n.id} n={n}/>)}
          <button onClick={clearDone} style={{background:"none",border:"none",
            color:"#9CA3AF",fontSize:11,cursor:"pointer",marginTop:6,padding:0}}>
            🗑 შესრულებული წაშლა
          </button>
        </>
      )}
    </div>
  );
}

function DraggableCards({ user, mine, partnerLaundries, invoices, drivers, laundries, partnerDrivers, vacantList, cardOrder, setCardOrder, onNavigate, notes, setNotes, rooms, pinnedCards=[], onTogglePin }) {
  const getCards = () => [
    { icon:"📋", label:"შეკვეთები",         sub:`${mine.length} სულ`,                                             dest:"შეკვეთები",          bg:"#EBF2FF", c:"#1B6EF3" },
    { icon:"🧾", label:"ინვოისები",         sub:`${invoices.filter(i=>i.hotel_code===user.code).length} ინვ.`,        dest:"ინვოისები",          bg:"#F0EBFF", c:"#7C3AED" },
    { icon:"🤝", label:"პარტნიორი სამრეცხაო", sub:`${partnerLaundries.length} პარტნ.`,                            dest:"პარტნიორები",        bg:"#E6F9F1", c:"#12B76A" },
    { icon:"🧺", label:"სამრეცხაოების სია", sub:`${laundries.length} სამრეცხაო`,                                  dest:"სამრეცხაოები",       bg:"#FFF1F2", c:"#E11D48" },
    { icon:"🚗", label:"ვაკ. მძღოლები",    sub:`${(vacantList||[]).length} ვაკ.`,                              dest:"ვაკანტური",          bg:"#F0F9FF", c:"#0369A1" },
    { icon:"🤝", label:"პარტნ. მძღოლები",  sub:`${(partnerDrivers||[]).length} პარტნ.`,                        dest:"პარტნიორი_მძღოლები", bg:"#EFF6FF", c:"#1D4ED8" },
    { icon:"🧹", label:"დასუფთავება",       sub:`${(rooms||[]).length>0?(rooms||[]).length+" ოთახი":"ოთახი არ არის"}`, dest:"cleaning",    bg:"#F0FDF4", c:"#16A34A" },
    { icon:"📝", label:"Note",              sub:`${(notes||[]).filter(n=>!n.done).length} შეუსრულებელი`,           dest:"note",               bg:"#FFFBEB", c:"#B45309" },
    { icon:"🔧", label:"ტექ. სერვ.",        sub:"სწრაფი გამოძახება",                                              dest:"tech_service",       bg:"#FEF2F2", c:"#DC2626" },
    { icon:"🛒", label:"შესყიდვები",        sub:"შეკვეთები და სია",                                              dest:"purchases",           bg:"#F5F3FF", c:"#6D28D9" },
    { icon:"📊", label:"რეპორტი",           sub:`${mine.length} შეკვ.`,                                           dest:"რეპორტი",            bg:"#E2E8F0", c:"#334155" },
  ];

  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);
  const [pressed, setPressed] = useState(null);
  const dragStart = useRef(null);
  const longPressTimer = useRef(null);
  const [longPressHint, setLongPressHint] = useState(null);

  const startLongPress = (cardIdx) => {
    longPressTimer.current = setTimeout(()=>{
      const cards = getCards();
      const card = cards[cardIdx];
      if(card && onTogglePin) {
        onTogglePin(card.dest);
        setLongPressHint(cardIdx);
        setTimeout(()=>setLongPressHint(null), 1200);
      }
    }, 600);
  };
  const cancelLongPress = () => {
    if(longPressTimer.current) clearTimeout(longPressTimer.current);
  };
  const cards = getCards();

  const onTouchStart = (e, idx) => {
    dragStart.current = { idx, t: Date.now() };
    setDragging(idx);
  };

  const onTouchMove = (e) => {
    if (dragging === null) return;
    e.preventDefault();
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const cardEl = el?.closest("[data-cardidx]");
    if (cardEl) {
      const targetIdx = parseInt(cardEl.getAttribute("data-cardidx"));
      if (targetIdx !== dragging) setOver(targetIdx);
    }
  };

  const onTouchEnd = () => {
    if (dragging !== null && over !== null && dragging !== over) {
      setCardOrder(prev => {
        const next = [...prev];
        const fromPos = next.indexOf(dragging);
        const toPos   = next.indexOf(over);
        next.splice(fromPos, 1);
        next.splice(toPos, 0, dragging);
        return next;
      });
    }
    setDragging(null);
    setOver(null);
  };

  const onMouseDown = (e, idx) => {
    dragStart.current = { idx, t: Date.now() };
    setDragging(idx);
  };

  const onMouseEnter = (idx) => { if (dragging !== null) setOver(idx); };

  const onMouseUp = () => {
    if (dragging !== null && over !== null && dragging !== over) {
      setCardOrder(prev => {
        const next = [...prev];
        const fromPos = next.indexOf(dragging);
        const toPos   = next.indexOf(over);
        next.splice(fromPos, 1);
        next.splice(toPos, 0, dragging);
        return next;
      });
    }
    setDragging(null);
    setOver(null);
  };

  const bp = useBreakpoint();
  const hCols = bp==="mobile" ? 2 : 4;
  const hRows = bp==="mobile" ? 6 : 3;
  const hGap  = bp==="mobile" ? 8 : 12;
  const hPad  = bp==="mobile" ? "10px" : "16px";

  return (
    <div style={{padding:hPad,height:"100%",boxSizing:"border-box",display:"flex",flexDirection:"column",overflow:"hidden"}}
      onMouseUp={onMouseUp}
      onTouchEnd={onTouchEnd}
      onTouchMove={onTouchMove}
    >
      <p style={{margin:"0 0 8px",fontSize:10,color:"#9CA3AF",flexShrink:0}}>✥ გადაათრიე გადასაწყობად</p>
      {(()=>{
        const cards=getCards();
        const pinnedWithCards=pinnedCards.filter(k=>cards.some(c=>c.dest===k));
        const filtered=pinnedWithCards.length>0?cardOrder.filter(i=>pinnedWithCards.includes(cards[i]?.dest)):cardOrder;
        return (
      <div style={{flex:1,display:"grid",
        gridTemplateColumns:`repeat(${hCols},1fr)`,
        gridTemplateRows:`repeat(${hRows},1fr)`,
        gap:hGap,minHeight:0}}>
        {filtered.map(cardIdx => {
          const card = cards[cardIdx];
          const isDragging = dragging === cardIdx;
          const isOver     = over === cardIdx;
          return (
            <div
              key={card.label}
              data-cardidx={cardIdx}
              onTouchStart={e=>{setPressed(cardIdx);onTouchStart(e,cardIdx);startLongPress(cardIdx);}}
              onTouchEnd={()=>{setPressed(null);onTouchEnd();cancelLongPress();}}
              onMouseDown={e=>{setPressed(cardIdx);onMouseDown(e,cardIdx);startLongPress(cardIdx);}}
              onMouseUp={()=>{setPressed(null);cancelLongPress();}}
              onMouseLeave={e=>{setPressed(null); if(dragging===null){e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="none";e.currentTarget.style.outline="none";}}}
              onMouseEnter={e=>{onMouseEnter(cardIdx); if(dragging===null){e.currentTarget.style.transform="translateY(-5px)";e.currentTarget.style.boxShadow=`0 8px 20px rgba(0,0,0,0.10)`;e.currentTarget.style.outline="none";}}}
              onClick={()=>{
                if(!dragStart.current || Date.now()-dragStart.current.t < 200){
                  if(card.dest) onNavigate(card.dest);
                }
              }}
              style={{
                background: card.bg,
                borderRadius:16,
                padding:"18px 14px",
                cursor: isDragging ? "grabbing" : "grab",
                display:"flex",flexDirection:"column",alignItems:"center",
                justifyContent:"center",gap:8,textAlign:"center",minHeight:110,
                opacity: isDragging ? 0.45 : 1,
                outline: isOver ? `2.5px dashed ${card.c}` : "none",
                transform: pressed===cardIdx && !isDragging ? "translateY(2px)" : "translateY(0)",
                boxShadow: pinnedCards.includes(card.dest) ? `0 0 0 2px ${card.c}` : "none",
                minHeight:0,
                transition:"opacity 0.15s,outline 0.15s,transform 0.2s ease,box-shadow 0.2s ease",
                userSelect:"none",
                WebkitUserSelect:"none",
                touchAction:"none",
              }}>
              {longPressHint===cardIdx && (
                <div style={{position:"absolute",top:6,right:6,fontSize:16}}>
                  {pinnedCards.includes(card.dest)?"⭐":"☆"}
                </div>
              )}
              <span style={{fontSize:26}}>{card.icon}</span>
              <div>
                <p style={{margin:0,fontWeight:700,fontSize:12,color:card.c}}>{card.label}</p>
                <p style={{margin:"2px 0 0",fontSize:10,color:card.c,opacity:0.75}}>{card.sub}</p>
              </div>
            </div>
          );
        })}
      </div>
        );})()}
    </div>
  );
}

function HotelApp({ user, orders, invoices, hotels, laundries, drivers, vacantDrivers, hotelDriverPartners, onAddDriverPartner, onRemoveDriverPartner, onStatusChange, onNewOrder, partnerRequests, onSendPartnerRequest, onPartnerResponse, priceLists, priceNotifs, setPriceNotifs, onDeleteOrder }) {
  const { dark: darkMode } = useTheme();
  const [tab,setTab]=useState("მთავარი");
  const [detail,setDetail]=useState(null);
  const [showNew,setShowNew]=useState(false);
  const [filter,setFilter]=useState(null);
  const [cardOrder,setCardOrder]=useLS(`hcardOrder_${user.code}`,[0,1,2,3,4,5,6,7,8,9,10]);
  // Add missing + remove duplicates
  useEffect(()=>{
    const allIdx = [0,1,2,3,4,5,6,7,8,9,10];
    const deduped = [...new Set(cardOrder)];
    const missing = allIdx.filter(i=>!deduped.includes(i));
    const final=[...deduped,...missing];
    if(JSON.stringify(final)!==JSON.stringify(cardOrder)) setCardOrder(final);
  },[]);
  const [hotelNotes,setHotelNotes]=useLS(`notes_${user.code}`,[]);
  const [pinnedCards,setPinnedCards]=useLS(`pinned_${user.code}`,[]);
  const togglePin=key=>setPinnedCards(p=>p.includes(key)?p.filter(k=>k!==key):[...p,key]);
  const {granted:osGranted, request:requestOS, send:sendOS} = useOSNotifications();
  useEffect(()=>{ requestOS(); },[]);
  const [hotelRooms,setHotelRooms]=useLS(`rooms_${user.code}`,[]);
  const [cleaningLog,setCleaningLog]=useLS(`cleaning_${user.code}`,{});
  const [notifs,setNotifs]=useState([]);
  const [showNotifs,setShowNotifs]=useState(false);
  const [laundrySearch,setLaundrySearch]=useState("");
  const unreadCount = notifs.filter(n=>!n.read).length;

  // Watch priceNotifs for payment reminders from laundry → add to bell
  const seenReminderIds = useRef(new Set());
  useEffect(()=>{
    const reminders = (priceNotifs||[]).filter(n=>
      n.hotelCode===user.code && !n.read && !n.forLaundry && n.type==="reminder"
    );
    reminders.forEach(n=>{
      if(!seenReminderIds.current.has(n.id)){
        seenReminderIds.current.add(n.id);
        setNotifs(p=>[{id:n.id||Date.now(),read:false,
          text:n.msg||"📨 გადახდის შეხსენება",time:n.time||fmt24()},...p]);
      }
    });
  },[priceNotifs]);
  const prevOrderCount=useRef(orders.filter(o=>o.hotel_id===user.id).length);
  const prevInvCount=useRef(invoices.filter(i=>i.hotel_code===user.code).length);
  useEffect(()=>{
    const curOrds=orders.filter(o=>o.hotel_id===user.id).length;
    const curInvs=invoices.filter(i=>i.hotel_code===user.code).length;
    // Hotel created the order itself - no self-notification needed
    if(curInvs>prevInvCount.current){
      setNotifs(p=>[{id:Date.now()+1,read:false,text:`🧾 ინვოისი მოვიდა — გადახდა მოსათხოვია`,time:fmt24()},...p]);
      sendOS("🧾 CPG — ინვოისი", "ახალი ინვოისი მოვიდა. გადახდა მოსათხოვია.");
      try{
        const ctx=new(window.AudioContext||window.webkitAudioContext)();
        [[440,0,0.1],[554,0.15,0.1],[659,0.3,0.2],[880,0.55,0.3]].forEach(([f,s,d])=>{
          const o=ctx.createOscillator(),g=ctx.createGain();
          o.connect(g);g.connect(ctx.destination);
          o.frequency.value=f;o.type="sine";
          g.gain.setValueAtTime(0,ctx.currentTime+s);
          g.gain.linearRampToValueAtTime(0.3,ctx.currentTime+s+0.03);
          g.gain.linearRampToValueAtTime(0,ctx.currentTime+s+d);
          o.start(ctx.currentTime+s);o.stop(ctx.currentTime+s+d+0.05);
        });
      }catch(e){}
    }
    prevOrderCount.current=curOrds;
    prevInvCount.current=curInvs;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[orders,invoices]);
  const [orderFilter,setOrderFilter]=useState(null);
  const [collapsedLaundries,setCollapsedLaundries]=useState([]);
  const { primary:color, grad } = THEME.hotel;

  const mine     = orders.filter(o=>o.hotel_id===user.id || o.hotel_code===user.code);
  const active   = mine.filter(o=>ACTIVE_STATUSES.includes(o.status));
  const transit  = mine.filter(o=>["DRIVER_ON_THE_WAY","DELIVERY_ON_THE_WAY"].includes(o.status));
  const ready    = mine.filter(o=>o.status==="READY_FOR_DELIVERY");

  const partnerLaundries = laundries.filter(l => {
    if (user.laundry_code && l.code===user.laundry_code) return true;
    return partnerRequests.some(r=>
      r.status==="accepted" &&
      ((r.from_code===user.code && r.to_code===l.code) ||
       (r.to_code===user.code && r.from_code===l.code))
    );
  });
  const myPartnerDriverIds = hotelDriverPartners.filter(x=>x.hotel_id===user.id||x.hotel_code===user.code).map(x=>x.driver_id||x.driver_code);
  const partnerDrivers = vacantDrivers.filter(d=>myPartnerDriverIds.includes(d.id)||myPartnerDriverIds.includes(d.code));
  const allPartneredIds = hotelDriverPartners.map(x=>x.driver_id||x.driver_code);
  const vacantList = vacantDrivers.filter(d=>!allPartneredIds.includes(d.id)&&!allPartneredIds.includes(d.code));

  const tabs=[
    {key:"მთავარი",        icon:"🏠", label:"მთავარი"},
    {divider:true},
    {key:"შეკვეთები",      icon:"📋", label:"შეკვეთები"},
    {key:"ინვოისები",      icon:"🧾", label:"ინვოისები"},
    {key:"პარტნიორები",    icon:"🤝", label:"პარტნ. სამრ."},
    {key:"სამრეცხაოები",   icon:"🧺", label:"სამრ. სია"},
    {key:"ვაკანტური",      icon:"🚗", label:"ვაკ. მძღოლები"},
    {key:"პარტნიორი_მძღოლები", icon:"🚗", label:"პარტნ. მძღოლ."},
    {divider:true},
    {key:"cleaning",       icon:"🧹", label:"დასუფთავება"},
    {key:"note",           icon:"📝", label:"Note"},
    {key:"tech_service",   icon:"🔧", label:"ტექ. სერვ."},
    {key:"purchases",      icon:"🛒", label:"შესყიდვები"},
    {divider:true},
    {key:"რეპორტი",        icon:"📊", label:"რეპორტი"},
    {divider:true},
    {key:"პროფილი",        icon:"👤", label:"პროფილი"},
    {key:"help",           icon:"💬", label:"Help"},
  ];

  const incomingReqsH = partnerRequests.filter(r=>r.to_code===user.code && r.status==="pending");
  const bottomTabs=[
    {key:"მთავარი",    icon:"🏠", label:"მთავარი"},
    {key:"შეკვეთები",  icon:"📋", label:"შეკვეთები"},
    {key:"პარტნიორები",icon:"🤝", label:"პარტნ."},
    {key:"პროფილი",    icon:"👤", label:"პროფილი"},
    {key:"help",       icon:"💬", label:"Help"},
  ];



  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",background:darkMode?"#0F172A":"#F8FAFC"}}>
      <TrialBanner user={user}/>
      {!osGranted && <NotifPermBanner onRequest={requestOS}/>}
      <Header color={color} grad={grad} icon="🏨" title="სასტუმროს პანელი" hotelName={user.name}
        onBell={()=>{setShowNotifs(v=>!v);setNotifs(p=>p.map(n=>({...n,read:true})));}}
        bellBadge={unreadCount}
        sub={
          <p style={{margin:0,color:"rgba(255,255,255,0.9)",fontWeight:800,fontSize:18}}>
            {user.name}
          </p>
        }
      />
      {showNotifs && (
        <div style={{position:"absolute",top:70,right:12,zIndex:300,
          background:"#fff",borderRadius:14,boxShadow:"0 4px 24px rgba(0,0,0,0.15)",
          width:260,maxHeight:300,overflowY:"auto",padding:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <p style={{margin:0,fontWeight:700,fontSize:13}}>შეტყობინებები</p>
            <button onClick={()=>setNotifs([])} style={{background:"none",border:"none",
              color:"#9CA3AF",fontSize:11,cursor:"pointer"}}>ყველა წაშლა</button>
          </div>
          {notifs.length===0 && <p style={{color:"#9CA3AF",fontSize:12,textAlign:"center",margin:"16px 0"}}>შეტყობინება არ არის</p>}
          {notifs.map(n=>(
            <div key={n.id} style={{...S.card,marginBottom:6,padding:"8px 10px"}}>
              <p style={{margin:0,fontSize:12,fontWeight:600}}>🔔 {n.text}</p>
              <p style={{margin:"2px 0 0",fontSize:10,color:"#9CA3AF"}}>{n.time}</p>
            </div>
          ))}
        </div>
      )}

      <div style={{flex:1,display:"flex",flexDirection:"row",minHeight:0,overflow:"hidden"}}>
      <NavBar tabs={tabs} bottomTabs={bottomTabs} active={tab} onChange={setTab} color={color} badges={{"პარტნიორები": incomingReqsH.length}} pinned={pinnedCards} onTogglePin={togglePin}/>
      <div style={{flex:1,overflow:tab==="მთავარი"?"hidden":"auto",position:"relative",display:"flex",flexDirection:"column",minHeight:0}} onClick={()=>showNotifs&&setShowNotifs(false)}>
        {tab==="მთავარი" && (
          <DraggableCards
            user={user} mine={mine} partnerLaundries={partnerLaundries}
            invoices={invoices} drivers={drivers} laundries={laundries}
            partnerDrivers={partnerDrivers} vacantList={vacantList}
            cardOrder={cardOrder} setCardOrder={setCardOrder}
            notes={hotelNotes} setNotes={setHotelNotes}
            rooms={hotelRooms}
            pinnedCards={pinnedCards}
            onTogglePin={togglePin}
            onNavigate={setTab}
          />
        )}

        {tab==="შეკვეთები" && (()=>{
          const FILTERS = [
            {key:"ყველა",        statuses:null,                                                                bg:"#E2E8F0", c:"#334155"},
            {key:"ახალი",        statuses:["PENDING_LAUNDRY_APPROVAL"],                                       bg:"#FEF3C7", c:"#92400E"},
            {key:"ჩასაბარებელი",  statuses:["DELIVERED","PARTIALLY_DELIVERED"], bg:"#EBF2FF", c:"#1355CC"},
            {key:"სამრეცხაოშია", statuses:["RECEIVED_IN_LAUNDRY"],                                           bg:"#E6F9F1", c:"#12B76A"},
            {key:"მზადაა",       statuses:["READY_FOR_DELIVERY"],                                             bg:"#F0EBFF", c:"#7C3AED"},
            {key:"ნაწილობრივი", statuses:["PARTIALLY_DELIVERED"],                                       bg:"#FEF3C7", c:"#92400E"},
            {key:"დაუთვლელი",   statuses:null, uncountedOnly:true,                                          bg:"#FFF7ED", c:"#EA580C"},
          ];
          const activeF = orderFilter ? FILTERS.find(f=>f.key===orderFilter) : FILTERS[0];
          const displayed = (!activeF || (!activeF.statuses && !activeF.uncountedOnly)) ? mine
            : activeF.uncountedOnly ? mine.filter(o=>o.is_uncounted)
            : mine.filter(o=>activeF.statuses.includes(o.status));
          return (
          <div style={{padding:"14px 14px 14px"}}>
            {/* Stats / filter row */}
            <div style={{display:"flex",gap:6,marginBottom:14,justifyContent:"center"}}>
              {FILTERS.map(s=>{
                const cnt = s.uncountedOnly ? mine.filter(o=>o.is_uncounted).length : s.statuses ? mine.filter(o=>s.statuses.includes(o.status)).length : mine.length;
                const active = (orderFilter===s.key) || (!orderFilter && s.key==="ყველა");
                return (
                  <div key={s.key} onClick={()=>setOrderFilter(s.key==="ყველა" ? null : s.key)}
                    style={{background:active?s.c:s.bg, borderRadius:10, padding:"9px 0",
                      textAlign:"center", cursor:"pointer", flex:1,
                      transition:"background 0.15s, transform 0.2s ease, box-shadow 0.2s ease"}}
                    onMouseEnter={e=>{if(!active){e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,0.08)";}}}
                    onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="none";}}>
                    <p style={{margin:0,fontWeight:800,fontSize:16,color:active?"#fff":s.c}}>{cnt}</p>
                    <p style={{margin:"2px 0 0",fontSize:10,fontWeight:700,color:active?"#fff":s.c}}>{s.key}</p>
                  </div>
                );
              })}
            </div>

            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <p style={{margin:0,fontWeight:800,fontSize:15}}>შეკვეთები
                <span style={{marginLeft:6,fontSize:12,fontWeight:500,color:"#9CA3AF"}}>{displayed.length} შეკვ.</span>
              </p>
              <button onClick={()=>setShowNew(true)} style={{
                background:color,color:"#fff",border:"none",borderRadius:10,
                padding:"8px 14px",fontWeight:700,fontSize:12,cursor:"pointer",
                display:"flex",alignItems:"center",gap:5}}>
                <span style={{fontSize:16,lineHeight:1}}>+</span>ახალი
              </button>
            </div>

            {/* Grouped by laundry if 2+ partners */}
            {partnerLaundries.length >= 2 ? (
              partnerLaundries.map(l=>{
                const lOrders = displayed.filter(o=>o.laundry_id===l.id);
                if(lOrders.length===0) return null;
                const collapsed = (collapsedLaundries||[]).includes(l.id);
                return (
                  <div key={l.id} style={{marginBottom:12}}>
                    <div onClick={()=>setCollapsedLaundries(p=>p.includes(l.id)?p.filter(x=>x!==l.id):[...p,l.id])}
                      style={{display:"flex",alignItems:"center",gap:8,marginBottom:collapsed?0:8,
                        background:THEME.hotel.light,borderRadius:10,padding:"8px 12px",cursor:"pointer"}}>
                      <span style={{fontSize:13}}>🧺</span>
                      <span style={{fontWeight:700,fontSize:13,color,flex:1}}>{l.name}</span>
                      <span style={{fontSize:11,color:"#9CA3AF"}}>{lOrders.length} შეკვ.</span>
                      <span style={{fontSize:16,fontWeight:700,color,marginLeft:4}}>{collapsed?"＋":"－"}</span>
                    </div>
                    {!collapsed && lOrders.map(o=>(
                      <div key={o.id} onClick={()=>setDetail(o)} style={{...S.card,cursor:"pointer"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                          <div>
                            <p style={{margin:0,fontWeight:700,fontSize:14}}>#{o.order_number}</p>
                            <p style={{margin:"3px 0 1px",fontSize:12,color:"#6B7280"}}>
                              {new Date(o.pickup_date).toLocaleDateString("ka-GE")}
                            </p>
                            <p style={{margin:"2px 0 0",fontSize:11,color:"#6B7280"}}>
                              {[o.sheets_count>0&&`🛏 ${o.sheets_count}ც.`,o.towels_kg>0&&`🧺 ${o.towels_kg}კგ`,o.covers_kg>0&&`🛌 ${o.covers_kg}კგ`,...(o.custom_items||[]).map(ci=>`${ci.name} ${ci.qty}${ci.unit}`)].filter(Boolean).join(' · ')}
                            </p>
                          </div>
                          <Badge status={o.status}/>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })
            ) : (
              displayed.map(o=>(
                <div key={o.id} onClick={()=>setDetail(o)} style={{...S.card,cursor:"pointer"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                    <div>
                      <p style={{margin:0,fontWeight:700,fontSize:14}}>#{o.order_number}</p>
                      <p style={{margin:"3px 0 1px",fontSize:12,color:"#6B7280"}}>
                        {new Date(o.pickup_date).toLocaleDateString("ka-GE")}
                      </p>
                      <p style={{margin:"2px 0 0",fontSize:11,color:"#6B7280"}}>
                        {[o.sheets_count>0&&`🛏 ${o.sheets_count}ც.`,o.towels_kg>0&&`🧺 ${o.towels_kg}კგ`,o.covers_kg>0&&`🛌 ${o.covers_kg}კგ`,...(o.custom_items||[]).map(ci=>`${ci.name} ${ci.qty}${ci.unit}`)].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <Badge status={o.status}/>
                  </div>
                </div>
              ))
            )}
            {displayed.length===0 && <p style={{color:"#9CA3AF",textAlign:"center",marginTop:30}}>შეკვეთა არ არის</p>}
          </div>
          );
        })()}

        {tab==="პარტნიორები" && (
          <>
            {priceNotifs&&priceNotifs.filter(n=>n.hotelCode===user.code&&!n.read&&!n.forLaundry).map(n=>(
              <div key={n.id} style={{margin:"10px 14px 0",background:"#FEF3C7",borderRadius:12,
                padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:16}}>🔔</span>
                <div style={{flex:1}}>
                  <p style={{margin:0,fontSize:12,fontWeight:600,color:"#92400E"}}>{n.msg}</p>
                  <p style={{margin:"2px 0 0",fontSize:10,color:"#92400E",opacity:0.7}}>{n.time}</p>
                </div>
                <button onClick={()=>setPriceNotifs(p=>p.map(x=>x.id===n.id?{...x,read:true}:x))}
                  style={{background:"none",border:"none",color:"#92400E",cursor:"pointer",fontSize:14}}>✕</button>
              </div>
            ))}
            <PartnersPage user={user} hotels={hotels} laundries={laundries}
              partnerRequests={partnerRequests}
              onSendRequest={onSendPartnerRequest}
              onPartnerResponse={onPartnerResponse}
              priceLists={priceLists}/>
          </>
        )}
        {tab==="ინვოისები" && <InvoicesPage invoices={invoices} userId={user.id} userCode={user.code} userRole="hotel" user={user} laundries={partnerLaundries}/>}
        {tab==="რეპორტი" && <ReportsPage orders={orders} invoices={invoices} userId={user.id} userCode={user.code} userRole="hotel" color={color}/>}

        {tab==="პროფილი" && (
          <HotelProfile user={user} color={color} />
        )}
        {tab==="help" && <HelpChat user={user}/>}

        {tab==="note" && (
          <NoteTab notes={hotelNotes} setNotes={setHotelNotes} color={color}/>
        )}

        {tab==="cleaning" && (
          <CleaningTab rooms={hotelRooms} setRooms={setHotelRooms}
            cleaningLog={cleaningLog} setCleaningLog={setCleaningLog} color="#16A34A"/>
        )}

        {tab==="tech_service" && (
          <TechServiceTab color="#DC2626" userId={user.id} userCode={user.code}/>
        )}

        {tab==="purchases" && (
          <PurchasesTab color="#6D28D9" userId={user.id} userCode={user.code}/>
        )}

        {tab==="ვაკანტური" && (
          <div style={{padding:"14px 14px 14px"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
              <p style={{margin:0,fontWeight:800,fontSize:15}}>ვაკანტური მძღოლები</p>
              <span style={{marginLeft:"auto",background:"#FEF3C7",color:"#92400E",
                padding:"3px 10px",borderRadius:999,fontSize:12,fontWeight:700}}>{vacantList.length}</span>
            </div>
            {vacantList.length===0 && (
              <p style={{color:"#9CA3AF",textAlign:"center",marginTop:40}}>ვაკანტური მძღოლი არ არის</p>
            )}
            {vacantList.map(d=>(
              <div key={d.id} style={{...S.card,display:"flex",alignItems:"center",gap:12}}>
                <Ava name={d.name} size={42}/>
                <div style={{flex:1}}>
                  <p style={{margin:0,fontWeight:700,fontSize:14}}>{d.name}</p>
                  <p style={{margin:"2px 0 0",fontSize:12,color:"#6B7280"}}>{d.code}</p>
                  {d.phone && <p style={{margin:"2px 0 0",fontSize:12,color:"#6B7280"}}>📞 {d.phone}</p>}
                </div>
                <button onClick={()=>onAddDriverPartner(d.id)} style={{
                  background:"#1B6EF3",color:"#fff",border:"none",borderRadius:8,
                  padding:"7px 12px",fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>
                  + დამატება
                </button>
              </div>
            ))}
          </div>
        )}

        {tab==="პარტნიორი_მძღოლები" && (
          <div style={{padding:"14px 14px 14px"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
              <p style={{margin:0,fontWeight:800,fontSize:15}}>პარტნიორი მძღოლები</p>
              <span style={{marginLeft:"auto",background:"#E0F2FE",color:"#0369A1",
                padding:"3px 10px",borderRadius:999,fontSize:12,fontWeight:700}}>{partnerDrivers.length}</span>
            </div>
            {partnerDrivers.length===0 && (
              <div style={{textAlign:"center",padding:"30px 0",color:"#9CA3AF"}}>
                <p style={{fontSize:32,margin:"0 0 8px"}}>🚗</p>
                <p style={{fontSize:13,margin:0}}>პარტნიორი მძღოლი არ არის</p>
                <p style={{fontSize:12,margin:"4px 0 0",color:"#CBD5E1"}}>ვაკანტური მძღოლებიდან დაამატე</p>
              </div>
            )}
            {partnerDrivers.map(d=>(
              <div key={d.id} style={{...S.card,display:"flex",alignItems:"center",gap:12}}>
                <Ava name={d.name} size={42}/>
                <div style={{flex:1}}>
                  <p style={{margin:0,fontWeight:700,fontSize:14}}>{d.name}</p>
                  <p style={{margin:"2px 0 0",fontSize:12,color:"#6B7280"}}>{d.code}</p>
                  {d.phone && <p style={{margin:"2px 0 0",fontSize:12,color:"#6B7280"}}>📞 {d.phone}</p>}
                  <span style={{background:"#D1FAE5",color:"#065F46",
                    padding:"2px 8px",borderRadius:999,fontSize:11,fontWeight:600}}>ხელმისაწვდომი</span>
                </div>
                <button onClick={()=>onRemoveDriverPartner(d.id)} style={{
                  background:"none",color:"#EF4444",border:"1.5px solid #EF4444",borderRadius:8,
                  padding:"7px 10px",fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>
                  წაშლა
                </button>
              </div>
            ))}
          </div>
        )}

        {tab==="სამრეცხაოები" && (
          <div style={{padding:"14px 14px 14px"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
              <p style={{margin:0,fontWeight:800,fontSize:15}}>სამრეცხაოების სია</p>
            </div>
            <input
              type="text"
              value={laundrySearch}
              onChange={e=>setLaundrySearch(e.target.value)}
              placeholder="🔍 ძებნა სახელით, ქალაქით, მისამართით, ტელეფონით, კოდით..."
              style={{...S.input,marginBottom:12,width:"100%",boxSizing:"border-box"}}
            />
            {(()=>{
              const q = laundrySearch.trim().toLowerCase();
              const filtered = q === "" ? laundries : laundries.filter(l=>
                [l.name,l.city,l.address,l.phone,l.code].some(f=>f && String(f).toLowerCase().includes(q))
              );
              if(filtered.length===0){
                return <p style={{color:"#9CA3AF",textAlign:"center",marginTop:30}}>შედეგი არ მოიძებნა</p>;
              }
              return filtered.map(l=>{
                const lOrders = orders.filter(o=>o.laundry_id===l.id);
                const completed = lOrders.filter(o=>o.status==="COMPLETED").length;
                const total = lOrders.length;
                const rate = total>0 ? completed/total : 0;
                const stars = Math.max(1, Math.round(1 + rate*4));
                return (
                  <div key={l.id} style={{...S.card}}>
                    <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                      <Ava name={l.name} size={44}/>
                      <div style={{flex:1}}>
                        <p style={{margin:0,fontWeight:700,fontSize:14}}>{l.name}</p>
                        <div style={{display:"flex",flexWrap:"wrap",gap:"2px 12px",marginTop:4}}>
                          {l.city    && <p style={{margin:0,fontSize:12,color:"#6B7280"}}>🏙️ {l.city}</p>}
                          {l.address && <p style={{margin:0,fontSize:12,color:"#9CA3AF"}}>📍 {l.address}</p>}
                          {l.phone   && <p style={{margin:0,fontSize:12,color:"#374151"}}>📞 {l.phone}</p>}
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6}}>
                          <span style={{fontSize:13,color:"#F59E0B",letterSpacing:1}}>{"★".repeat(stars)}{"☆".repeat(5-stars)}</span>
                          <span style={{fontSize:11,color:"#9CA3AF"}}>{total>0?`${completed}/${total} დასრულდა`:"შეკვეთა ჯერ არ არის"}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}

      </div>

      </div>

      {detail && <OrderDetailModal order={detail} onClose={()=>setDetail(null)}
        userRole="hotel" color={color} onAction={onStatusChange}
        onDelete={id=>{onDeleteOrder&&onDeleteOrder(id);setDetail(null);}}/>}
      {showNew && <NewOrderModal onClose={()=>setShowNew(false)} user={user} laundries={partnerLaundries} partnerDrivers={partnerDrivers} priceLists={priceLists}
        onRequestPrices={(lCode,lName)=>{setPriceNotifs&&setPriceNotifs(p=>[{id:Date.now(),forLaundry:lCode,hotelCode:user.code,msg:`🏨 ${user.name}-ს სურს ფასების დაყენება`,read:false,time:fmt24()},...p]);}}
        onSubmit={f=>{onNewOrder(f);setShowNew(false);}}/>}
      {filter && (
        <Modal title={filter.label} onClose={()=>setFilter(null)}>
          {filter.orders.map(o=>(
            <div key={o.id} onClick={()=>{setDetail(o);setFilter(null);}} style={{...S.card,cursor:"pointer",marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <div>
                  <p style={{margin:0,fontWeight:700}}>#{o.order_number}</p>
                  <p style={{margin:"3px 0 0",fontSize:12,color:"#6B7280"}}>{o.hotel_name}</p>
                </div>
                <Badge status={o.status}/>
              </div>
            </div>
          ))}
        </Modal>
      )}
    </div>
  );
}


function PriceListManager({ laundryCode, hotelCode, hotelName, priceLists, setPriceLists, onClose, onNotify }) {
  const key = `${laundryCode}_${hotelCode}`;
  const existing = priceLists[key] || { items:[] };

  const DEFAULT_ITEMS = ["თეთრეული","პირსახოცი","გადასაფარებელი","ფარდები","საბანი"];
  const initItems = DEFAULT_ITEMS.map(name=>{
    const found = existing.items.find(i=>i.name===name);
    return found || {name, unit:"კგ", price:""};
  }).concat(existing.items.filter(i=>!DEFAULT_ITEMS.includes(i.name)));

  const [items, setItems] = useState(initItems);
  const [newName, setNewName] = useState("");
  const [newUnit, setNewUnit] = useState("კგ");

  const setPrice = (idx,v) => setItems(p=>p.map((it,i)=>i===idx?{...it,price:v}:it));
  const setUnit  = (idx,v) => setItems(p=>p.map((it,i)=>i===idx?{...it,unit:v}:it));
  const removeItem = idx   => setItems(p=>p.filter((_,i)=>i!==idx));

  const addCustom = () => {
    if(!newName.trim()) return;
    setItems(p=>[...p,{name:newName.trim(),unit:newUnit,price:""}]);
    setNewName(""); setNewUnit("კგ");
  };

  const save = async () => {
    const filled = items.filter(i=>i.price!=="");
    const prev = priceLists[key];
    const changed = !prev || JSON.stringify(prev.items)!==JSON.stringify(filled);
    const updatedAt = new Date().toISOString().slice(0,10);
    const okPl = await sbWrite(supabase.from('price_lists').upsert([{
        laundry_code: laundryCode,
        hotel_code: hotelCode,
        items: filled,
        updated_at: updatedAt,
      }], {onConflict:'laundry_code,hotel_code'}), 'ფასების შენახვა');
    if (okPl === false) return;
    setPriceLists(p=>({...p,[key]:{items:filled,updatedAt}}));
    if(changed && onNotify) onNotify(hotelCode, `🔔 ${hotelName}-ის ფასები განახლდა სამრეცხაოს მიერ`);
    onClose();
  };

  return (
    <Modal title={`ფასები — ${hotelName}`} onClose={onClose}>
      <p style={{margin:"0 0 12px",fontSize:12,color:"#6B7280"}}>
        ფასი ცარიელი — ინვოისში "?" გამოჩნდება
      </p>
      <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14}}>
        {items.map((it,idx)=>(
          <div key={idx} style={{display:"flex",alignItems:"center",gap:8,
            background:"#F8FAFC",borderRadius:10,padding:"8px 10px"}}>
            <p style={{margin:0,flex:1,fontSize:13,fontWeight:500}}>{it.name}</p>
            <select value={it.unit} onChange={e=>setUnit(idx,e.target.value)}
              style={{...S.input,width:60,padding:"6px 4px",fontSize:12}}>
              {["კგ","ც.","კომ.","პაკ."].map(u=><option key={u}>{u}</option>)}
            </select>
            <div style={{display:"flex",alignItems:"center",gap:4}}>
              <input type="number" placeholder="0.00" value={it.price}
                onChange={e=>setPrice(idx,e.target.value)}
                style={{...S.input,width:70,textAlign:"right",fontSize:13,padding:"6px 8px"}}/>
              <span style={{fontSize:12,color:"#6B7280"}}>₾</span>
            </div>
            <button onClick={()=>removeItem(idx)}
              style={{background:"none",border:"none",color:"#EF4444",cursor:"pointer",fontSize:14}}>✕</button>
          </div>
        ))}
      </div>

      {/* Custom item add */}
      <div style={{display:"flex",gap:8,marginBottom:14,background:"#EBF2FF",
        borderRadius:10,padding:"10px 10px"}}>
        <input placeholder="ახალი item..." value={newName}
          onChange={e=>setNewName(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&addCustom()}
          style={{...S.input,flex:1,fontSize:12}}/>
        <select value={newUnit} onChange={e=>setNewUnit(e.target.value)}
          style={{...S.input,width:60,padding:"6px 4px",fontSize:12}}>
          {["კგ","ც.","კომ.","პაკ."].map(u=><option key={u}>{u}</option>)}
        </select>
        <button onClick={addCustom} style={{background:"#1B6EF3",color:"#fff",border:"none",
          borderRadius:10,padding:"0 14px",fontWeight:700,fontSize:16,cursor:"pointer"}}>+</button>
      </div>

      <Btn color="#12B76A" onClick={save} style={{width:"100%"}}>✅ შენახვა</Btn>
    </Modal>
  );
}

function LaundryApp({ user, orders, drivers, hotels, laundries, invoices, onStatusChange, onCreateInvoice, partnerRequests, priceLists, setPriceLists, priceNotifs, setPriceNotifs, onSendPartnerRequest, onPartnerResponse, onAssignDriver, driverRequests=[] }) {
  const { dark: darkMode } = useTheme();
  const [tab,setTab]=useState("მთავარი");
  const [detail,setDetail]=useState(null);
  const [laundryNotes,setLaundryNotes]=useLS(`lnotes_${user.code}`,[]);
  const [laundryCardOrder,setLaundryCardOrder]=useLS(`lcardOrder_${user.code}`,[0,1,2,3,4,5,6,7]);
  useEffect(()=>{
    const allIdx=[0,1,2,3,4,5,6,7];
    const deduped=[...new Set(laundryCardOrder)];
    const missing=allIdx.filter(i=>!deduped.includes(i));
    const final=[...deduped,...missing];
    if(JSON.stringify(final)!==JSON.stringify(laundryCardOrder)) setLaundryCardOrder(final);
  },[]);
  const [pinnedCardsL,setPinnedCardsL]=useLS(`pinned_${user.code}`,[]);
  const [inviteTarget,setInviteTarget]=useState(null);
  const [hotelSection,setHotelSection]=useState("other");
  const [dirCityFilter,setDirCityFilter]=useState("");
  const [hotelSearch,setHotelSearch]=useState("");
  const [hotelTypeFilter,setHotelTypeFilter]=useState("all");
  const togglePinL=key=>setPinnedCardsL(p=>p.includes(key)?p.filter(k=>k!==key):[...p,key]);
  const {request:requestOSL, send:sendOSL} = useOSNotifications();
  useEffect(()=>{ requestOSL(); },[]);
    const [lDragging,setLDragging]=useState(null);
  const lLongPressTimer=useRef(null);
  const [lLongPressHint,setLLongPressHint]=useState(null);
  const lStartLongPress=(idx,dest)=>{lLongPressTimer.current=setTimeout(()=>{togglePinL(dest);setLLongPressHint(idx);setTimeout(()=>setLLongPressHint(null),1200);},600);};
  const lCancelLongPress=()=>{if(lLongPressTimer.current)clearTimeout(lLongPressTimer.current);};
  const [lOver,setLOver]=useState(null);
  const [lOrderFilter,setLOrderFilter]=useState(null);
  const [priceModal,setPriceModal]=useState(null);
  const handlePriceNotify=(hotelCode,msg)=>{
    setPriceNotifs(p=>[{id:Date.now(),hotelCode,msg,read:false,time:fmt24()},...p]);
  };
  const [lCollapsedHotels,setLCollapsedHotels]=useState([]);
  const [lCollapsedCitiesPartner,setLCollapsedCitiesPartner]=useState([]);
  const [lCollapsedCitiesLaundries,setLCollapsedCitiesLaundries]=useState([]);
  const [lCollapsedPDrivers,setLCollapsedPDrivers]=useState(false);
  const [lCollapsedAllDrivers,setLCollapsedAllDrivers]=useState(false);
  const { primary:color, grad } = THEME.laundry;
  const bp = useBreakpoint();
  const lCols = bp==="mobile" ? 2 : bp==="tablet" ? 4 : 4;
  const lRows = bp==="mobile" ? 4 : 2;
  const lGap  = bp==="mobile" ? 8 : 12;
  const lPad  = bp==="mobile" ? "10px" : "16px";

  const mine    = orders.filter(o=>o.laundry_code===user.code);
  const newOrds = mine.filter(o=>o.status==="PENDING_LAUNDRY_APPROVAL");
  const inProg  = mine.filter(o=>["APPROVED","DRIVER_ASSIGNED","DRIVER_ON_THE_WAY","PICKED_UP",
    "RECEIVED_IN_LAUNDRY","DELIVERY_DRIVER_ASSIGNED","DELIVERY_ON_THE_WAY"].includes(o.status));
  const rdyDel  = mine.filter(o=>o.status==="READY_FOR_DELIVERY");
  const acceptedDriverIds = (driverRequests||[]).filter(r=>r.laundry_code===user.code&&r.status==="accepted").map(r=>r.driver_id);
  const myDrvs  = drivers.filter(d=>d.laundry_code===user.code||acceptedDriverIds.includes(d.id));
  const myHotels = hotels.filter(h=>{
    if(h.laundry_code===user.code) return true;
    return partnerRequests.some(r=>
      r.status==="accepted" &&
      ((r.from_code===user.code&&r.to_code===h.code)||(r.to_code===user.code&&r.from_code===h.code))
    );
  });



  const incomingReqs = partnerRequests.filter(r=>r.to_code===user.code && r.status==="pending");
  const [showLaundryNotifs,setShowLaundryNotifs]=useState(false);
  const [laundryNotifs,setLaundryNotifs]=useState([]);
  const myPriceNotifs = (priceNotifs||[]).filter(n=>n.forLaundry===user.code&&!n.read);

  const playLaundryAlert = () => {
    try {
      const ctx=new(window.AudioContext||window.webkitAudioContext)();
      [[660,0,0.15],[880,0.2,0.15],[1100,0.4,0.25]].forEach(([f,s,d])=>{
        const o=ctx.createOscillator(),g=ctx.createGain();
        o.connect(g);g.connect(ctx.destination);
        o.frequency.value=f;o.type="sine";
        g.gain.setValueAtTime(0,ctx.currentTime+s);
        g.gain.linearRampToValueAtTime(0.35,ctx.currentTime+s+0.04);
        g.gain.linearRampToValueAtTime(0,ctx.currentTime+s+d);
        o.start(ctx.currentTime+s);o.stop(ctx.currentTime+s+d+0.05);
      });
    } catch(e){}
  };

  // Play sound when new custom item notif arrives
  const prevNotifCount = useRef((priceNotifs||[]).filter(n=>n.forLaundry===user.code).length);
  const prevOrderCount2 = useRef(mine.length);
  useEffect(()=>{
    const cur=(priceNotifs||[]).filter(n=>n.forLaundry===user.code).length;
    if(cur>prevNotifCount.current) playLaundryAlert();
    prevNotifCount.current=cur;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[priceNotifs]);
  useEffect(()=>{
    if(mine.length>prevOrderCount2.current) {
      playLaundryAlert();
      sendOSL("📋 CPG — ახალი შეკვეთა", "ახალი შეკვეთა მოვიდა. დადასტურება მოითხოვება.");
    }
    prevOrderCount2.current=mine.length;
  },[mine.length]);

  const tabs=[
    {key:"მთავარი",     icon:"🏠", label:"მთავარი"},
    {divider:true},
    {key:"შეკვეთები",   icon:"📋", label:"შეკვეთები"},
    {key:"ინვოისები",   icon:"🧾", label:"ინვოისები"},
    {key:"პარტნიორები", icon:"🤝", label:"პარტნ."},
    {key:"l_hotels",    icon:"🏢", label:"კატალოგი"},
    {key:"l_p_drivers", icon:"🚗", label:"პარტნ. მძღოლ."},
    {key:"l_all_drivers",icon:"👥", label:"მძღოლ. სია"},
    {divider:true},
    {key:"l_note",      icon:"📝", label:"Note"},
    {divider:true},
    {key:"l_report",    icon:"📊", label:"რეპორტი"},
    {divider:true},
    {key:"სხვა",        icon:"👤", label:"პროფილი"},
    {key:"help",        icon:"💬", label:"Help"},
  ];
  const laundryBottomTabs=[
    {key:"მთავარი",    icon:"🏠", label:"მთავარი"},
    {key:"შეკვეთები",  icon:"📋", label:"შეკვეთები"},
    {key:"პარტნიორები",icon:"🤝", label:"პარტნ."},
    {key:"სხვა",       icon:"👤", label:"პროფილი"},
    {key:"help",       icon:"💬", label:"Help"},
  ];

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",background:darkMode?"#0F172A":"#F8FAFC"}}>
      <TrialBanner user={user}/>
      {!requestOSL && false}
      <Header color={color} grad={grad} icon="🧺" title="სამრეცხაოს პანელი"
        onBell={()=>setShowLaundryNotifs(v=>!v)}
        bellBadge={myPriceNotifs.length+incomingReqs.length}
        sub={
          <p style={{margin:0,color:"rgba(255,255,255,0.9)",fontWeight:800,fontSize:17}}>{user.name}</p>
        }
      />
      {showLaundryNotifs && (
        <div style={{position:"absolute",top:70,right:12,zIndex:300,
          background:"#fff",borderRadius:14,boxShadow:"0 4px 24px rgba(0,0,0,0.15)",
          width:280,maxHeight:320,overflowY:"auto",padding:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <p style={{margin:0,fontWeight:700,fontSize:13}}>შეტყობინებები</p>
            <button onClick={()=>{setPriceNotifs(p=>p.map(n=>n.forLaundry===user.code?{...n,read:true}:n));}}
              style={{background:"none",border:"none",color:"#9CA3AF",fontSize:11,cursor:"pointer"}}>ყველა წაშლა</button>
          </div>
          {myPriceNotifs.length===0&&<p style={{color:"#9CA3AF",fontSize:12,textAlign:"center",margin:"12px 0"}}>შეტყობინება არ არის</p>}
          {myPriceNotifs.map(n=>(
            <div key={n.id} style={{...S.card,marginBottom:6,padding:"8px 10px",background:"#FEF3C7"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <p style={{margin:0,fontSize:12,fontWeight:600,color:"#92400E",flex:1}}>{n.msg}</p>
                <button onClick={()=>setPriceNotifs(p=>p.map(x=>x.id===n.id?{...x,read:true}:x))}
                  style={{background:"none",border:"none",color:"#92400E",cursor:"pointer",fontSize:13,padding:0,marginLeft:6}}>✕</button>
              </div>
              <p style={{margin:"3px 0 0",fontSize:10,color:"#92400E",opacity:0.7}}>{n.time}</p>
            </div>
          ))}
        </div>
      )}

      <div style={{flex:1,display:"flex",flexDirection:"row",minHeight:0,overflow:"hidden"}}>
      <NavBar tabs={tabs} bottomTabs={laundryBottomTabs} active={tab} onChange={setTab} color={color} badges={{"პარტნიორები": incomingReqs.length}} pinned={pinnedCardsL} onTogglePin={togglePinL}/>
      <div style={{flex:1,overflow:tab==="მთავარი"?"hidden":"auto",position:"relative",display:"flex",flexDirection:"column",minHeight:0}} onClick={()=>showLaundryNotifs&&setShowLaundryNotifs(false)}>
        {tab==="მთავარი" && (()=>{
          const LCARDS = [
            { icon:"📋", label:"შეკვეთები",        sub:`${newOrds.length} ახალი · ${inProg.length} მიმდინ.`,dest:"შეკვეთები",     bg:"#EBF2FF", c:"#1B6EF3" },
            { icon:"🏨", label:"პარტნიორები",   sub:`${myHotels.length} სასტ.`,                      dest:"პარტნიორები",  bg:"#FEF3C7", c:"#F59E0B" },
            { icon:"🏢", label:"ბიზნეს კატალოგი",  sub:`${hotels.length + HOTEL_DIRECTORY.length} ბიზნესი`, dest:"l_hotels",     bg:"#FFF1F2", c:"#E11D48" },
                    { icon:"📝", label:"Note",              sub:`${laundryNotes.filter(n=>!n.done).length} ტასკი`,dest:"l_note",       bg:"#FFFBEB", c:"#B45309" },
            { icon:"🚗", label:"პარტნიორი მძღოლ.", sub:`${myDrvs.length} მძღოლი`,                        dest:"l_p_drivers",  bg:"#F0EBFF", c:"#7C3AED" },
            { icon:"👥", label:"მძღოლების სია",    sub:`${drivers.filter(d=>d.laundry_code!==user.code&&!acceptedDriverIds.includes(d.id)).length} სულ`,                           dest:"l_all_drivers",bg:"#F0F9FF", c:"#0369A1" },
            { icon:"📊", label:"რეპორტი",           sub:`${mine.length} შეკვ.`,                           dest:"l_report",     bg:"#E2E8F0", c:"#334155" },
            { icon:"🧾", label:"ინვოისები",          sub:`${invoices.filter(i=>i.laundry_code===user.code).length} ინვ.`, dest:"ინვოისები", bg:"#F0EBFF", c:"#7C3AED" },
          ];
          return (
          <div style={{padding:lPad,flex:1,boxSizing:"border-box",display:"flex",flexDirection:"column",overflow:"hidden",minHeight:0}}
            onMouseUp={()=>{
              if(lDragging!==null&&lOver!==null&&lDragging!==lOver){
                setLaundryCardOrder(prev=>{const n=[...prev];const f=n.indexOf(lDragging);const t=n.indexOf(lOver);n.splice(f,1);n.splice(t,0,lDragging);return n;});
              }
              setLDragging(null);setLOver(null);
            }}
            onTouchEnd={()=>{
              if(lDragging!==null&&lOver!==null&&lDragging!==lOver){
                setLaundryCardOrder(prev=>{const n=[...prev];const f=n.indexOf(lDragging);const t=n.indexOf(lOver);n.splice(f,1);n.splice(t,0,lDragging);return n;});
              }
              setLDragging(null);setLOver(null);
            }}
            onTouchMove={e=>{
              if(lDragging===null) return;
              e.preventDefault();
              const t=e.touches[0];
              const el=document.elementFromPoint(t.clientX,t.clientY);
              const card=el?.closest("[data-lcardidx]");
              if(card){const i=parseInt(card.getAttribute("data-lcardidx"));if(i!==lDragging)setLOver(i);}
            }}>
            <p style={{margin:"0 0 8px",fontSize:10,color:"#9CA3AF",flexShrink:0}}>✥ გადაათრიე გადასაწყობად</p>
            <div style={{flex:1,display:"grid",gridTemplateColumns:`repeat(${lCols},1fr)`,gridTemplateRows:`repeat(${lRows},1fr)`,gap:lGap,minHeight:0}}>
              {(()=>{
                const pinnedWithCards=pinnedCardsL.filter(key=>LCARDS.some(c=>c.dest===key));
                return pinnedWithCards.length>0
                  ? laundryCardOrder.filter(i=>pinnedWithCards.includes(LCARDS[i]?.dest))
                  : laundryCardOrder;
              })().map(idx=>{
                const card=LCARDS[idx];
                if(!card) return null;
                const isDragging=lDragging===idx, isOver=lOver===idx;
                return (
                  <div key={card.label}
                    data-lcardidx={idx}
                    onTouchStart={()=>setLDragging(idx)}
                    onMouseDown={()=>setLDragging(idx)}
                    onClick={()=>{if(!isDragging)setTab(card.dest);}}
                    onMouseEnter={e=>{
                      if(lDragging===null){
                        e.currentTarget.style.transform="translateY(-5px)";
                        e.currentTarget.style.boxShadow="0 8px 20px rgba(0,0,0,0.10)";
                        e.currentTarget.style.outline="none";
                      }
                    }}
                    onMouseLeave={e=>{
                      e.currentTarget.style.transform="translateY(0)";
                      e.currentTarget.style.boxShadow="none";
                      e.currentTarget.style.outline="none";
                    }}
                    style={{background:card.bg,borderRadius:16,padding:"18px 14px",
                      cursor:isDragging?"grabbing":"grab",
                      display:"flex",flexDirection:"column",alignItems:"center",
                      justifyContent:"center",gap:8,textAlign:"center",minHeight:110,
                      opacity:isDragging?0.45:1,
                      outline:isOver?`2.5px dashed ${card.c}`:"none",
                      transform:isOver&&!isDragging?"translateY(-5px)":"translateY(0)",
                      transition:"opacity 0.15s,outline 0.15s,transform 0.2s ease,box-shadow 0.2s ease",
                      userSelect:"none",WebkitUserSelect:"none",touchAction:"none"}}>
                    <span style={{fontSize:28}}>{card.icon}</span>
                    <div>
                      <p style={{margin:0,fontWeight:800,fontSize:13,color:card.c}}>{card.label}</p>
                      <p style={{margin:"3px 0 0",fontSize:11,color:card.c,opacity:0.75}}>{card.sub}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          );
        })()}

        {tab==="პარტნიორები" && (
          <div style={{padding:"14px 14px 14px"}}>
            <p style={{margin:"0 0 12px",fontWeight:800,fontSize:15}}>🤝 პარტნიორები</p>
            {myHotels.length===0&&<p style={{color:"#9CA3AF",textAlign:"center",marginTop:40}}>სასტუმრო არ არის</p>}
            {[...new Set(myHotels.filter(h=>h.city).map(h=>h.city))].sort().map(city=>{
              const items=myHotels.filter(h=>h.city===city);
              const isC=lCollapsedCitiesPartner.includes(city);
              return (
                <div key={city} style={{marginBottom:10}}>
                  <div onClick={()=>setLCollapsedCitiesPartner(p=>p.includes(city)?p.filter(x=>x!==city):[...p,city])}
                    style={{display:"flex",alignItems:"center",gap:8,background:"#FEF3C7",
                      borderRadius:10,padding:"9px 12px",cursor:"pointer",marginBottom:isC?0:8}}>
                    <span style={{fontSize:13}}>📍</span>
                    <span style={{fontWeight:700,fontSize:13,color:"#F59E0B",flex:1}}>{city}</span>
                    <span style={{fontSize:11,color:"#9CA3AF"}}>{items.length} სასტ.</span>
                    <span style={{fontSize:16,fontWeight:700,color:"#F59E0B"}}>{isC?"＋":"－"}</span>
                  </div>
                  {!isC && items.map(h=>{
                    const plKey=`${user.code}_${h.code}`;
                    const pl=priceLists[plKey];
                    return (
                    <div key={h.id} style={{...S.card}}>
                      <div style={{display:"flex",alignItems:"center",gap:12}}>
                        <Ava name={h.name} size={42}/>
                        <div style={{flex:1}}>
                          <p style={{margin:0,fontWeight:700,fontSize:14}}>{h.name}</p>
                          <p style={{margin:"2px 0 0",fontSize:12,color:"#6B7280"}}>{h.code}</p>
                          {h.city&&<p style={{margin:"2px 0 0",fontSize:12,color:"#6B7280"}}>🏙️ {h.city}</p>}
                          {h.address&&<p style={{margin:"2px 0 0",fontSize:12,color:"#9CA3AF"}}>📍 {h.address}</p>}
                          {h.rooms_count&&<p style={{margin:"2px 0 0",fontSize:12,color:"#6B7280"}}>🛏️ {h.rooms_count} ნომერი</p>}
                          {h.phone&&<p style={{margin:"2px 0 0",fontSize:12,color:"#374151"}}>📞 {h.phone}</p>}
                          {pl&&<p style={{margin:"2px 0 0",fontSize:11,color:"#12B76A"}}>✓ ფასები: {pl.items.length} item · {pl.updatedAt}</p>}
                          {!pl&&<p style={{margin:"2px 0 0",fontSize:11,color:"#F59E0B"}}>⚠️ ფასები არ არის დაყენებული</p>}
                        </div>
                        <button onClick={()=>setPriceModal(h)}
                          style={{background:"#12B76A",color:"#fff",border:"none",
                            borderRadius:8,padding:"7px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                          💰 ფასები
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
        {priceModal && (
          <PriceListManager
            laundryCode={user.code}
            hotelCode={priceModal.code}
            hotelName={priceModal.name}
            priceLists={priceLists}
            setPriceLists={setPriceLists}
            onClose={()=>setPriceModal(null)}
            onNotify={handlePriceNotify}
          />
        )}

        {tab==="l_hotels" && (
          <div style={{padding:"14px"}}>
            {inviteTarget && <InviteModal laundry={user} hotel={inviteTarget} onClose={()=>setInviteTarget(null)}/>}
            {/* Section tabs */}
            <div style={{display:"flex",gap:8,marginBottom:14}}>
              {[{k:"partners",l:`✅ ჩემი პარტნიორები`},
                {k:"other",l:`🏢 სხვა სასტუმრო`}].map(s=>(
                <button key={s.k} onClick={()=>setHotelSection(s.k)} style={{
                  flex:1,padding:"9px 6px",borderRadius:10,border:"none",cursor:"pointer",
                  background:hotelSection===s.k?color:"#E2E8F0",
                  color:hotelSection===s.k?"#fff":"#475569",fontWeight:700,fontSize:11}}>
                  {s.l}
                </button>
              ))}
            </div>
            {hotelSection==="partners" && hotels.filter(h=>
              partnerRequests.some(r=>r.status==="accepted"&&
                ((r.from_code===user.code&&r.to_code===h.code)||(r.to_code===user.code&&r.from_code===h.code)))
            ).map(h=>(
              <div key={h.id} style={{...S.card,display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                <Ava name={h.name} size={38}/>
                <div style={{flex:1}}>
                  <p style={{margin:0,fontWeight:700,fontSize:13}}>{h.name}</p>
                  <p style={{margin:"1px 0 0",fontSize:11,color:"#6B7280"}}>{h.code}</p>
                  {h.city&&<p style={{margin:"1px 0 0",fontSize:11,color:"#6B7280"}}>🏙️ {h.city}</p>}
                  {h.address&&<p style={{margin:"1px 0 0",fontSize:11,color:"#9CA3AF"}}>📍 {h.address}</p>}
                  {h.rooms_count&&<p style={{margin:"1px 0 0",fontSize:11,color:"#6B7280"}}>🛏️ {h.rooms_count} ნომერი</p>}
                  {h.phone&&<p style={{margin:"1px 0 0",fontSize:11,color:"#374151"}}>📞 {h.phone}</p>}
                </div>
                <span style={{background:"#D1FAE5",color:"#065F46",
                  padding:"4px 10px",borderRadius:999,fontSize:11,fontWeight:600}}>✅ პარტნ.</span>
              </div>
            ))}
            {hotelSection==="other" && (()=>{
              const myPartnerCodes = partnerRequests
                .filter(r=>r.status==="accepted"&&(r.from_code===user.code||r.to_code===user.code))
                .map(r=>r.from_code===user.code?r.to_code:r.from_code);
              const cpgOther = hotels.filter(h=>!myPartnerCodes.includes(h.code));
              const dirOther = HOTEL_DIRECTORY.filter(d=>
                !hotels.some(h=>h.name===d.name&&h.city===d.city)
              );
              const combinedAll = [
                ...cpgOther.map(h=>({...h,_type:"cpg"})),
                ...dirOther.map(d=>({...d,code:null,_type:"dir",client_type:"hotel"}))
              ];
              const combined = hotelTypeFilter==="all" ? combinedAll : combinedAll.filter(h=>(h.client_type||"hotel")===hotelTypeFilter);
              const cities=[...new Set(combined.map(h=>h.city).filter(Boolean))].sort();
              const q = hotelSearch.trim().toLowerCase();
              const searching = q !== "";
              const searchResults = searching ? combined.filter(h=>
                [h.name,h.city,h.address,h.phone,h.code].some(f=>f && String(f).toLowerCase().includes(q))
              ) : [];

              const renderHotelRow = h => (
                <div key={h.id} style={{...S.card,display:"flex",alignItems:"center",
                  gap:10,marginBottom:6}}>
                  <Ava name={h.name} size={38}/>
                  <div style={{flex:1}}>
                    <p style={{margin:0,fontWeight:700,fontSize:13}}>{h.name}</p>
                    <p style={{margin:"1px 0 0",fontSize:11,color:"#6B7280"}}>
                      {h.city&&<span style={{marginRight:4}}>🏙️ {h.city}</span>}
                      {h.address&&<span>{h.address}</span>}
                      {h.rooms_count&&<span style={{marginLeft:4,color:"#6B7280"}}>🛏️ {h.rooms_count} ნომ.</span>}
                      {h.phone&&<span style={{marginLeft:4}}>📞 {h.phone}</span>}
                      {h._type==="cpg"&&<span style={{marginLeft:6,background:"#D1FAE5",
                        color:"#065F46",borderRadius:999,fontSize:9,
                        fontWeight:700,padding:"1px 6px"}}>CPG</span>}
                    </p>
                  </div>
                  <button onClick={()=>setInviteTarget(h)}
                    style={{background:"#F0EBFF",color:"#7C3AED",border:"none",
                      borderRadius:8,padding:"6px 10px",fontSize:11,
                      fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                    📩 მოწვევა
                  </button>
                </div>
              );

              return (<>
                <div style={{display:"flex",gap:8,marginBottom:12}}>
                  {[{k:"all",l:"ყველა"},{k:"hotel",l:"🏨 სასტუმრო"},{k:"clinic",l:"🏥 კლინიკა"}].map(t=>(
                    <button key={t.k} onClick={()=>setHotelTypeFilter(t.k)} style={{
                      flex:1,padding:"8px 6px",borderRadius:10,border:"none",cursor:"pointer",
                      background:hotelTypeFilter===t.k?color:"#E2E8F0",
                      color:hotelTypeFilter===t.k?"#fff":"#475569",fontWeight:700,fontSize:11}}>
                      {t.l}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={hotelSearch}
                  onChange={e=>{ setHotelSearch(e.target.value); setDirCityFilter(""); }}
                  placeholder="🔍 ძებნა სახელით, ქალაქით, მისამართით, ტელეფონით, კოდით..."
                  style={{...S.input,marginBottom:12,width:"100%",boxSizing:"border-box"}}
                />
                {searching ? (
                  searchResults.length===0
                    ? <p style={{color:"#9CA3AF",textAlign:"center",marginTop:30}}>შედეგი არ მოიძებნა</p>
                    : searchResults.map(renderHotelRow)
                ) : (<>
                  <div style={{
                    display:"grid",
                    gridTemplateColumns:"repeat(auto-fill, minmax(120px, 1fr))",
                    gap:8, marginBottom:12
                  }}>
                  {cities.map(city=>{
                    const cityHotels = combined.filter(h=>h.city===city);
                    return (
                      <div key={city}>
                        <div onClick={()=>setDirCityFilter(dirCityFilter===city?"":city)}
                          style={{display:"flex",flexDirection:"column",alignItems:"center",
                            justifyContent:"center",textAlign:"center",
                            background:dirCityFilter===city?"#E11D48":"#fff",
                            borderRadius:12,padding:"14px 10px",cursor:"pointer",
                            boxShadow:"0 2px 8px rgba(225,29,72,0.12)",
                            border:`1.5px solid ${dirCityFilter===city?"#E11D48":"#FFE0E6"}`,
                            transition:"all 0.15s",height:72,boxSizing:"border-box"}}>
                          <p style={{margin:0,fontWeight:700,fontSize:13,
                            color:dirCityFilter===city?"#fff":"#E11D48",
                            lineHeight:1.2}}>{city}</p>
                          <p style={{margin:"4px 0 0",fontSize:12,fontWeight:600,
                            color:dirCityFilter===city?"rgba(255,255,255,0.9)":"#9CA3AF"}}>
                            {cityHotels.length}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  </div>

                  {/* Hotels for selected city */}
                  {dirCityFilter && combined.filter(h=>h.city===dirCityFilter).map(renderHotelRow)}
                </>)}
              </>);
            })()}
          </div>
        )}

        {tab==="l_laundries" && (
          <div style={{padding:"14px 14px 14px"}}>
            <p style={{margin:"0 0 12px",fontWeight:800,fontSize:15}}>🧺 სამრეცხაოების სია</p>
            {[...new Set(laundries.filter(l=>l.city).map(l=>l.city))].sort().map(city=>{
              const items=laundries.filter(l=>l.city===city);
              const isC=lCollapsedCitiesLaundries.includes(city);
              return (
                <div key={city} style={{marginBottom:10}}>
                  <div onClick={()=>setLCollapsedCitiesLaundries(p=>p.includes(city)?p.filter(x=>x!==city):[...p,city])}
                    style={{display:"flex",alignItems:"center",gap:8,background:"#E6F9F1",
                      borderRadius:10,padding:"9px 12px",cursor:"pointer",marginBottom:isC?0:8}}>
                    <span style={{fontSize:13}}>📍</span>
                    <span style={{fontWeight:700,fontSize:13,color:"#12B76A",flex:1}}>{city}</span>
                    <span style={{fontSize:11,color:"#9CA3AF"}}>{items.length} სამრ.</span>
                    <span style={{fontSize:16,fontWeight:700,color:"#12B76A"}}>{isC?"＋":"－"}</span>
                  </div>
                  {!isC && items.map(l=>{
                    const lO=orders.filter(o=>o.laundry_id===l.id);
                    const comp=lO.filter(o=>o.status==="COMPLETED").length;
                    const stars=Math.max(1,Math.round(1+(lO.length>0?comp/lO.length:0)*4));
                    return (
                      <div key={l.id} style={{...S.card,display:"flex",alignItems:"flex-start",gap:12}}>
                        <Ava name={l.name} size={42}/>
                        <div style={{flex:1}}>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <p style={{margin:0,fontWeight:700,fontSize:14}}>{l.name}</p>
                            {l.isDemo&&<span style={{background:"#FEF3C7",color:"#92400E",
                              fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:999}}>DEMO</span>}
                          </div>
                          <p style={{margin:"2px 0 0",fontSize:12,color:"#6B7280"}}>{l.code}</p>
                          {l.phone&&<p style={{margin:"2px 0 0",fontSize:12,color:"#374151"}}>📞 {l.phone}</p>}
                          <div style={{display:"flex",alignItems:"center",gap:6,marginTop:4}}>
                            <span style={{fontSize:13,color:"#F59E0B"}}>{"★".repeat(stars)}{"☆".repeat(5-stars)}</span>
                            <span style={{fontSize:10,color:"#9CA3AF"}}>{comp}/{lO.length}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {tab==="l_note" && (
          <NoteTab notes={laundryNotes} setNotes={setLaundryNotes} color={color}/>
        )}

        {tab==="l_p_drivers" && (
          <div style={{padding:"14px 14px 14px"}}>
            <div onClick={()=>setLCollapsedPDrivers(v=>!v)}
              style={{display:"flex",alignItems:"center",gap:8,background:"#F0EBFF",
                borderRadius:10,padding:"9px 12px",cursor:"pointer",marginBottom:lCollapsedPDrivers?0:12}}>
              <span style={{fontSize:14}}>🚗</span>
              <span style={{fontWeight:700,fontSize:14,color:"#7C3AED",flex:1}}>პარტნიორი მძღოლები</span>
              <span style={{fontSize:11,color:"#9CA3AF"}}>{myDrvs.length} მძღოლი</span>
              <span style={{fontSize:16,fontWeight:700,color:"#7C3AED",marginLeft:4}}>{lCollapsedPDrivers?"＋":"－"}</span>
            </div>
            {!lCollapsedPDrivers && (
              <>
                {myDrvs.length===0&&<p style={{color:"#9CA3AF",textAlign:"center",marginTop:20}}>მძღოლი არ არის</p>}
                {myDrvs.map(d=>(
                  <div key={d.id} style={{...S.card,display:"flex",alignItems:"center",gap:12}}>
                    <Ava name={d.name} size={42}/>
                    <div style={{flex:1}}>
                      <p style={{margin:0,fontWeight:700,fontSize:14}}>{d.name}</p>
                      <p style={{margin:"2px 0 0",fontSize:12,color:"#6B7280"}}>{d.code}</p>
                      {d.phone&&<p style={{margin:"2px 0 0",fontSize:12,color:"#374151"}}>📞 {d.phone}</p>}
                    </div>
                    <span style={{background:d.available?"#D1FAE5":"#FEE2E2",
                      color:d.available?"#065F46":"#991B1B",
                      padding:"4px 10px",borderRadius:999,fontSize:11,fontWeight:600}}>
                      {d.available?"ხელმისაწვდომი":"დაკავებული"}
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {tab==="l_all_drivers" && (
          <div style={{padding:"14px 14px 14px"}}>
            <div onClick={()=>setLCollapsedAllDrivers(v=>!v)}
              style={{display:"flex",alignItems:"center",gap:8,background:"#EBF2FF",
                borderRadius:10,padding:"9px 12px",cursor:"pointer",marginBottom:lCollapsedAllDrivers?0:12}}>
              <span style={{fontSize:14}}>👥</span>
              <span style={{fontWeight:700,fontSize:14,color:"#1B6EF3",flex:1}}>მძღოლების სია</span>
              <span style={{fontSize:11,color:"#9CA3AF"}}>{drivers.length} სულ</span>
              <span style={{fontSize:16,fontWeight:700,color:"#1B6EF3",marginLeft:4}}>{lCollapsedAllDrivers?"＋":"－"}</span>
            </div>
            {!lCollapsedAllDrivers && (
              <>
                {drivers.filter(d=>d.laundry_code!==user.code&&!acceptedDriverIds.includes(d.id)).length===0&&<p style={{color:"#9CA3AF",textAlign:"center",marginTop:20}}>მძღოლი არ არის</p>}
                {drivers.filter(d=>d.laundry_code!==user.code&&!acceptedDriverIds.includes(d.id)).map(d=>{
                  const isMyDriver = d.laundry_code===user.code;
                  const isPendingDriver = driverRequests.some(r=>r.driver_id===d.id&&r.laundry_code===user.code&&r.status==="pending");
                  return (
                  <div key={d.id} style={{...S.card,display:"flex",alignItems:"center",gap:12}}>
                    <Ava name={d.name} size={42}/>
                    <div style={{flex:1}}>
                      <p style={{margin:0,fontWeight:700,fontSize:14}}>{d.name}</p>
                      <p style={{margin:"2px 0 0",fontSize:12,color:"#6B7280"}}>{d.code}</p>
                      {d.phone&&<p style={{margin:"2px 0 0",fontSize:12,color:"#374151"}}>📞 {d.phone}</p>}
                    </div>
                    {isMyDriver ? (
                      <span style={{background:"#D1FAE5",color:"#065F46",
                        padding:"4px 10px",borderRadius:999,fontSize:11,fontWeight:600}}>ჩემი</span>
                    ) : isPendingDriver ? (
                      <span style={{background:"#FEF3C7",color:"#92400E",
                        padding:"4px 10px",borderRadius:999,fontSize:11,fontWeight:600}}>⏳</span>
                    ) : (
                      <button onClick={()=>onAssignDriver&&onAssignDriver(d.id)}
                        style={{background:color,color:"#fff",border:"none",borderRadius:8,
                          padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                        + მოთხოვნა
                      </button>
                    )}
                  </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {tab==="l_report" && (
          <ReportsPage orders={orders} invoices={invoices} userId={user.id} userCode={user.code} userRole="laundry" color={color}/>
        )}

        {tab==="შეკვეთები" && (()=>{
          const L_FILTERS = [
            {key:"ყველა",    statuses:null,                                                             bg:"#E2E8F0",c:"#334155"},
            {key:"ახალი",    statuses:["PENDING_LAUNDRY_APPROVAL"],                                    bg:"#FEF3C7",c:"#92400E"},
            {key:"მოსატანი",    statuses:["APPROVED","DRIVER_ASSIGNED","DRIVER_ON_THE_WAY","PICKED_UP"],  bg:"#E0F2FE",c:"#0EA5E9"},
            {key:"სამრეცხაოშია", statuses:["RECEIVED_IN_LAUNDRY"],                                        bg:"#E6F9F1",c:"#12B76A"},
            {key:"მზადაა",       statuses:["READY_FOR_DELIVERY","DELIVERY_DRIVER_ASSIGNED","DELIVERY_ON_THE_WAY"], bg:"#F0EBFF",c:"#7C3AED"},
            {key:"ნაწილობრივი", statuses:["PARTIALLY_DELIVERED"],                                      bg:"#FEF3C7",c:"#92400E"},
            {key:"დასრულდა", statuses:["COMPLETED"],                                                   bg:"#F3F4F6",c:"#374151"},
          ];
          const activeF = lOrderFilter ? L_FILTERS.find(f=>f.key===lOrderFilter) : L_FILTERS[0];
          const displayed = (!activeF||!activeF.statuses) ? mine : mine.filter(o=>activeF.statuses.includes(o.status));
          const hotelGroups = [...new Set(displayed.map(o=>o.hotel_name))].sort();
          const OrderCard = ({o}) => (
            <div onClick={()=>setDetail(o)} style={{...S.card,cursor:"pointer"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <p style={{margin:0,fontWeight:700,fontSize:13}}>#{o.order_number}</p>
                  <p style={{margin:"2px 0 1px",fontSize:12,color:"#6B7280"}}>
                    {new Date(o.pickup_date).toLocaleDateString("ka-GE")}
                  </p>
                    <p style={{margin:"2px 0 0",fontSize:11,color:"#9CA3AF"}}>
                      {[o.sheets_count>0&&`🛏 ${o.sheets_count}ც.`,o.towels_kg>0&&`🧺 ${o.towels_kg}კგ`,o.covers_kg>0&&`🛌 ${o.covers_kg}კგ`,...(o.custom_items||[]).map(ci=>`${ci.name} ${ci.qty}${ci.unit}`)].filter(Boolean).join(' · ')}
                    </p>
                </div>
                <Badge status={o.status}/>
              </div>
            </div>
          );
          return (
          <div style={{padding:"14px 14px 14px"}}>
            {/* Filters */}
            <div style={{display:"flex",gap:6,marginBottom:14}}>
              {L_FILTERS.map(f=>{
                const cnt=f.statuses?mine.filter(o=>f.statuses.includes(o.status)).length:mine.length;
                const active=(lOrderFilter===f.key)||(!lOrderFilter&&f.key==="ყველა");
                return (
                  <div key={f.key} onClick={()=>setLOrderFilter(f.key==="ყველა"?null:f.key)}
                    style={{flex:1,background:active?f.c:f.bg,borderRadius:10,
                      padding:"7px 0",textAlign:"center",cursor:"pointer",
                      transition:"background 0.15s, transform 0.2s ease, box-shadow 0.2s ease"}}
                    onMouseEnter={e=>{if(!active){e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,0.08)";}}}
                    onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="none";}}>
                    <p style={{margin:0,fontWeight:800,fontSize:15,color:active?"#fff":f.c}}>{cnt}</p>
                    <p style={{margin:"1px 0 0",fontSize:9,fontWeight:700,color:active?"#fff":f.c}}>{f.key}</p>
                  </div>
                );
              })}
            </div>
            <p style={{margin:"0 0 10px",fontWeight:800,fontSize:13}}>
              შეკვეთები <span style={{color:"#9CA3AF",fontWeight:400}}>({displayed.length})</span>
            </p>
            {/* Grouped by hotel */}
            {hotelGroups.map(hname=>{
              const hOrders = displayed.filter(o=>o.hotel_name===hname);
              const isCollapsed = lCollapsedHotels.includes(hname);
              return (
                <div key={hname} style={{marginBottom:10}}>
                  <div onClick={()=>setLCollapsedHotels(p=>p.includes(hname)?p.filter(x=>x!==hname):[...p,hname])}
                    style={{display:"flex",alignItems:"center",gap:8,background:THEME.laundry.light,
                      borderRadius:10,padding:"8px 12px",cursor:"pointer",marginBottom:isCollapsed?0:8}}>
                    <span style={{fontSize:12}}>🏨</span>
                    <span style={{fontWeight:700,fontSize:13,color,flex:1}}>{hname}</span>
                    <span style={{fontSize:11,color:"#9CA3AF"}}>{hOrders.length} შეკვ.</span>
                    <span style={{fontSize:16,fontWeight:700,color,marginLeft:4}}>{isCollapsed?"＋":"－"}</span>
                  </div>
                  {!isCollapsed && hOrders.map(o=><OrderCard key={o.id} o={o}/>)}
                </div>
              );
            })}
            {displayed.length===0&&<p style={{color:"#9CA3AF",textAlign:"center",marginTop:30}}>შეკვეთა არ არის</p>}
          </div>
          );
        })()}



        {tab==="ინვოისები" && <InvoicesPage invoices={invoices} userId={user.id} userCode={user.code} userRole="laundry" user={user} setNotifs={setLaundryNotifs} setPriceNotifs={setPriceNotifs} partnerRequests={partnerRequests} hotels={hotels}/>}
        {tab==="პარტნიორები" && (
          <>
            {priceNotifs&&priceNotifs.filter(n=>n.hotelCode===user.code&&!n.read&&!n.forLaundry).map(n=>(
              <div key={n.id} style={{margin:"10px 14px 0",background:"#FEF3C7",borderRadius:12,
                padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:16}}>🔔</span>
                <div style={{flex:1}}>
                  <p style={{margin:0,fontSize:12,fontWeight:600,color:"#92400E"}}>{n.msg}</p>
                  <p style={{margin:"2px 0 0",fontSize:10,color:"#92400E",opacity:0.7}}>{n.time}</p>
                </div>
                <button onClick={()=>setPriceNotifs(p=>p.map(x=>x.id===n.id?{...x,read:true}:x))}
                  style={{background:"none",border:"none",color:"#92400E",cursor:"pointer",fontSize:14}}>✕</button>
              </div>
            ))}
            <PartnersPage user={user} hotels={hotels} laundries={laundries}
              partnerRequests={partnerRequests}
              onSendRequest={onSendPartnerRequest}
              onPartnerResponse={onPartnerResponse}
              priceLists={priceLists}/>
          </>
        )}
        {tab==="რეპორტი" && <ReportsPage orders={orders} invoices={invoices} userId={user.id} userCode={user.code} userRole="laundry" color={color}/>}
        {tab==="სხვა" && (
          <LaundryProfile user={user} color={color} myHotels={myHotels} />
        )}
        {tab==="help" && <HelpChat user={user}/>}
      </div>

      </div>

      {detail && (
        <OrderDetailModal order={detail} onClose={()=>setDetail(null)}
          userRole="laundry" color={color}
          onAction={(id,s,e)=>{
            onStatusChange(id,s,e);
          }}
          drivers={myDrvs}
          priceLists={priceLists}
          onEditPriceList={()=>setPriceModal({code:detail.hotel_code,name:detail.hotel_name})}/>
      )}

    </div>
  );
}

function DriverApp({ user, orders, hotels, laundries, onStatusChange, driverRequests=[], onDriverRequestResponse }) {
  const { dark: darkMode } = useTheme();
  const [tab,setTab]=useState("მთავარი");
  const [detail,setDetail]=useState(null);
  const [filter,setFilter]=useState(null);
  const [driverNotes,setDriverNotes]=useLS(`dnotes_${user.code}`,[]);
  const [collapsedLaundries,setCollapsedLaundries]=useState([]);
  const [collapsedHotels,setCollapsedHotels]=useState([]);
  const [pinnedCardsD,setPinnedCardsD]=useLS(`pinned_${user.code}`,[]);
  const togglePinD=key=>setPinnedCardsD(p=>p.includes(key)?p.filter(k=>k!==key):[...p,key]);
  const dLongPressTimer=useRef(null);
  const [dLongPressHint,setDLongPressHint]=useState(null);
  const dStartLongPress=(idx,dest)=>{dLongPressTimer.current=setTimeout(()=>{togglePinD(dest);setDLongPressHint(idx);setTimeout(()=>setDLongPressHint(null),1200);},600);};
  const dCancelLongPress=()=>{if(dLongPressTimer.current)clearTimeout(dLongPressTimer.current);};
  const {request:requestOSD, send:sendOSD} = useOSNotifications();
  useEffect(()=>{ requestOSD(); },[]);
  const [driverCardOrder,setDriverCardOrder]=useLS(`driverCardOrder_${user.code}`,[0,1,2,3,4,5,6]);
  useEffect(()=>{
    const allIdx=[0,1,2,3,4,5,6];
    const deduped=[...new Set(driverCardOrder)];
    const missing=allIdx.filter(i=>!deduped.includes(i));
    const final=[...deduped,...missing];
    if(JSON.stringify(final)!==JSON.stringify(driverCardOrder)) setDriverCardOrder(final);
  },[]);
  const [driverDragging,setDriverDragging]=useState(null);
  const [driverOver,setDriverOver]=useState(null);
  const [showDriverNotifs,setShowDriverNotifs]=useState(false);
  const myDriverReqs = driverRequests.filter(r=>(r.driver_id===user.id||r.driver_id===user.code)&&r.status==="pending");
  const cancelledOrders = driverRequests.filter(r=>(r.driver_id===user.id||r.driver_id===user.code)&&r.status==="cancelled_order"&&!r.read);
  const prevDriverReqCount = useRef(myDriverReqs.length);
  const audioCtxRef = useRef(null);

  // Unlock AudioContext on first user interaction
  useEffect(()=>{
    const unlock = () => {
      if(!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if(audioCtxRef.current.state === "suspended") {
        audioCtxRef.current.resume();
      }
      document.removeEventListener("touchstart", unlock);
      document.removeEventListener("click", unlock);
    };
    document.addEventListener("touchstart", unlock, {passive:true});
    document.addEventListener("click", unlock);
    return()=>{
      document.removeEventListener("touchstart", unlock);
      document.removeEventListener("click", unlock);
    };
  },[]);

  const playNotifSound = (type="order") => {
    try {
      if(!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if(ctx.state === "suspended") ctx.resume();
      const beep = (f,s,d) => {
        const o=ctx.createOscillator(), g=ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value=f; o.type="sine";
        g.gain.setValueAtTime(0, ctx.currentTime+s);
        g.gain.linearRampToValueAtTime(0.3, ctx.currentTime+s+0.04);
        g.gain.linearRampToValueAtTime(0, ctx.currentTime+s+d);
        o.start(ctx.currentTime+s); o.stop(ctx.currentTime+s+d+0.05);
      };
      if(type==="order")  { beep(660,0,0.12); beep(880,0.15,0.12); beep(1100,0.3,0.2); }
      if(type==="driver") { beep(440,0,0.1); beep(660,0.15,0.1); beep(880,0.3,0.25); }
    } catch(e) {}
  };

  const bp = useBreakpoint();
  const { primary:color, grad } = THEME.driver;

  const mine    = orders.filter(o=>o.driver_id===user.id);
  const today   = mine.filter(o=>!["COMPLETED","DELIVERED","REJECTED"].includes(o.status));
  const done    = mine.filter(o=>["COMPLETED","DELIVERED"].includes(o.status));
  const transit = mine.filter(o=>["DRIVER_ON_THE_WAY","DELIVERY_ON_THE_WAY"].includes(o.status));

  // Track new task assignments
  const prevMineCount = useRef(mine.length);
  useEffect(()=>{
    if(mine.length > prevMineCount.current) {
      playNotifSound("driver");
      sendOSD("🚗 CPG — ახალი დავალება", "ახალი სამუშაო დავალება გაქვს. შეამოწმე!");
    }
    prevMineCount.current = mine.length;
  },[mine.length]);

  // Track partner requests
  useEffect(()=>{
    const cur = driverRequests.filter(r=>(r.driver_id===user.id||r.driver_id===user.code)&&r.status==="pending").length;
    if(cur > prevDriverReqCount.current) {
      playNotifSound("order");
    }
    prevDriverReqCount.current = cur;
  },[driverRequests]);
  const acceptedReq = driverRequests.find(r=>(r.driver_id===user.id||r.driver_id===user.code)&&r.status==="accepted");
  const myLaundryCode = acceptedReq?.laundry_code || user.laundry_code;
  const myLaundry = laundries.find(l=>l.code===myLaundryCode);
  const partnerHotels = myLaundry ? hotels.filter(h=>h.laundry_code===user.laundry_code) : [];



  const tabs=[
    {key:"მთავარი",    icon:"🏠", label:"მთავარი"},
    {divider:true},
    {key:"დავალებები", icon:"📋", label:"დავალებები"},
    {key:"ისტორია",    icon:"📅", label:"ისტორია"},
    {divider:true},
    {key:"პარტნ_სამრ", icon:"🤝", label:"პარტნ. სამრ."},
    {key:"სამრ_სია",   icon:"🧺", label:"სამრ. სია"},
    {key:"სასტ_სია",   icon:"🏨", label:"სასტ. სია"},
    {divider:true},
    {key:"d_note",     icon:"📝", label:"Note"},
    {divider:true},
    {key:"d_report",   icon:"📊", label:"რეპორტი"},
    {divider:true},
    {key:"პროფილი",    icon:"👤", label:"პროფილი"},
    {key:"help",       icon:"💬", label:"Help"},
  ];
  const driverBottomTabs=[
    {key:"მთავარი",   icon:"🏠",label:"მთავარი"},
    {key:"დავალებები",icon:"📋",label:"დავალება"},
    {key:"ისტორია",   icon:"📅",label:"ისტორია"},
    {key:"პროფილი",   icon:"👤",label:"პროფილი"},
    {key:"help",      icon:"💬",label:"Help"},
  ];

  const DRIVER_CARDS = [
    { icon:"📋", label:"დავალებები",       sub:`${today.length} ახალი`,          dest:"დავალებები",    bg:"#EBF2FF", c:"#1B6EF3" },
    { icon:"📅", label:"ისტორია",           sub:`${done.length} დასრულ.`,         dest:"ისტორია",       bg:"#F0EBFF", c:"#7C3AED" },
    { icon:"🤝", label:"პარტნიორი სამრ.",  sub:myLaundry?myLaundry.name:"—",    dest:"პარტნ_სამრ",   bg:"#E6F9F1", c:"#12B76A" },
    { icon:"🧺", label:"სამრეცხაოები",     sub:`${laundries.length} სამრ.`,      dest:"სამრ_სია",     bg:"#FFF1F2", c:"#E11D48" },
    { icon:"🏨", label:"სასტუმროები",      sub:`${partnerHotels.length} სასტ.`,  dest:"სასტ_სია",     bg:"#FEF3C7", c:"#F59E0B" },
    { icon:"📝", label:"Note",             sub:`${driverNotes.filter(n=>!n.done).length} ტასკი`, dest:"d_note", bg:"#FFFBEB", c:"#B45309" },
    { icon:"📊", label:"რეპორტი",           sub:`${done.length} შეს.`,            dest:"d_report",     bg:"#F0F9FF", c:"#0369A1" },
  ];

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",background:darkMode?"#0F172A":"#F8FAFC"}}>
      <TrialBanner user={user}/>
      <Header color={color} grad={grad} icon="🚗" title="მძღოლის პანელი"
        onBell={()=>setShowDriverNotifs(v=>!v)}
        bellBadge={myDriverReqs.length+cancelledOrders.length}
        sub={
          <p style={{margin:0,color:"rgba(255,255,255,0.9)",fontWeight:800,fontSize:18}}>
            გამარჯობა, {user.name}
          </p>
        }
      />
      {showDriverNotifs && (
        <div style={{position:"absolute",top:70,right:12,zIndex:300,
          background:"#fff",borderRadius:14,boxShadow:"0 4px 24px rgba(0,0,0,0.15)",
          width:280,maxHeight:320,overflowY:"auto",padding:12}}>
          <p style={{margin:"0 0 10px",fontWeight:700,fontSize:13}}>📬 პარტნიორობის მოთხოვნები</p>
          {myDriverReqs.length===0&&cancelledOrders.length===0&&<p style={{color:"#9CA3AF",fontSize:12,textAlign:"center",margin:"12px 0"}}>შეტყობინება არ არის</p>}
          {cancelledOrders.map(n=>(
            <div key={n.id} style={{...S.card,marginBottom:6,padding:"8px 10px",background:"#FEE2E2"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <p style={{margin:0,fontSize:12,fontWeight:600,color:"#991B1B",flex:1}}>{n.msg}</p>
                <button onClick={()=>onDriverRequestResponse&&onDriverRequestResponse(n.id,"mark_read")}
                  style={{background:"none",border:"none",color:"#991B1B",cursor:"pointer",fontSize:13,padding:0,marginLeft:6}}>✕</button>
              </div>
              <p style={{margin:"3px 0 0",fontSize:10,color:"#991B1B",opacity:0.7}}>{n.time}</p>
            </div>
          ))}
          {myDriverReqs.map(r=>(
            <div key={r.id} style={{background:"#F0EBFF",borderRadius:12,padding:"10px 12px",marginBottom:8}}>
              <p style={{margin:"0 0 6px",fontSize:13,fontWeight:600,color:"#7C3AED"}}>
                🧺 {r.laundry_name}
              </p>
              <p style={{margin:"0 0 8px",fontSize:11,color:"#6B7280"}}>გთხოვს პარტნიორობას</p>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{onDriverRequestResponse(r.id,true);setShowDriverNotifs(false);}}
                  style={{flex:1,background:"#7C3AED",color:"#fff",border:"none",
                    borderRadius:8,padding:"7px 0",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                  ✅ დათანხმება
                </button>
                <button onClick={()=>{onDriverRequestResponse(r.id,false);setShowDriverNotifs(false);}}
                  style={{flex:1,background:"none",border:"1.5px solid #EF4444",color:"#EF4444",
                    borderRadius:8,padding:"7px 0",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                  ❌ უარი
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{flex:1,display:"flex",flexDirection:"row",minHeight:0,overflow:"hidden"}}>
      <NavBar tabs={tabs} bottomTabs={driverBottomTabs} active={tab} onChange={setTab} color={color} pinned={pinnedCardsD} onTogglePin={togglePinD}/>
      <div style={{flex:1,overflow:tab==="მთავარი"?"hidden":"auto"}}>
        {tab==="მთავარი" && (
          <div style={{padding:"12px",height:"100%",boxSizing:"border-box",display:"flex",flexDirection:"column",overflow:"hidden"}}
            onMouseUp={()=>{
              if(driverDragging!==null && driverOver!==null && driverDragging!==driverOver){
                setDriverCardOrder(prev=>{
                  const next=[...prev];
                  const from=next.indexOf(driverDragging);
                  const to=next.indexOf(driverOver);
                  next.splice(from,1); next.splice(to,0,driverDragging);
                  return next;
                });
              }
              setDriverDragging(null); setDriverOver(null);
            }}
            onTouchEnd={()=>{
              if(driverDragging!==null && driverOver!==null && driverDragging!==driverOver){
                setDriverCardOrder(prev=>{
                  const next=[...prev];
                  const from=next.indexOf(driverDragging);
                  const to=next.indexOf(driverOver);
                  next.splice(from,1); next.splice(to,0,driverDragging);
                  return next;
                });
              }
              setDriverDragging(null); setDriverOver(null);
            }}
            onTouchMove={e=>{
              if(driverDragging===null) return;
              e.preventDefault();
              const t=e.touches[0];
              const el=document.elementFromPoint(t.clientX,t.clientY);
              const card=el?.closest("[data-driveridx]");
              if(card){ const i=parseInt(card.getAttribute("data-driveridx")); if(i!==driverDragging) setDriverOver(i); }
            }}>
            <p style={{margin:"0 0 8px",fontSize:10,color:"#9CA3AF",flexShrink:0}}>✥ გადაათრიე გადასაწყობად</p>
            <div style={{flex:1,display:"grid",gridTemplateColumns:`repeat(${bp==="mobile"?2:4},1fr)`,gridTemplateRows:`repeat(${bp==="mobile"?4:2},1fr)`,gap:bp==="mobile"?8:12,minHeight:0}}>
              {(()=>{
                const pinnedWithCards=pinnedCardsD.filter(key=>DRIVER_CARDS.some(c=>c.dest===key));
                return pinnedWithCards.length>0
                  ? driverCardOrder.filter(i=>pinnedWithCards.includes(DRIVER_CARDS[i]?.dest))
                  : driverCardOrder;
              })().map(idx=>{
                const card=DRIVER_CARDS[idx];
                const isDragging=driverDragging===idx;
                const isOver=driverOver===idx;
                return (
                  <div key={card.label}
                    data-driveridx={idx}
                    onTouchStart={()=>setDriverDragging(idx)}
                    onMouseDown={()=>setDriverDragging(idx)}
                    onClick={()=>{ if(!isDragging) setTab(card.dest); }}
                    onMouseEnter={e=>{
                      if(driverDragging===null){
                        e.currentTarget.style.transform="translateY(-5px)";
                        e.currentTarget.style.boxShadow="0 8px 20px rgba(0,0,0,0.10)";
                        e.currentTarget.style.outline="none";
                      }
                    }}
                    onMouseLeave={e=>{
                      e.currentTarget.style.transform="translateY(0)";
                      e.currentTarget.style.boxShadow="none";
                      e.currentTarget.style.outline="none";
                    }}
                    style={{background:card.bg,borderRadius:16,padding:"18px 14px",
                      cursor:isDragging?"grabbing":"grab",
                      display:"flex",flexDirection:"column",alignItems:"center",
                      justifyContent:"center",gap:8,textAlign:"center",minHeight:110,
                      opacity:isDragging?0.45:1,
                      outline:isOver?`2.5px dashed ${card.c}`:"none",
                      transform:isOver&&!isDragging?"translateY(-5px)":"translateY(0)",
                      transition:"opacity 0.15s,outline 0.15s,transform 0.2s ease,box-shadow 0.2s ease",
                      userSelect:"none",WebkitUserSelect:"none",touchAction:"none"}}>
                    <span style={{fontSize:28}}>{card.icon}</span>
                    <div>
                      <p style={{margin:0,fontWeight:800,fontSize:13,color:card.c}}>{card.label}</p>
                      <p style={{margin:"3px 0 0",fontSize:11,color:card.c,opacity:0.75}}>{card.sub}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab==="პარტნ_სამრ" && (
          <div style={{padding:"14px 14px 14px"}}>
            <p style={{margin:"0 0 12px",fontWeight:800,fontSize:15}}>🤝 პარტნიორი სამრეცხაო</p>
            {myLaundry ? (
              <div style={{...S.card}}>
                <Ava name={myLaundry.name} size={44}/>
                <div style={{marginTop:10}}>
                  <p style={{margin:0,fontWeight:700,fontSize:15}}>{myLaundry.name}</p>
                  <p style={{margin:"3px 0 0",fontSize:12,color:"#6B7280"}}>{myLaundry.code}</p>
                  {myLaundry.phone   && <p style={{margin:"3px 0 0",fontSize:12,color:"#374151"}}>📞 {myLaundry.phone}</p>}
                  {myLaundry.address && <p style={{margin:"3px 0 0",fontSize:12,color:"#9CA3AF"}}>📍 {myLaundry.address}</p>}
                </div>
              </div>
            ) : (
              <p style={{color:"#9CA3AF",textAlign:"center",marginTop:40}}>სამრეცხაო არ არის</p>
            )}
          </div>
        )}

        {tab==="სამრ_სია" && (
          <div style={{padding:"14px 14px 14px"}}>
            <p style={{margin:"0 0 12px",fontWeight:800,fontSize:15}}>🧺 სამრეცხაოების სია</p>
            {[...new Set(laundries.filter(l=>l.city).map(l=>l.city))].sort().map(city=>{
              const items = laundries.filter(l=>l.city===city);
              const isCollapsed = collapsedLaundries.includes(city);
              return (
                <div key={city} style={{marginBottom:10}}>
                  <div onClick={()=>setCollapsedLaundries(p=>p.includes(city)?p.filter(x=>x!==city):[...p,city])}
                    style={{display:"flex",alignItems:"center",gap:8,
                      background:"#FFF1F2",borderRadius:10,padding:"9px 12px",
                      cursor:"pointer",marginBottom:isCollapsed?0:8}}>
                    <span style={{fontSize:13}}>📍</span>
                    <span style={{fontWeight:700,fontSize:13,color:"#E11D48",flex:1}}>{city}</span>
                    <span style={{fontSize:11,color:"#9CA3AF"}}>{items.length} სამრ.</span>
                    <span style={{fontSize:16,fontWeight:700,color:"#E11D48",marginLeft:4}}>{isCollapsed?"＋":"－"}</span>
                  </div>
                  {!isCollapsed && items.map(l=>{
                    const lOrders=orders.filter(o=>o.laundry_id===l.id);
                    const comp=lOrders.filter(o=>o.status==="COMPLETED").length;
                    const stars=Math.max(1,Math.round(1+(lOrders.length>0?comp/lOrders.length:0)*4));
                    return (
                      <div key={l.id} style={{...S.card,display:"flex",alignItems:"flex-start",gap:12}}>
                        <Ava name={l.name} size={42}/>
                        <div style={{flex:1}}>
                          <p style={{margin:0,fontWeight:700,fontSize:14}}>{l.name}</p>
                          <p style={{margin:"2px 0 0",fontSize:12,color:"#6B7280"}}>{l.code}</p>
                          {l.phone && <p style={{margin:"2px 0 0",fontSize:12,color:"#374151"}}>📞 {l.phone}</p>}
                          <div style={{display:"flex",alignItems:"center",gap:6,marginTop:4}}>
                            <span style={{fontSize:13,color:"#F59E0B"}}>{"★".repeat(stars)}{"☆".repeat(5-stars)}</span>
                            <span style={{fontSize:10,color:"#9CA3AF"}}>{comp}/{lOrders.length} შეკვ.</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {tab==="სასტ_სია" && (
          <div style={{padding:"14px 14px 14px"}}>
            <p style={{margin:"0 0 12px",fontWeight:800,fontSize:15}}>🏨 პარტნიორების სია</p>
            {hotels.filter(h=>h.city).length===0 && <p style={{color:"#9CA3AF",textAlign:"center",marginTop:40}}>სასტუმრო არ არის</p>}
            {[...new Set(hotels.filter(h=>h.city).map(h=>h.city))].sort().map(city=>{
              const items = hotels.filter(h=>h.city===city);
              const isCollapsed = collapsedHotels.includes(city);
              return (
                <div key={city} style={{marginBottom:10}}>
                  <div onClick={()=>setCollapsedHotels(p=>p.includes(city)?p.filter(x=>x!==city):[...p,city])}
                    style={{display:"flex",alignItems:"center",gap:8,
                      background:"#FEF3C7",borderRadius:10,padding:"9px 12px",
                      cursor:"pointer",marginBottom:isCollapsed?0:8}}>
                    <span style={{fontSize:13}}>📍</span>
                    <span style={{fontWeight:700,fontSize:13,color:"#F59E0B",flex:1}}>{city}</span>
                    <span style={{fontSize:11,color:"#9CA3AF"}}>{items.length} სასტ.</span>
                    <span style={{fontSize:16,fontWeight:700,color:"#F59E0B",marginLeft:4}}>{isCollapsed?"＋":"－"}</span>
                  </div>
                  {!isCollapsed && items.map(h=>(
                    <div key={h.id} style={{...S.card,display:"flex",alignItems:"center",gap:12}}>
                      <Ava name={h.name} size={42}/>
                      <div style={{flex:1}}>
                        <p style={{margin:0,fontWeight:700,fontSize:14}}>{h.name}</p>
                        <p style={{margin:"2px 0 0",fontSize:12,color:"#6B7280"}}>{h.code}</p>
                        {h.city       && <p style={{margin:"2px 0 0",fontSize:12,color:"#6B7280"}}>🏙️ {h.city}</p>}
                        {h.address    && <p style={{margin:"2px 0 0",fontSize:12,color:"#9CA3AF"}}>📍 {h.address}</p>}
                        {h.rooms_count && <p style={{margin:"2px 0 0",fontSize:12,color:"#6B7280"}}>🛏️ {h.rooms_count} ნომერი</p>}
                        {h.phone      && <p style={{margin:"2px 0 0",fontSize:12,color:"#374151"}}>📞 {h.phone}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {tab==="d_note" && (
          <NoteTab notes={driverNotes} setNotes={setDriverNotes} color={color}/>
        )}

        {tab==="d_report" && (
          <ReportsPage orders={orders} invoices={[]} userId={user.id} userCode={user.code} userRole="driver" color={color}/>
        )}

        {tab==="დავალებები" && (()=>{
          const laundryGroups = [...new Set(today.map(o=>o.laundry_name||"სხვა"))];
          return (
          <div style={{padding:"14px 14px 14px"}}>
            <p style={{margin:"0 0 12px",fontWeight:800,fontSize:15}}>დღევანდელი დავალებები</p>
            {today.length===0 && <p style={{color:"#9CA3AF",textAlign:"center",marginTop:40}}>დავალება არ არის</p>}
            {laundryGroups.map(lname=>{
              const group = today.filter(o=>(o.laundry_name||"სხვა")===lname);
              return (
                <div key={lname} style={{marginBottom:14}}>
                  {laundryGroups.length>1 && (
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,
                      background:THEME.driver.light||"#F0EBFF",borderRadius:10,padding:"7px 12px"}}>
                      <span style={{fontSize:12}}>🧺</span>
                      <span style={{fontWeight:700,fontSize:12,color}}>{lname}</span>
                      <span style={{fontSize:11,color:"#9CA3AF",marginLeft:"auto"}}>{group.length} დავ.</span>
                    </div>
                  )}
                  {group.map(o=>(
              <div key={o.id} onClick={()=>setDetail(o)} style={{...S.card,cursor:"pointer"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                  <div>
                    <p style={{margin:0,fontWeight:700}}>#{o.order_number}</p>
                    <p style={{margin:"3px 0 2px",fontSize:12,color:"#6B7280"}}>
                      {["DRIVER_ASSIGNED","DRIVER_ON_THE_WAY","PICKED_UP"].includes(o.status)
                        ?"📦 Pickup — "+o.hotel_name:"🚚 მიტანა — "+o.hotel_name}
                    </p>
                    {o.pickup_eta && <p style={{margin:0,fontSize:12,color}}>🕐 {o.pickup_eta}</p>}
                    <p style={{margin:"3px 0 0",fontSize:11,color:"#9CA3AF"}}>{[o.sheets_count>0&&`🛏 ${o.sheets_count}ც.`,o.towels_kg>0&&`🧺 ${o.towels_kg}კგ`,o.covers_kg>0&&`🛌 ${o.covers_kg}კგ`,...(o.custom_items||[]).map(ci=>`${ci.name} ${ci.qty}${ci.unit}`)].filter(Boolean).join(' · ')}</p>
                  </div>
                  <Badge status={o.status}/>
                </div>
                <div style={{display:"flex",gap:8}}>
                  {o.status==="DRIVER_ASSIGNED" && (
                    <Btn color={color} small onClick={e=>{e.stopPropagation();onStatusChange(o.id,"DRIVER_ON_THE_WAY")}} style={{flex:1}}>🚗 გავემგზავრე</Btn>
                  )}
                  {o.status==="DRIVER_ON_THE_WAY" && (
                    <Btn color={color} small onClick={e=>{e.stopPropagation();onStatusChange(o.id,"PICKED_UP")}} style={{flex:1}}>📦 ავიღე</Btn>
                  )}
                  {o.status==="PICKED_UP" && (
                    <Btn color={color} small onClick={e=>{e.stopPropagation();onStatusChange(o.id,"RECEIVED_IN_LAUNDRY")}} style={{flex:1}}>🧺 ჩავაბარე</Btn>
                  )}
                  {o.status==="DELIVERY_DRIVER_ASSIGNED" && (
                    <Btn color={color} small onClick={e=>{e.stopPropagation();onStatusChange(o.id,"DELIVERY_ON_THE_WAY")}} style={{flex:1}}>🚗 მიტანა</Btn>
                  )}
                  {o.status==="DELIVERY_ON_THE_WAY" && (
                    <Btn color={color} small onClick={e=>{e.stopPropagation();onStatusChange(o.id,"DELIVERED")}} style={{flex:1}}>✅ ჩავაბარე</Btn>
                  )}
                  <button style={{background:"#F3F4F6",border:"none",borderRadius:8,
                    padding:"7px 12px",fontWeight:600,fontSize:12,cursor:"pointer"}}>🗺️ რუკა</button>
                </div>
              </div>
                  ))}
                </div>
              );
            })}
          </div>
          );
        })()}

        {tab==="ისტორია" && (()=>{
          const [histFilter,setHistFilter] = [window.__dHistFilter,window.__dSetHistFilter||((v)=>{window.__dHistFilter=v;})];
          // Use local state via a trick - history filter stored on window for simplicity
          const hf = window.__dHistFilter||{sort:"date_desc",laundry:"",hotel:""};
          const setHF = (upd)=>{ window.__dHistFilter={...hf,...upd}; };

          const uniqueLaundries = [...new Set(done.map(o=>o.laundry_name||"სხვა"))];
          const uniqueHotels    = [...new Set(done.map(o=>o.hotel_name||"სხვა"))];

          let filtered = [...done];
          if(hf.laundry) filtered = filtered.filter(o=>o.laundry_name===hf.laundry);
          if(hf.hotel)   filtered = filtered.filter(o=>o.hotel_name===hf.hotel);
          if(hf.sort==="date_desc") filtered.sort((a,b)=>new Date(b.pickup_date)-new Date(a.pickup_date));
          if(hf.sort==="date_asc")  filtered.sort((a,b)=>new Date(a.pickup_date)-new Date(b.pickup_date));

          return (
          <div style={{padding:"14px 14px 14px"}}>
            <p style={{margin:"0 0 10px",fontWeight:800,fontSize:15}}>შესრულებული დავალებები
              <span style={{marginLeft:6,fontSize:12,fontWeight:400,color:"#9CA3AF"}}>{filtered.length} შეკვ.</span>
            </p>

            {/* Filters */}
            <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12,
              background:"#F8FAFC",borderRadius:12,padding:"10px 12px"}}>
              <div style={{display:"flex",gap:8}}>
                <select value={hf.laundry} onChange={e=>{setHF({laundry:e.target.value});setTab("ისტ2");setTimeout(()=>setTab("ისტორია"),0);}}
                  style={{...S.input,flex:1,fontSize:12,padding:"6px 8px"}}>
                  <option value="">🧺 ყველა სამრეცხაო</option>
                  {uniqueLaundries.map(l=><option key={l} value={l}>{l}</option>)}
                </select>
                <select value={hf.hotel} onChange={e=>{setHF({hotel:e.target.value});setTab("ისტ2");setTimeout(()=>setTab("ისტორია"),0);}}
                  style={{...S.input,flex:1,fontSize:12,padding:"6px 8px"}}>
                  <option value="">🏨 ყველა სასტუმრო</option>
                  {uniqueHotels.map(h=><option key={h} value={h}>{h}</option>)}
                </select>
              </div>
              <select value={hf.sort} onChange={e=>{setHF({sort:e.target.value});setTab("ისტ2");setTimeout(()=>setTab("ისტორია"),0);}}
                style={{...S.input,fontSize:12,padding:"6px 8px"}}>
                <option value="date_desc">📅 თარიღი — ახლიდან ძველამდე</option>
                <option value="date_asc">📅 თარიღი — ძველიდან ახლამდე</option>
              </select>
            </div>

            {filtered.length===0 && <p style={{color:"#9CA3AF",textAlign:"center",marginTop:40}}>ისტორია ცარიელია</p>}
            {filtered.map(o=>(
              <div key={o.id} onClick={()=>setDetail(o)} style={{...S.card,cursor:"pointer"}}>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <div>
                    <p style={{margin:0,fontWeight:700}}>#{o.order_number}</p>
                    <p style={{margin:"2px 0",fontSize:12,color:"#6B7280"}}>🏨 {o.hotel_name}</p>
                    <p style={{margin:"2px 0",fontSize:12,color:"#6B7280"}}>🧺 {o.laundry_name}</p>
                    <p style={{margin:"2px 0 0",fontSize:11,color:"#9CA3AF"}}>{new Date(o.pickup_date).toLocaleDateString("ka-GE")}</p>
                  </div>
                  <Badge status={o.status}/>
                </div>
              </div>
            ))}
          </div>
          );
        })()}

        {tab==="რეპორტი" && <ReportsPage orders={orders} invoices={[]} userId={user.id} userCode={user.code} userRole="driver" color={color}/>}

        {tab==="პროფილი" && (
          <DriverProfile user={user} color={color} />
        )}
        {tab==="help" && <HelpChat user={user}/>}
      </div>

      </div>

      {detail && <OrderDetailModal order={detail} onClose={()=>setDetail(null)}
        userRole="driver" color={color} onAction={onStatusChange}/>}
      {filter && (
        <Modal title={filter.label} onClose={()=>setFilter(null)}>
          {filter.orders.map(o=>(
            <div key={o.id} onClick={()=>{setDetail(o);setFilter(null);}} style={{...S.card,cursor:"pointer",marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <div>
                  <p style={{margin:0,fontWeight:700}}>#{o.order_number}</p>
                  <p style={{margin:"3px 0 0",fontSize:12,color:"#6B7280"}}>{o.hotel_name}</p>
                </div>
                <Badge status={o.status}/>
              </div>
            </div>
          ))}
        </Modal>
      )}
    </div>
  );
}

function RegLayout({ title, color, onBack, children }) {
  return (
    <div style={{height:"100%",background:"linear-gradient(160deg,#EBF2FF 0%,#F0EBFF 100%)",
      overflowY:"auto",padding:"20px 20px 40px"}}>
      <button onClick={onBack} style={{background:"none",border:"none",
        color:"#6B7280",cursor:"pointer",fontSize:14,marginBottom:16,padding:0}}>← უკან</button>
      <h2 style={{margin:"0 0 18px",fontWeight:800,fontSize:18,color:"#1e293b"}}>{title}</h2>
      <div style={{display:"flex",flexDirection:"column",gap:11}}>{children}</div>
    </div>
  );
}
function RegField({ label, placeholder="", type="text", value, onChange }) {
  const [ro, setRo] = useState(true);
  return (
    <label style={S.lbl}>{label}
      <input type={type} placeholder={placeholder} value={value}
        readOnly={ro}
        onFocus={()=>setRo(false)}
        autoComplete={type==="password"?"new-password":"nope"}
        onChange={e=>onChange(e.target.value)} style={S.input}/>
    </label>
  );
}
function ErrMsg({ children }) {
  return <p style={{color:"#EF4444",fontSize:12,margin:"0 0 2px",background:"#FEE2E2",
    padding:"6px 10px",borderRadius:8}}>{children}</p>;
}

function HotelRegisterForm({ onBack, onRegister }) {
  const [f,setF]=useState({hotel_name:"",company_name:"",company_id:"",contact:"",phone:"",email:"",city:"",address:"",rooms_count:"",client_type:"",password:"",password2:""});
  const [err,setErr]=useState("");
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const color=THEME.hotel.primary;
  const go=()=>{
    if(!f.hotel_name.trim()){setErr("ორგანიზაციის დასახელება შეიყვანე");return;}
    if(!f.company_name.trim()){setErr("კომპანიის დასახელება შეიყვანე");return;}
    if(!f.contact.trim()){setErr("საკონტაქტო პირი შეიყვანე");return;}
    if(!f.phone.trim()){setErr("ტელეფონი შეიყვანე");return;}
    if(!f.email.trim()){setErr("მეილი შეიყვანე");return;}
    if(f.password.length<6||!/[0-9]/.test(f.password)||!/[a-zA-Zა-ჰ]/.test(f.password)){setErr("პაროლი მინ. 6 სიმბოლო, უნდა შეიცავდეს ასოს და ციფრს");return;}
    if(f.password!==f.password2){setErr("პაროლები არ ემთხვევა");return;}
    onRegister({role:"hotel",...f});
  };
  return (
    <RegLayout title="🏨 სასტუმრო / კლინიკის რეგისტრაცია" color={color} onBack={onBack}>
      {err&&<ErrMsg>{err}</ErrMsg>}
      <div style={{marginBottom:10}}>
        <label style={{display:"block",fontSize:13,fontWeight:600,marginBottom:4,color:"#374151"}}>ორგანიზაციის ტიპი</label>
        <select value={f.client_type} onChange={e=>set("client_type",e.target.value)} style={S.input}>
          <option value="">— აირჩიე —</option>
          <option value="hotel">სასტუმრო</option>
          <option value="clinic">კლინიკა</option>
        </select>
      </div>
      <RegField label="ორგანიზაციის დასახელება" placeholder="ორგანიზაციის დასახელება" value={f.hotel_name} onChange={v=>set("hotel_name",v)}/>
      <RegField label="კომპანიის დასახელება" placeholder="Sunshine LLC" value={f.company_name} onChange={v=>set("company_name",v)}/>
      <RegField label="საიდენტ. კოდი" placeholder="205XXXXXX" value={f.company_id} onChange={v=>set("company_id",v)}/>
      <RegField label="საკონტაქტო პირი" placeholder="გიორგი ბერიძე" value={f.contact} onChange={v=>set("contact",v)}/>
      <RegField label="ტელეფონი" placeholder="+995 5XX XX XX XX" value={f.phone} onChange={v=>set("phone",v)}/>
      <RegField label="ელ-ფოსტა" placeholder="hotel@example.com" type="email" value={f.email} onChange={v=>set("email",v)}/>
      <RegField label="ქალაქი" placeholder="თბილისი" value={f.city} onChange={v=>set("city",v)}/>
      <RegField label="მისამართი" placeholder="რუსთაველის გამზ. 10" value={f.address} onChange={v=>set("address",v)}/>
      <RegField label="ნომრების რაოდენობა" placeholder="მაგ. 45" type="number" value={f.rooms_count} onChange={v=>set("rooms_count",v)}/>
      <RegField label="პაროლი" type="password" value={f.password} onChange={v=>set("password",v)}/>
      <RegField label="პაროლის დადასტურება" type="password" value={f.password2} onChange={v=>set("password2",v)}/>
      <Btn color={color} onClick={go} style={{width:"100%",marginTop:6}}>რეგისტრაცია</Btn>
    </RegLayout>
  );
}

function LaundryRegisterForm({ onBack, onRegister }) {
  const [f,setF]=useState({name:"",company_name:"",company_id:"",contact:"",phone:"",email:"",city:"",address:"",password:"",password2:""});
  const [err,setErr]=useState("");
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const color=THEME.laundry.primary;
  const go=()=>{
    if(!f.name.trim()){setErr("სამრეცხაოს დასახელება შეიყვანე");return;}
    if(!f.company_name.trim()){setErr("კომპანიის დასახელება შეიყვანე");return;}
    if(!f.contact.trim()){setErr("საკონტაქტო პირი შეიყვანე");return;}
    if(!f.phone.trim()){setErr("ტელეფონი შეიყვანე");return;}
    if(!f.email.trim()){setErr("მეილი შეიყვანე");return;}
    if(f.password.length<6||!/[0-9]/.test(f.password)||!/[a-zA-Zა-ჰ]/.test(f.password)){setErr("პაროლი მინ. 6 სიმბოლო, უნდა შეიცავდეს ასოს და ციფრს");return;}
    if(f.password!==f.password2){setErr("პაროლები არ ემთხვევა");return;}
    onRegister({role:"laundry",...f});
  };
  return (
    <RegLayout title="🧺 სამრეცხაოს რეგისტრაცია" color={color} onBack={onBack}>
      {err&&<ErrMsg>{err}</ErrMsg>}
      <RegField label="სამრეცხაოს დასახელება" placeholder="Clean and Fresh" value={f.name} onChange={v=>set("name",v)}/>
      <RegField label="კომპანიის დასახელება" placeholder="CleanPro LLC" value={f.company_name} onChange={v=>set("company_name",v)}/>
      <RegField label="საიდენტ. კოდი" placeholder="205XXXXXX" value={f.company_id} onChange={v=>set("company_id",v)}/>
      <RegField label="საკონტაქტო პირი" placeholder="ნინო კვარაცხელია" value={f.contact} onChange={v=>set("contact",v)}/>
      <RegField label="ტელეფონი" placeholder="+995 5XX XX XX XX" value={f.phone} onChange={v=>set("phone",v)}/>
      <RegField label="ელ-ფოსტა" placeholder="laundry@example.com" type="email" value={f.email} onChange={v=>set("email",v)}/>
      <RegField label="ქალაქი" placeholder="თბილისი" value={f.city} onChange={v=>set("city",v)}/>
      <RegField label="მისამართი" placeholder="რუსთაველის გამზ. 45" value={f.address} onChange={v=>set("address",v)}/>
      <RegField label="პაროლი" type="password" value={f.password} onChange={v=>set("password",v)}/>
      <RegField label="პაროლის დადასტურება" type="password" value={f.password2} onChange={v=>set("password2",v)}/>
      <Btn color={color} onClick={go} style={{width:"100%",marginTop:6}}>რეგისტრაცია</Btn>
    </RegLayout>
  );
}

function DriverRegisterForm({ onBack, onRegister }) {
  const [f,setF]=useState({name:"",phone:"",email:"",id_number:"",password:"",password2:""});
  const [err,setErr]=useState("");
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const color=THEME.driver.primary;
  const go=()=>{
    if(!f.name.trim()){setErr("სახელი და გვარი შეიყვანე");return;}
    if(!f.phone.trim()){setErr("ტელეფონი შეიყვანე");return;}
    if(f.password.length<6||!/[0-9]/.test(f.password)||!/[a-zA-Zა-ჰ]/.test(f.password)){setErr("პაროლი მინ. 6 სიმბოლო, უნდა შეიცავდეს ასოს და ციფრს");return;}
    if(f.password!==f.password2){setErr("პაროლები არ ემთხვევა");return;}
    onRegister({role:"driver",...f});
  };
  return (
    <RegLayout title="🚗 მძღოლის რეგისტრაცია" color={color} onBack={onBack}>
      {err&&<ErrMsg>{err}</ErrMsg>}
      <RegField label="სახელი და გვარი" placeholder="გიორგი მამულაშვილი" value={f.name} onChange={v=>set("name",v)}/>
      <RegField label="პირადი ნომერი" placeholder="XXXXXXXXXXX" value={f.id_number} onChange={v=>set("id_number",v)}/>
      <RegField label="ტელეფონი" placeholder="+995 5XX XX XX XX" value={f.phone} onChange={v=>set("phone",v)}/>
      <RegField label="ელ-ფოსტა (არასავალდებულო)" placeholder="driver@example.com" type="email" value={f.email} onChange={v=>set("email",v)}/>
      <RegField label="პაროლი" type="password" value={f.password} onChange={v=>set("password",v)}/>
      <RegField label="პაროლის დადასტურება" type="password" value={f.password2} onChange={v=>set("password2",v)}/>
      <Btn color={color} onClick={go} style={{width:"100%",marginTop:6}}>რეგისტრაცია</Btn>
    </RegLayout>
  );
}

/* ── FIX #2: ForgotPasswordForm — receives role prop, no dropdown, role-specific title ── */
function ForgotPasswordForm({ onBack, role, hotels, laundries, drivers }) {
  const [method, setMethod] = useState(null); // null | "email" | "phone"
  const [contact, setContact] = useState("");
  const [code, setCode] = useState("");
  const [found, setFound] = useState(null);
  const [newPass, setNewPass] = useState("");
  const [newPass2, setNewPass2] = useState("");
  const [step, setStep] = useState("method"); // method → contact → code → pass → done
  const [err, setErr] = useState("");

  const titles = {
    hotel:   "🏨 სასტუმროს პაროლის აღდგენა",
    laundry: "🧺 სამრეცხაოს პაროლის აღდგენა",
    driver:  "🚗 მძღოლის პაროლის აღდგენა",
  };

  const findByContact = () => {
    setErr("");
    const list = role==="hotel" ? hotels : role==="laundry" ? laundries : drivers;
    const val = contact.trim().toLowerCase();
    const u = method==="email"
      ? list.find(x=>x.email && x.email.toLowerCase()===val)
      : list.find(x=>x.phone && x.phone.replace(/\s/g,"")===val.replace(/\s/g,""));
    if(!u){ setErr(method==="email"?"ამ მეილით ანგარიში ვერ მოიძებნა":"ამ ნომრით ანგარიში ვერ მოიძებნა"); return; }
    setFound(u);
    setStep("code");
  };

  const verifyCode = () => {
    if(code.trim()==="0000"||code.trim().length===4){ setStep("pass"); setErr(""); }
    else setErr("კოდი არასწორია (გამოიყენე: 0000)");
  };

  const reset = () => {
    if(newPass.length<6||!/[0-9]/.test(newPass)||!/[a-zA-Zა-ჰ]/.test(newPass)){setErr("პაროლი მინ. 6 სიმბოლო, უნდა შეიცავდეს ასოს და ციფრს");return;}
    if(newPass!==newPass2){setErr("პაროლები არ ემთხვევა");return;}
    found.password=newPass;
    setStep("done");
  };

  const btnStyle = (active) => ({
    display:"flex", alignItems:"center", justifyContent:"center", gap:10,
    width:"100%", padding:"14px 16px", borderRadius:12,
    border:`2px solid ${active?"#1B6EF3":"#E5E7EB"}`,
    background:active?"#EBF2FF":"#fff",
    color:active?"#1B6EF3":"#374151",
    cursor:"pointer", fontSize:14, fontWeight:500, marginBottom:10,
  });

  return (
    <RegLayout title={titles[role]||"🔑 პაროლის აღდგენა"} color="#6B7280" onBack={onBack}>
      {step==="done" && (
        <div style={{textAlign:"center",padding:"20px 0"}}>
          <p style={{fontSize:40,margin:"0 0 12px"}}>✅</p>
          <p style={{fontWeight:700,fontSize:15}}>პაროლი წარმატებით შეიცვალა!</p>
          <Btn color="#1B6EF3" onClick={onBack} style={{marginTop:16}}>შესვლაზე დაბრუნება</Btn>
        </div>
      )}
      {step==="method" && (
        <>
          <p style={{fontSize:13,color:"#6B7280",margin:"0 0 16px"}}>
            აირჩიე პაროლის აღდგენის მეთოდი:
          </p>
          <button style={btnStyle(method==="email")} onClick={()=>setMethod("email")}>
            <span style={{fontSize:20}}>📧</span>
            <div style={{textAlign:"left"}}>
              <div style={{fontWeight:600}}>ელ-ფოსტით</div>
              <div style={{fontSize:11,color:"#9CA3AF"}}>კოდი გაიგზავნება მეილზე</div>
            </div>
          </button>
          <button style={btnStyle(method==="phone")} onClick={()=>setMethod("phone")}>
            <span style={{fontSize:20}}>📱</span>
            <div style={{textAlign:"left"}}>
              <div style={{fontWeight:600}}>SMS-ით</div>
              <div style={{fontSize:11,color:"#9CA3AF"}}>კოდი გაიგზავნება ტელეფონზე</div>
            </div>
          </button>
          {method && (
            <Btn color="#1B6EF3" onClick={()=>setStep("contact")} style={{width:"100%",marginTop:8}}>
              გაგრძელება →
            </Btn>
          )}
        </>
      )}
      {step==="contact" && (
        <>
          <p style={{fontSize:13,color:"#6B7280",margin:"0 0 12px"}}>
            {method==="email"?"შეიყვანე რეგისტრაციისას მითითებული ელ-ფოსტა:":"შეიყვანე რეგისტრაციისას მითითებული ტელეფონის ნომერი:"}
          </p>
          <RegField
            label={method==="email"?"ელ-ფოსტა":"ტელეფონი"}
            placeholder={method==="email"?"example@gmail.com":"+995 5XX XX XX XX"}
            type={method==="email"?"email":"tel"}
            value={contact} onChange={v=>setContact(v)}/>
          {err&&<ErrMsg>{err}</ErrMsg>}
          <Btn color="#1B6EF3" onClick={findByContact} style={{width:"100%",marginTop:12}}>
            კოდის გაგზავნა
          </Btn>
        </>
      )}
      {step==="code" && (
        <>
          <div style={{background:"#F0FDF4",borderRadius:10,padding:12,marginBottom:14}}>
            <p style={{margin:0,fontSize:12,color:"#065F46"}}>
              {method==="email"?`✅ კოდი გაიგზავნა: ${found?.email||contact}`:`✅ კოდი გაიგზავნა: ${found?.phone||contact}`}
            </p>
            <p style={{margin:"4px 0 0",fontSize:11,color:"#6B7280"}}>(დემო: გამოიყენე 0000)</p>
          </div>
          <RegField label="დადასტურების კოდი" placeholder="4-ნიშნა კოდი"
            value={code} onChange={v=>setCode(v)}/>
          {err&&<ErrMsg>{err}</ErrMsg>}
          <Btn color="#1B6EF3" onClick={verifyCode} style={{width:"100%",marginTop:12}}>
            დადასტურება
          </Btn>
        </>
      )}
      {step==="pass" && (
        <>
          <div style={{background:"#F0FDF4",borderRadius:10,padding:12,marginBottom:12}}>
            <p style={{margin:0,fontWeight:700,fontSize:13}}>{found?.name}</p>
            <p style={{margin:"2px 0 0",fontSize:12,color:"#6B7280"}}>{found?.code}</p>
          </div>
          <RegField label="ახალი პაროლი" type="password" value={newPass} onChange={setNewPass}/>
          <RegField label="პაროლის დადასტურება" type="password" value={newPass2} onChange={setNewPass2}/>
          {err&&<ErrMsg>{err}</ErrMsg>}
          <Btn color="#1B6EF3" onClick={reset} style={{width:"100%",marginTop:10}}>
            პაროლის შეცვლა
          </Btn>
        </>
      )}
    </RegLayout>
  );
}


function PasswordChangeModal({ onClose }) {
  const [p1,setP1]=useState("");
  const [p2,setP2]=useState("");
  const [err,setErr]=useState("");
  const [busy,setBusy]=useState(false);
  const save=async()=>{
    setErr("");
    if(p1.length<6 || !/[a-zA-Zა-ჰ]/.test(p1) || !/[0-9]/.test(p1)){ setErr("მინიმუმ 6 სიმბოლო, ასო + ციფრი"); return; }
    if(p1!==p2){ setErr("პაროლები არ ემთხვევა"); return; }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password: p1 });
    setBusy(false);
    if(error){ setErr(error.message); return; }
    alert("პაროლი შეიცვალა");
    onClose();
  };
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:9999,
      display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{...S.card,width:"100%",maxWidth:380,padding:20}}>
        <h3 style={{margin:"0 0 14px",fontSize:16,fontWeight:800}}>🔑 პაროლის შეცვლა</h3>
        <RegField label="ახალი პაროლი" type="password" value={p1} onChange={setP1}/>
        <RegField label="პაროლის დადასტურება" type="password" value={p2} onChange={setP2}/>
        {err && <ErrMsg>{err}</ErrMsg>}
        <div style={{display:"flex",gap:8,marginTop:14}}>
          <button onClick={onClose} disabled={busy} style={{flex:1,background:"#E2E8F0",
            color:"#475569",border:"none",borderRadius:10,padding:"11px",
            fontWeight:700,fontSize:14,cursor:"pointer"}}>გაუქმება</button>
          <button onClick={save} disabled={busy} style={{flex:1,background:busy?"#94A3B8":"#1B6EF3",
            color:"#fff",border:"none",borderRadius:10,padding:"11px",
            fontWeight:700,fontSize:14,cursor:busy?"wait":"pointer"}}>შენახვა</button>
        </div>
      </div>
    </div>
  );
}
// -- Admin Panel --------------------------------------------
function AdminLoginScreen({ onLogin, onBack }) {
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const { dark } = useTheme();
  const go = async () => {
    setErr("");
    const { data: a, error: e } = await supabase.auth.signInWithPassword({ email:"admin@cpg.local", password: pass });
    if(e || !a || !a.user){ setErr("პაროლი არასწორია"); return; }
    const { data: prof } = await supabase.from('users').select('role').eq('auth_id', a.user.id).maybeSingle();
    if(!prof || prof.role !== 'admin'){ await supabase.auth.signOut(); setErr("პაროლი არასწორია"); return; }
    onLogin();
  };
  return (
    <div style={{height:"100%",display:"flex",flexDirection:"column",alignItems:"center",
      justifyContent:"center",padding:24,background:dark?"#0F172A":"#F8FAFC"}}>
      <div style={{width:"100%",maxWidth:340}}>
        <button onClick={onBack} style={{background:"none",border:"none",
          color:"#6B7280",cursor:"pointer",fontSize:14,marginBottom:20,padding:0}}>← უკან</button>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{fontSize:48,marginBottom:8}}>🔐</div>
          <h2 style={{margin:"0 0 4px",fontWeight:800,fontSize:20,color:dark?"#F1F5F9":"#111827"}}>Admin პანელი</h2>
          <p style={{margin:0,fontSize:13,color:"#6B7280"}}>Connective Platform Georgia</p>
        </div>
        <input type="password" placeholder="Admin პაროლი"
          value={pass} onChange={e=>{setPass(e.target.value);setErr("");}}
          onKeyDown={e=>e.key==="Enter"&&go()}
          autoComplete="new-password"
          style={{...S.input,width:"100%",boxSizing:"border-box",marginBottom:10,
            background:dark?"#1E293B":"#fff",color:dark?"#F1F5F9":"#111827"}}/>
        {err && <p style={{color:"#EF4444",fontSize:12,marginBottom:10}}>{err}</p>}
        <Btn color="#7C3AED" onClick={go} style={{width:"100%",padding:14,fontSize:15}}>შესვლა</Btn>
      </div>
    </div>
  );
}

function AdminPanel({ hotels, setHotels, laundries, setLaundries, drivers, setDrivers,
  orders, invoices, onBack }) {
  const { dark } = useTheme();
  const [tab, setTab] = useState("dashboard");
  const [search, setSearch] = useState("");
  const [showAdminPw, setShowAdminPw] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const handleAdminReset = async (code) => {
    setResetBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-reset", { body: { code } });
      if (error) {
        let msg = error.message || String(error);
        try { const ctx = await error.context.json(); if (ctx && ctx.error) msg = ctx.error; } catch (e) {}
        alert("შეცდომა: " + msg);
        setResetTarget(null);
        return;
      }
      setResetTarget(null);
      setResetResult(data);
    } catch (e) {
      alert("შეცდომა: " + (e && e.message ? e.message : String(e)));
      setResetTarget(null);
    } finally {
      setResetBusy(false);
    }
  };
  const bg = dark?"#0F172A":"#F1F5F9";
  const card = dark?"#1E293B":"#fff";
  const txt = dark?"#F1F5F9":"#111827";
  const sub = dark?"#94A3B8":"#6B7280";

  const now = new Date();

  // ── Stats ──
  const activeOrders = orders.filter(o=>o.status!=="COMPLETED"&&o.status!=="CANCELLED");
  const todayOrders  = orders.filter(o=>new Date(o.created_at).toDateString()===now.toDateString());
  const totalRev     = invoices.reduce((s,i)=>s+i.total,0);

  // ── Trial helpers ──
  const getTrialDays = (u) => {
    if(!u.trial_end) return null;
    const diff = Math.ceil((new Date(u.trial_end)-now)/(1000*60*60*24));
    return diff;
  };
  const getStatus = (u) => {
    if(u.sub_status==="active") return {label:"აქტიური",bg:"#D1FAE5",c:"#065F46"};
    if(u.sub_status==="suspended") return {label:"დაბლოკილი",bg:"#FEE2E2",c:"#991B1B"};
    const days = getTrialDays(u);
    if(days===null) return {label:"—",bg:"#F3F4F6",c:"#6B7280"};
    if(days>0) return {label:`Trial ${days}დ`,bg:"#FEF3C7",c:"#92400E"};
    return {label:"Trial ამოიწურა",bg:"#FEE2E2",c:"#991B1B"};
  };

  const extendTrial = async (setter, id, days=30) => {
    const end = new Date(); end.setDate(end.getDate()+days);
    const { error } = await supabase.from("users").update({trial_end:end.toISOString(),sub_status:"trial"}).eq("id",id);
    if(error){ alert("❌ ვერ შეინახა, სცადე თავიდან"); return; }
    setter(p=>p.map(u=>u.id===id?{...u,trial_end:end.toISOString(),sub_status:"trial"}:u));
  };
  const activate = async (setter, id) => {
    const { error } = await supabase.from("users").update({sub_status:"active",trial_end:null}).eq("id",id);
    if(error){ alert("❌ ვერ შეინახა, სცადე თავიდან"); return; }
    setter(p=>p.map(u=>u.id===id?{...u,sub_status:"active",trial_end:null}:u));
  };
  const suspend = async (setter, id) => {
    const { error } = await supabase.from("users").update({sub_status:"suspended"}).eq("id",id);
    if(error){ alert("❌ ვერ შეინახა, სცადე თავიდან"); return; }
    setter(p=>p.map(u=>u.id===id?{...u,sub_status:"suspended"}:u));
  };
  const deleteUser = (setter, id) => {
    if(window.confirm("ანგარიში წაიშლება. დარწმუნებული ხარ?"))
      setter(p=>p.filter(u=>u.id!==id));
  };

  // ── Tabs ──
  const suspectedUsers = [...hotels,...laundries,...drivers].filter(u=>u.suspected_abuse);

  const tabs = [
    {key:"dashboard",  icon:"📊", label:"Dashboard"},
    {key:"hotels",     icon:"🏨", label:`სასტუმრო (${hotels.length})`},
    {key:"laundries",  icon:"🧺", label:`სამრეცხაო (${laundries.length})`},
    {key:"drivers",    icon:"🚗", label:`მძღოლი (${drivers.length})`},
    {key:"orders",     icon:"📋", label:`შეკვეთები (${activeOrders.length})`},
    {key:"suspicious", icon:"🚨", label:`საეჭვო (${suspectedUsers.length})`},
  ];

  const UserRow = ({u, setter}) => {
    const st = getStatus(u);
    return (
      <div style={{background:card,borderRadius:12,padding:"12px 14px",marginBottom:8,
        border:dark?"1px solid #334155":"1px solid #E5E7EB"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
          <div>
            <p style={{margin:0,fontWeight:700,fontSize:13,color:txt}}>{u.name}</p>
            <p style={{margin:"2px 0 0",fontSize:11,color:sub}}>{u.code} · {u.phone||"—"}</p>
            {u.city && <p style={{margin:"1px 0 0",fontSize:11,color:sub}}>📍 {u.city}</p>}
          </div>
          <span style={{background:st.bg,color:st.c,borderRadius:999,
            fontSize:10,fontWeight:700,padding:"3px 10px",whiteSpace:"nowrap"}}>{st.label}</span>
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          <button onClick={()=>extendTrial(setter,u.id,30)} style={{
            background:"#FEF3C7",color:"#92400E",border:"none",borderRadius:8,
            padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>+30 Trial</button>
          <button onClick={()=>activate(setter,u.id)} style={{
            background:"#D1FAE5",color:"#065F46",border:"none",borderRadius:8,
            padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>✅ Activate</button>
          <button onClick={()=>suspend(setter,u.id)} style={{
            background:"#FEE2E2",color:"#991B1B",border:"none",borderRadius:8,
            padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>🚫 Block</button>
          <button onClick={()=>setResetTarget(u)} style={{
            background:"#DBEAFE",color:"#1E40AF",border:"none",borderRadius:8,
            padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>Reset</button>
          <button onClick={()=>deleteUser(setter,u.id)} style={{
            background:"#F3F4F6",color:"#6B7280",border:"none",borderRadius:8,
            padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>🗑 წაშლა</button>
        </div>
      </div>
    );
  };

  const filterUsers = (list) => search.trim()
    ? list.filter(u=>(u.name||"").toLowerCase().includes(search.toLowerCase())
        || (u.code||"").toLowerCase().includes(search.toLowerCase())
        || (u.city||"").toLowerCase().includes(search.toLowerCase()))
    : list;

  return (
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:bg}}>
      {/* Header */}
      <div style={{background:"linear-gradient(135deg,#4C1D95,#7C3AED)",
        padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <p style={{margin:0,fontWeight:800,fontSize:16,color:"#fff"}}>🔐 Admin პანელი</p>
          <p style={{margin:0,fontSize:11,color:"rgba(255,255,255,0.7)"}}>Connective Platform Georgia</p>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button onClick={()=>setShowAdminPw(true)} style={{background:"rgba(255,255,255,0.15)",border:"none",
            borderRadius:10,padding:"8px 14px",color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>
            🔑 პაროლი
          </button>
          <button onClick={onBack} style={{background:"rgba(255,255,255,0.15)",border:"none",
            borderRadius:10,padding:"8px 14px",color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>
            გასვლა →
          </button>
        </div>
      </div>
      {showAdminPw && <PasswordChangeModal onClose={()=>setShowAdminPw(false)}/>}
      {resetTarget && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:16}}>
          <div style={{background:dark?"#1E293B":"#fff",borderRadius:16,padding:24,maxWidth:360,width:"100%"}}>
            <p style={{margin:"0 0 12px",fontWeight:800,fontSize:16,color:txt}}>პაროლის განულება?</p>
            <p style={{margin:"0 0 4px",fontSize:13,color:txt}}>ამ მომხმარებელს მიმდინარე პაროლი გაუქმდება.</p>
            <p style={{margin:"0 0 8px",fontSize:13,fontWeight:700,color:"#DC2626"}}>ძველი პაროლით ვეღარ შევა.</p>
            <p style={{margin:"0 0 18px",fontSize:13,color:sub}}>{resetTarget.name} ({resetTarget.code})</p>
            <div style={{display:"flex",gap:8}}>
              <button disabled={resetBusy} onClick={()=>setResetTarget(null)} style={{flex:1,background:"#F3F4F6",color:"#374151",border:"none",borderRadius:10,padding:"10px",fontSize:13,fontWeight:600,cursor:"pointer"}}>გაუქმება</button>
              <button disabled={resetBusy} onClick={()=>handleAdminReset(resetTarget.code)} style={{flex:1,background:"#DC2626",color:"#fff",border:"none",borderRadius:10,padding:"10px",fontSize:13,fontWeight:700,cursor:"pointer",opacity:resetBusy?0.6:1}}>დიახ, რესეტი</button>
            </div>
          </div>
        </div>
      )}
      {resetResult && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:16}}>
          <div style={{background:dark?"#1E293B":"#fff",borderRadius:16,padding:24,maxWidth:360,width:"100%"}}>
            <p style={{margin:"0 0 6px",fontWeight:800,fontSize:16,color:txt}}>ახალი პაროლი</p>
            <p style={{margin:"0 0 12px",fontSize:12,color:sub}}>{resetResult.name} ({resetResult.code})</p>
            <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:14}}>
              <code style={{flex:1,background:dark?"#0F172A":"#F3F4F6",color:txt,borderRadius:8,padding:"12px 14px",fontSize:18,fontWeight:700,letterSpacing:1,textAlign:"center"}}>{resetResult.password}</code>
              <button onClick={()=>{navigator.clipboard.writeText(resetResult.password);}} style={{background:"#DBEAFE",color:"#1E40AF",border:"none",borderRadius:8,padding:"12px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>კოპირება</button>
            </div>
            <p style={{margin:"0 0 16px",fontSize:12,color:sub}}>გადაეცი კლიენტს. დახურვის შემდეგ პაროლი აღარ გამოჩნდება.</p>
            <button onClick={()=>setResetResult(null)} style={{width:"100%",background:"#4C1D95",color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer"}}>დახურვა</button>
          </div>
        </div>
      )}

      {/* Sidebar + Content */}
      <div style={{flex:1,display:"flex",overflow:"hidden"}}>
        {/* Sidebar */}
        <div style={{width:200,flexShrink:0,background:dark?"#1E293B":"#fff",
          borderRight:dark?"1px solid #334155":"1px solid #E5E7EB",
          display:"flex",flexDirection:"column",overflowY:"auto"}}>
          {tabs.map(t=>(
            <button key={t.key} onClick={()=>setTab(t.key)} style={{
              display:"flex",alignItems:"center",gap:10,padding:"12px 16px",
              border:"none",cursor:"pointer",textAlign:"left",
              borderLeft:`3px solid ${tab===t.key?"#7C3AED":"transparent"}`,
              background:tab===t.key?(dark?"rgba(124,58,237,0.15)":"#F3E8FF"):"transparent",
              color:tab===t.key?"#7C3AED":sub,fontWeight:tab===t.key?700:500,fontSize:13}}>
              <span style={{fontSize:16}}>{t.icon}</span>
              <span style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{flex:1,overflowY:"auto",padding:16}}>

          {/* DASHBOARD */}
          {tab==="dashboard" && (
            <div>
              <p style={{margin:"0 0 14px",fontWeight:800,fontSize:16,color:txt}}>📊 სტატისტიკა</p>

              {/* Trial alerts */}
              {(()=>{
                const allUsers=[...hotels,...laundries,...drivers];
                const expired=allUsers.filter(u=>u.trial_end&&u.sub_status!=="active"&&u.sub_status!=="suspended"&&Math.ceil((new Date(u.trial_end)-now)/(1000*60*60*24))<=0);
                const expiring=allUsers.filter(u=>u.trial_end&&u.sub_status!=="active"&&u.sub_status!=="suspended"&&Math.ceil((new Date(u.trial_end)-now)/(1000*60*60*24))>0&&Math.ceil((new Date(u.trial_end)-now)/(1000*60*60*24))<=7);
                if(expired.length===0&&expiring.length===0) return null;
                return (
                  <div style={{marginBottom:16}}>
                    {expired.length>0&&(
                      <div style={{background:"#FEE2E2",borderRadius:12,padding:12,marginBottom:8}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                          <p style={{margin:0,fontSize:13,fontWeight:700,color:"#991B1B"}}>🔴 Trial ამოიწურა ({expired.length})</p>
                          <button onClick={()=>{
                            expired.forEach(u=>{
                              const setter=hotels.find(h=>h.id===u.id)?setHotels:laundries.find(l=>l.id===u.id)?setLaundries:setDrivers;
                              extendTrial(setter,u.id,30);
                            });
                          }} style={{background:"#991B1B",color:"#fff",border:"none",borderRadius:8,
                            padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                            ყველას +30 Trial
                          </button>
                        </div>
                        {expired.map(u=>(
                          <div key={u.id} style={{display:"flex",justifyContent:"space-between",
                            alignItems:"center",marginBottom:4}}>
                            <span style={{fontSize:12,color:"#991B1B"}}>{u.name} · {u.code}</span>
                            <div style={{display:"flex",gap:4}}>
                              <button onClick={()=>{const s=hotels.find(h=>h.id===u.id)?setHotels:laundries.find(l=>l.id===u.id)?setLaundries:setDrivers;extendTrial(s,u.id,30);}}
                                style={{background:"#FEF3C7",color:"#92400E",border:"none",borderRadius:6,padding:"3px 8px",fontSize:10,cursor:"pointer"}}>+30</button>
                              <button onClick={()=>{const s=hotels.find(h=>h.id===u.id)?setHotels:laundries.find(l=>l.id===u.id)?setLaundries:setDrivers;activate(s,u.id);}}
                                style={{background:"#D1FAE5",color:"#065F46",border:"none",borderRadius:6,padding:"3px 8px",fontSize:10,cursor:"pointer"}}>✅</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {expiring.length>0&&(
                      <div style={{background:"#FEF3C7",borderRadius:12,padding:12,marginBottom:8}}>
                        <p style={{margin:"0 0 8px",fontSize:13,fontWeight:700,color:"#92400E"}}>🟡 Trial მალე ამოიწურება ({expiring.length})</p>
                        {expiring.map(u=>{
                          const d=Math.ceil((new Date(u.trial_end)-now)/(1000*60*60*24));
                          return(
                          <div key={u.id} style={{display:"flex",justifyContent:"space-between",
                            alignItems:"center",marginBottom:4}}>
                            <span style={{fontSize:12,color:"#92400E"}}>{u.name} · {d}დ</span>
                            <button onClick={()=>{const s=hotels.find(h=>h.id===u.id)?setHotels:laundries.find(l=>l.id===u.id)?setLaundries:setDrivers;extendTrial(s,u.id,30);}}
                              style={{background:"#FEF3C7",color:"#92400E",border:"1px solid #F59E0B",borderRadius:6,padding:"3px 8px",fontSize:10,cursor:"pointer"}}>+30 დღე</button>
                          </div>
                        )})}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                {[
                  {label:"სასტუმრო / აპარტამენტი",val:hotels.length,icon:"🏨",bg:"#EBF2FF",c:"#1B6EF3"},
                  {label:"სამრეცხაო",val:laundries.length,icon:"🧺",bg:"#E6F9F1",c:"#12B76A"},
                  {label:"მძღოლი",val:drivers.length,icon:"🚗",bg:"#F0EBFF",c:"#7C3AED"},
                  {label:"აქტ. შეკვ.",val:activeOrders.length,icon:"📋",bg:"#FEF3C7",c:"#F59E0B"},
                  {label:"დღ. შეკვ.",val:todayOrders.length,icon:"📅",bg:"#FFF1F2",c:"#E11D48"},
                  {label:"ბრუნვა ₾",val:totalRev.toFixed(0),icon:"💰",bg:"#F0FDF4",c:"#16A34A"},
                ].map(s=>(
                  <div key={s.label} style={{background:s.bg,borderRadius:14,padding:14}}>
                    <p style={{margin:0,fontSize:11,color:s.c,fontWeight:600}}>{s.icon} {s.label}</p>
                    <p style={{margin:"4px 0 0",fontWeight:800,fontSize:22,color:s.c}}>{s.val}</p>
                  </div>
                ))}
              </div>

              <p style={{margin:"0 0 10px",fontWeight:700,fontSize:13,color:txt}}>🔄 ბოლო შეკვეთები</p>
              {orders.slice(0,5).map(o=>(
                <div key={o.id} style={{background:card,borderRadius:10,padding:"10px 12px",
                  marginBottom:6,border:dark?"1px solid #334155":"1px solid #E5E7EB"}}>
                  <div style={{display:"flex",justifyContent:"space-between"}}>
                    <p style={{margin:0,fontSize:12,fontWeight:700,color:txt}}>{o.order_number}</p>
                    <span style={{fontSize:10,color:sub}}>{o.created_at?.slice(0,10)}</span>
                  </div>
                  <p style={{margin:"2px 0 0",fontSize:11,color:sub}}>{o.hotel_name} → {o.laundry_name||"—"}</p>
                  <p style={{margin:"2px 0 0",fontSize:10,color:"#7C3AED",fontWeight:600}}>{o.status}</p>
                </div>
              ))}
            </div>
          )}

          {/* HOTELS */}
          {tab==="hotels" && (
            <div>
              <p style={{margin:"0 0 10px",fontWeight:800,fontSize:16,color:txt}}>🏨 სასტუმროები</p>
              <input placeholder="ძებნა..." value={search} onChange={e=>setSearch(e.target.value)}
                style={{...S.input,width:"100%",boxSizing:"border-box",marginBottom:12,
                  background:card,color:txt}}/>
              {filterUsers(hotels).map(u=><UserRow key={u.id} u={u} setter={setHotels}/>)}
              {filterUsers(hotels).length===0 && <p style={{color:sub,textAlign:"center",marginTop:20}}>ვერ მოიძებნა</p>}
            </div>
          )}

          {/* LAUNDRIES */}
          {tab==="laundries" && (
            <div>
              <p style={{margin:"0 0 10px",fontWeight:800,fontSize:16,color:txt}}>🧺 სამრეცხაოები</p>
              <input placeholder="ძებნა..." value={search} onChange={e=>setSearch(e.target.value)}
                style={{...S.input,width:"100%",boxSizing:"border-box",marginBottom:12,
                  background:card,color:txt}}/>
              {filterUsers(laundries).map(u=><UserRow key={u.id} u={u} setter={setLaundries}/>)}
              {filterUsers(laundries).length===0 && <p style={{color:sub,textAlign:"center",marginTop:20}}>ვერ მოიძებნა</p>}
            </div>
          )}

          {/* DRIVERS */}
          {tab==="drivers" && (
            <div>
              <p style={{margin:"0 0 10px",fontWeight:800,fontSize:16,color:txt}}>🚗 მძღოლები</p>
              <input placeholder="ძებნა..." value={search} onChange={e=>setSearch(e.target.value)}
                style={{...S.input,width:"100%",boxSizing:"border-box",marginBottom:12,
                  background:card,color:txt}}/>
              {filterUsers(drivers).map(u=><UserRow key={u.id} u={u} setter={setDrivers}/>)}
              {filterUsers(drivers).length===0 && <p style={{color:sub,textAlign:"center",marginTop:20}}>ვერ მოიძებნა</p>}
            </div>
          )}

          {/* SUSPICIOUS */}
          {tab==="suspicious" && (
            <div>
              <p style={{margin:"0 0 10px",fontWeight:800,fontSize:16,color:txt}}>🚨 საეჭვო რეგისტრაციები</p>
              {suspectedUsers.length===0 ? (
                <div style={{textAlign:"center",padding:"40px 0",color:sub}}>
                  <p style={{fontSize:32}}>✅</p>
                  <p style={{fontSize:13}}>საეჭვო რეგისტრაცია არ აღმოჩენილა</p>
                </div>
              ) : suspectedUsers.map(u=>(
                <div key={u.id} style={{background:"#FEF2F2",borderRadius:12,padding:"12px 14px",
                  marginBottom:8,border:"1px solid #FECACA"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <div>
                      <p style={{margin:0,fontWeight:700,fontSize:13,color:"#991B1B"}}>⚠️ {u.name}</p>
                      <p style={{margin:"2px 0 0",fontSize:11,color:"#EF4444"}}>{u.code} · {u.role}</p>
                    </div>
                    <span style={{fontSize:10,background:"#FEE2E2",color:"#991B1B",
                      borderRadius:999,padding:"2px 8px",fontWeight:700}}>დუბლიკატი</span>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>{const s=hotels.find(h=>h.id===u.id)?setHotels:laundries.find(l=>l.id===u.id)?setLaundries:setDrivers;activate(s,u.id);}}
                      style={{background:"#D1FAE5",color:"#065F46",border:"none",borderRadius:8,padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>✅ Activate</button>
                    <button onClick={()=>{const s=hotels.find(h=>h.id===u.id)?setHotels:laundries.find(l=>l.id===u.id)?setLaundries:setDrivers;suspend(s,u.id);}}
                      style={{background:"#FEE2E2",color:"#991B1B",border:"none",borderRadius:8,padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>🚫 Block</button>
                    <button onClick={()=>{const s=hotels.find(h=>h.id===u.id)?setHotels:laundries.find(l=>l.id===u.id)?setLaundries:setDrivers;deleteUser(s,u.id);}}
                      style={{background:"#F3F4F6",color:"#6B7280",border:"none",borderRadius:8,padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>🗑 წაშლა</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ORDERS */}
          {tab==="orders" && (
            <div>
              <p style={{margin:"0 0 10px",fontWeight:800,fontSize:16,color:txt}}>📋 შეკვეთები</p>
              <div style={{display:"flex",gap:8,marginBottom:12}}>
                {["ყველა","PENDING_LAUNDRY_APPROVAL","COMPLETED"].map(s=>(
                  <button key={s} onClick={()=>setSearch(s==="ყველა"?"":s)}
                    style={{padding:"6px 12px",borderRadius:8,border:"none",cursor:"pointer",fontSize:11,
                      background:search===(s==="ყველა"?"":s)?"#7C3AED":"#E5E7EB",
                      color:search===(s==="ყველა"?"":s)?"#fff":"#374151",fontWeight:600}}>
                    {s==="ყველა"?"ყველა":s==="PENDING_LAUNDRY_APPROVAL"?"⏳ მოლოდინი":"✅ დასრულებული"}
                  </button>
                ))}
              </div>
              {orders.filter(o=>!search||o.status===search).slice(0,50).map(o=>(
                <div key={o.id} style={{background:card,borderRadius:10,padding:"10px 12px",
                  marginBottom:6,border:dark?"1px solid #334155":"1px solid #E5E7EB"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <p style={{margin:0,fontSize:13,fontWeight:700,color:txt}}>{o.order_number}</p>
                    <span style={{fontSize:10,color:sub}}>{o.created_at?.slice(0,10)}</span>
                  </div>
                  <p style={{margin:"2px 0",fontSize:11,color:sub}}>
                    🏨 {o.hotel_name} → 🧺 {o.laundry_name||"—"}
                  </p>
                  <span style={{fontSize:10,fontWeight:700,
                    color:o.status==="COMPLETED"?"#16A34A":o.status.includes("PENDING")?"#F59E0B":"#7C3AED"}}>
                    {o.status}
                  </span>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
/* ── FIX #1 & #3: LoginPage — removed forgot btn from role selector, renamed on login form ── */

function HelpChat({ user }) {
  const [messages, setMessages] = useState([
    {role:"assistant", content:"გამარჯობა! მე ვარ Connective Platform-ის AI ასისტენტი 🤖\n\nროგორ შემიძლია დაგეხმაროთ?"}
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const { dark } = useTheme();

  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:"smooth"}); }, [messages]);

  const roleGeo = user.role==="hotel"?"სასტუმრო":user.role==="laundry"?"სამრეცხაო":"მძღოლი";
  const roleEmoji = user.role==="hotel"?"🏨":user.role==="laundry"?"🧺":"🚗";

  const roleInstructions = {
    hotel: `შენ ეხმარები სასტუმროს თანამშრომელს. პასუხები უნდა ეხებოდეს მხოლოდ:
- ახალი შეკვეთის შექმნა (ნივთები: თეთრეული, პირსახოცი, პოსტელი)
- შეკვეთის სტატუსის თვალყურისდევნება
- ინვოისების ნახვა და მართვა
- პარტნიორი სამრეცხაოების მართვა
- პარტნიორი მძღოლების მართვა
- ვაკანტური მძღოლების ნახვა
- Note, დასუფთავება, შესყიდვების მართვა`,
    laundry: `შენ ეხმარები სამრეცხაოს ოპერატორს. პასუხები უნდა ეხებოდეს მხოლოდ:
- შეკვეთების მართვა (დადასტურება, მძღოლის დანიშვნა)
- ინვოისების გენერირება
- პარტნიორი სასტუმროების მართვა
- მძღოლების მართვა
- ფასების სიის მართვა`,
    driver: `შენ ეხმარები მძღოლს. პასუხები უნდა ეხებოდეს მხოლოდ:
- დავალებების ნახვა და დადასტურება
- Pickup: "ავიღე სასტუმროდან" → "ჩავაბარე სამრეცხაოს"
- Delivery: "ავიღე სამრეცხაოდან" → "ჩავაბარე სასტუმროს"
- ისტორიის ნახვა
- პარტნიორი სამრეცხაოს მართვა`
  };

  const SYSTEM = `შენ ხარ Connective Platform Georgia-ს AI ასისტენტი.
მომხმარებელი: ${user.name} | პლატფორმა: ${roleEmoji} ${roleGeo} | კოდი: ${user.code}

${roleInstructions[user.role]||roleInstructions.hotel}

შეკვეთის ფლოუ:
ახალი → დადასტურდა → მძღოლი დაინიშნა → გზაშია → სასტუმროდან აიღო → სამრეცხაოს ჩაბარდა → მზადაა → მიტანაზე → ჩაბარდა → დასრულდა

წესები:
- პასუხე ქართულად, მოკლედ და გასაგებად
- გამოიყენე სწორი ქართული გრამატიკა
- პასუხი მიეც მხოლოდ ${roleGeo}ის პლატფორმის შესაბამისად
- სხვა პლატფორმების შესახებ კითხვაზე უთხარი: "ეს ფუნქცია თქვენს პლატფორმაზე არ არის"
- ტექნიკური პრობლემის შემთხვევაში: "გთხოვთ დაუკავშირდეთ ადმინისტრატორს: 📞 577 111 725"`;

  const send = async () => {
    if(!input.trim()||loading) return;
    const userMsg = {role:"user", content:input.trim()};
    setMessages(p=>[...p, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch("/.netlify/functions/chat", {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          system: SYSTEM,
          messages:[...messages, userMsg].filter(m=>m.role!=="system")
        })
      });
      const data = await res.json();
      if(!res.ok) throw new Error(`${res.status}: ${JSON.stringify(data)}`);
      const text = data.content?.[0]?.text || "პასუხი ვერ მივიღე.";
      setMessages(p=>[...p, {role:"assistant", content:text}]);
    } catch(e) {
      setMessages(p=>[...p, {role:"assistant", content:`❌ შეცდომა: ${e.message}\n\nგთხოვთ დაუკავშირდეთ: 📞 577 111 725`}]);
    }
    setLoading(false);
  };

  const bg = dark?"#0F172A":"#F8FAFC";
  const cardBg = dark?"#1E293B":"#fff";

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",background:bg}}>
      {/* Header */}
      <div style={{background:"linear-gradient(135deg,#1B6EF3,#7C3AED)",padding:"14px 16px",flexShrink:0}}>
        <p style={{margin:0,color:"#fff",fontWeight:800,fontSize:15}}>💬 Help — AI ასისტენტი</p>
        <p style={{margin:"2px 0 0",color:"rgba(255,255,255,0.8)",fontSize:11}}>
          კითხვა გაქვს? დაგეხმარები!
        </p>
      </div>

      {/* Messages */}
      <div style={{flex:1,overflowY:"auto",padding:"12px"}}>
        {messages.map((m,i)=>(
          <div key={i} style={{
            display:"flex",justifyContent:m.role==="user"?"flex-end":"flex-start",
            marginBottom:10}}>
            <div style={{
              maxWidth:"80%",
              background:m.role==="user"?"#1B6EF3":cardBg,
              color:m.role==="user"?"#fff":dark?"#F1F5F9":"#111827",
              borderRadius:m.role==="user"?"14px 14px 4px 14px":"14px 14px 14px 4px",
              padding:"10px 14px",
              fontSize:13,lineHeight:1.5,
              boxShadow:"0 1px 4px rgba(0,0,0,0.08)",
              whiteSpace:"pre-wrap"
            }}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{display:"flex",justifyContent:"flex-start",marginBottom:10}}>
            <div style={{background:cardBg,borderRadius:"14px 14px 14px 4px",
              padding:"10px 14px",fontSize:13,color:"#9CA3AF",
              boxShadow:"0 1px 4px rgba(0,0,0,0.08)"}}>
              ✍️ იწერება...
            </div>
          </div>
        )}
        <div ref={bottomRef}/>
      </div>

      {/* Input */}
      <div style={{padding:"10px 12px",background:cardBg,
        borderTop:`1px solid ${dark?"#334155":"#F1F5F9"}`,
        display:"flex",gap:8,flexShrink:0}}>
        <input
          value={input}
          onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&send()}
          placeholder="კითხვა დაწერე..."
          style={{...S.input,flex:1,fontSize:13,padding:"10px 14px"}}
        />
        <button onClick={send} disabled={loading||!input.trim()} style={{
          background:loading||!input.trim()?"#E5E7EB":"#1B6EF3",
          color:loading||!input.trim()?"#9CA3AF":"#fff",
          border:"none",borderRadius:10,padding:"0 16px",
          fontWeight:700,fontSize:16,cursor:"pointer",flexShrink:0
        }}>➤</button>
      </div>

      {/* Phone fallback */}
      <div style={{padding:"8px 12px 10px",textAlign:"center",background:cardBg,
        borderTop:`1px solid ${dark?"#334155":"#F1F5F9"}`}}>
        <p style={{margin:0,fontSize:11,color:"#9CA3AF"}}>
          ადმინი: <a href="tel:577111725" style={{color:"#1B6EF3",fontWeight:700}}>📞 577 111 725</a>
        </p>
      </div>
    </div>
  );
}

function LoginPage({ laundries, hotels, drivers, onLogin, onRegisterHotel, onRegisterLaundry, onRegisterDriver, onAdminLogin, darkMode, setDarkMode }) {
  const inviteParams = useMemo(()=>{
    const p = new URLSearchParams(window.location.search);
    return {code: p.get("invite"), name: p.get("iname"), forHotel: p.get("for")};
  },[]);
  const [role,setRole]=useState(null);
  const [code,setCode]=useState("");
  const [pass,setPass]=useState("");
  const [err,setErr]=useState("");
  const [showForgot,setShowForgot]=useState(false);

  /* FIX #2: pass current role to ForgotPasswordForm */
  if (showForgot) return (
    <ForgotPasswordForm
      onBack={()=>setShowForgot(false)}
      role={role}
      hotels={hotels} laundries={laundries} drivers={drivers}/>
  );

  const dm = darkMode ? DM.dark : DM.light;

  if (!role) return (
    <div style={{height:"100%",background:dm.pageBg,
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:"100%",maxWidth:340}}>
        <div style={{display:"flex",justifyContent:"flex-end",marginBottom:16}}>
          <button onClick={()=>setDarkMode(v=>!v)} style={{
            background:darkMode?"#334155":"#E2E8F0",
            border:"none",borderRadius:999,padding:"7px 16px",
            cursor:"pointer",display:"flex",alignItems:"center",gap:6,
            fontSize:13,fontWeight:700,color:darkMode?"#F1F5F9":"#374151"}}>
            {darkMode ? "☀️ Light" : "🌙 Dark"}
          </button>
        </div>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{marginBottom:12,display:"flex",justifyContent:"center"}}>
          <div style={{
            width:88,height:88,borderRadius:18,
            background:"#0a0f1e",
            display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
            boxShadow:"0 8px 24px rgba(0,0,0,0.35)"}}>
            <img src="/logo192.png" alt="CPG" style={{width:72,height:72,objectFit:"contain"}}/>
          </div>
        </div>
          <h1 style={{margin:0,fontWeight:800,fontSize:22,color:dm.text}}>Connective Platform</h1>
        </div>
        {inviteParams.code && (
          <div style={{background:"#E6F9F1",borderRadius:12,padding:"10px 16px",
            marginBottom:16,width:"100%",boxSizing:"border-box",textAlign:"center"}}>
            <p style={{margin:0,fontSize:13,fontWeight:700,color:"#065F46"}}>
              🧺 {inviteParams.name||inviteParams.code} გიწვევთ!
            </p>
            <p style={{margin:"4px 0 0",fontSize:11,color:"#0F6E56"}}>
              დარეგისტრირდი სასტუმროდ და გახდი პარტნიორი
            </p>
          </div>
        )}
        {[
          {key:"hotel",   label:"სასტუმრო / კლინიკა",              icon:"🏢", color:"#1B6EF3"},
          {key:"laundry", label:"სამრეცხაო",              icon:"🫧", color:"#12B76A"},
          {key:"driver",  label:"მძღოლი",                icon:"🚚", color:"#7C3AED"},
          {key:"tech",    label:"ტექ სერვისი", icon:"🔧", color:"#DC2626"},
          {key:"supplier",label:"Market Place",     icon:"🛒", color:"#6D28D9"},
        ].map(r=>(
          <button key={r.key} onClick={()=>setRole(r.key)} style={{
            width:"100%",display:"flex",alignItems:"center",gap:14,padding:16,marginBottom:10,
            background:dm.roleBg,border:`2px solid ${dm.roleBorder}`,
            borderRadius:14,cursor:"pointer",textAlign:"left",
            transition:"transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s"}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=r.color;e.currentTarget.style.transform="translateY(-4px)";e.currentTarget.style.boxShadow=`0 8px 20px rgba(0,0,0,0.1)`;}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=dm.roleBorder;e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="none";}}>
            <span style={{fontSize:26}}>{r.icon}</span>
            <p style={{margin:0,fontWeight:700,color:r.color,fontSize:15}}>{r.label}</p>
          </button>
        ))}
      </div>
    </div>
  );

  const rc=THEME[role]?.primary||"#1B6EF3";
  const ph=role==="hotel"?"HTL-XXXX":role==="laundry"?"LND-XXXX":"DRV-XXX";
  const regFn=role==="hotel"?onRegisterHotel:role==="laundry"?onRegisterLaundry:onRegisterDriver;

  // Coming soon for new platforms
  if(role==="tech" || role==="supplier") {
    const isTech = role==="tech";
    const pColor = isTech?"#DC2626":"#6D28D9";
    const pBg    = isTech?"#FEF2F2":"#F5F3FF";
    return (
      <div style={{height:"100%",background:dm.pageBg,
        display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24}}>
        <div style={{width:"100%",maxWidth:340,textAlign:"center"}}>
          <button onClick={()=>setRole(null)} style={{display:"block",marginBottom:20,
            background:"none",border:"none",color:dm.sub,cursor:"pointer",fontSize:14}}>← უკან</button>
          <div style={{background:pBg,borderRadius:24,padding:32,marginBottom:20}}>
            <p style={{fontSize:52,margin:"0 0 12px"}}>{isTech?"🔧":"🛒"}</p>
            <h2 style={{margin:"0 0 8px",fontWeight:800,fontSize:20,color:pColor}}>
              {isTech?"ტექ სერვისი":"Market Place"}
            </h2>
            <p style={{margin:"0 0 16px",fontSize:13,color:"#6B7280",lineHeight:1.5}}>
              {isTech
                ?"ტექ სერვისის მიმწოდებლების პლატფორმა მალე გაიხსნება"
                :"Market Place პლატფორმა მალე გაიხსნება"}
            </p>
            <div style={{background:"rgba(255,255,255,0.7)",borderRadius:12,padding:"10px 16px"}}>
              <p style={{margin:0,fontSize:12,color:pColor,fontWeight:700}}>🚀 მალე · Coming Soon</p>
            </div>
          </div>
          <p style={{fontSize:12,color:dm.muted}}>
            დაინტერესების შემთხვევაში დაგვიკავშირდი
          </p>
        </div>
      </div>
    );
  }

  const doLogin=async ()=>{
    setErr("");
    const c = code.trim().toUpperCase();
    const email = c.toLowerCase()+"@cpg.local";
    const { data: authData, error: authErr } = await supabase.auth.signInWithPassword({ email, password: pass });
    if(authErr || !authData || !authData.user){ setErr("კოდი ან პაროლი არასწორია"); return; }
    let data, error;
    try {
      const res = await supabase.from('users').select('*').eq('auth_id', authData.user.id).maybeSingle();
      data = res.data; error = res.error;
    } catch(e){ error = e; }
    if(!data || error){ await supabase.auth.signOut(); setErr("პროფილი ვერ მოიძებნა"); return; }
    if(data.role !== role){ await supabase.auth.signOut(); setErr("კოდი ან პაროლი არასწორია"); return; }
    if(data.sub_status==="suspended"){ await supabase.auth.signOut(); setErr("❌ ანგარიში დაბლოკილია. დაუკავშირდით ადმინს: 📞 577 111 725"); return; }
    if(data.trial_end && data.sub_status!=="active"){
      const daysLeft=Math.ceil((new Date(data.trial_end)-new Date())/(1000*60*60*24));
      if(daysLeft<=0){ await supabase.auth.signOut(); setErr("⏰ Trial პერიოდი დასრულდა. გააქტიურებისთვის: 📞 577 111 725"); return; }
    }
    onLogin({...data, id:data.id||Date.now()});
  };

  return (
    <div style={{height:"100%",background:dm.pageBg,
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:"100%",maxWidth:340}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <button onClick={()=>setRole(null)} style={{background:"none",border:"none",
            color:dm.sub,cursor:"pointer",fontSize:14,padding:0}}>← უკან</button>
          <button onClick={()=>setDarkMode(v=>!v)} style={{
        background:darkMode?"#334155":"#E2E8F0",
        border:"none",borderRadius:999,padding:"7px 16px",
        cursor:"pointer",display:"flex",alignItems:"center",gap:6,
        fontSize:13,fontWeight:700,color:darkMode?"#F1F5F9":"#374151",
        boxShadow:"0 2px 8px rgba(0,0,0,0.12)"}}>
        {darkMode ? "☀️ Light" : "🌙 Dark"}
      </button>
        </div>
        <h2 style={{margin:"0 0 4px",fontWeight:800,fontSize:20,color:dm.text}}>კეთილი იყოს დაბრუნება</h2>
        <p style={{margin:"0 0 18px",fontSize:13,color:dm.sub}}>შედი კოდით</p>
        {err&&<p style={{color:"#EF4444",fontSize:13,marginBottom:10}}>{err}</p>}
        <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
          <input placeholder={ph} value={code} onChange={e=>setCode(e.target.value)}
            style={{...S.input,background:dm.input,borderColor:dm.inputBorder,color:dm.text}}/>
          <input type="password" placeholder="პაროლი" value={pass}
            onChange={e=>setPass(e.target.value)}
            style={{...S.input,background:dm.input,borderColor:dm.inputBorder,color:dm.text}}/>
        </div>
        <Btn color={rc} onClick={doLogin} style={{width:"100%",padding:14,fontSize:15}}>შესვლა</Btn>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:14}}>
          <p style={{margin:0,fontSize:13,color:dm.sub}}>
            ანგარიში არ გაქვს?{" "}
            <button onClick={regFn} style={{background:"none",border:"none",
              color:rc,fontWeight:700,cursor:"pointer",fontSize:13,padding:0}}>რეგისტრაცია</button>
          </p>
        </div>
      </div>
    </div>
  );
}

// ── One-time data reset ──────────────────────────────────────
(()=>{
  if(!localStorage.getItem("_v5_reset")){
    const keep = ["darkMode","_v4_reset"];
    Object.keys(localStorage).forEach(k=>{ if(!keep.includes(k)) localStorage.removeItem(k); });
    localStorage.setItem("_v5_reset","1");
  }
})();

export default function App() {
  const [darkMode,setDarkMode]=useLS("darkMode",false);
  const [laundries,setLaundries]=useState([]);
  const [hotels,setHotels]=useState([]);
  const [driversList,setDriversList]=useState([]);
  const [orders,setOrders]=useState([]);
  const [invoices,setInvoices]=useState([]);
  const [user,setUser]=useState(()=>{
    try { const s=localStorage.getItem('cpg_session'); return s?JSON.parse(s):null; } catch { return null; }
  });
  const [appLoading,setAppLoading]=useState(false);
  const [partnerRequests,setPartnerRequests]=useState([]);
  const [priceLists,setPriceLists]=useState({});
  const [driverRequests,setDriverRequests]=useState([]);
  const [priceNotifs,setPriceNotifs]=useState([]);

  // Load data from Supabase when user logs in
  const loadFromSupabase = async (loggedUser) => {
    if(!loggedUser) return;
    try {
      // Load all users
      const {data:sbUsers} = await supabase.from(loggedUser.role==='admin'?'users':'directory').select('*');
      if(sbUsers && sbUsers.length>0){
        const sbHotels   = sbUsers.filter(u=>u.role==='hotel');
        const sbLaundries= sbUsers.filter(u=>u.role==='laundry');
        const sbDrivers  = sbUsers.filter(u=>u.role==='driver');
        setHotels(prev=>{
          const merged=prev.map(h=>{const sb=sbHotels.find(s=>s.code===h.code);return sb?{...h,...sb}:h;});
          const localCodes=new Set(prev.map(h=>h.code));
          const newOnes=sbHotels.filter(h=>!localCodes.has(h.code));
          return [...merged,...newOnes];
        });
        setLaundries(prev=>{
          const localCodes=new Set(prev.map(l=>l.code));
          const newOnes=sbLaundries.filter(l=>!localCodes.has(l.code));
          return newOnes.length>0?[...prev,...newOnes]:prev;
        });
        setDriversList(prev=>{
          const localCodes=new Set(prev.map(d=>d.code));
          const newOnes=sbDrivers.filter(d=>!localCodes.has(d.code));
          return newOnes.length>0?[...prev,...newOnes]:prev;
        });
        // Vacant pool = registered drivers not bound to a laundry (free agents)
        setVacantDrivers(sbDrivers.filter(d=>!d.laundry_code));
      }
      // Load orders
      let ordersQuery = supabase.from('orders').select('*').order('created_at',{ascending:false});
      if(loggedUser.role==='hotel')   ordersQuery = ordersQuery.eq('hotel_code', loggedUser.code);
      if(loggedUser.role==='laundry') ordersQuery = ordersQuery.eq('laundry_code', loggedUser.code);
      if(loggedUser.role==='driver')  ordersQuery = ordersQuery.eq('driver_id', loggedUser.id);
      const {data:sbOrders} = await ordersQuery;
      if(sbOrders && sbOrders.length>0){
        setOrders(prev=>{
          const localNums = new Set(prev.map(o=>o.order_number));
          const newOnes = sbOrders.filter(o=>!localNums.has(o.order_number)).map(o=>({
            ...o, id:o.id||Date.now(),
            lineItems: o.line_items||[],
            custom_items: o.custom_items||[],
          }));
          return newOnes.length>0 ? [...newOnes,...prev] : prev;
        });
      }
      // Load invoices
      let invQuery = supabase.from('invoices').select('*').order('created_at',{ascending:false});
      if(loggedUser.role==='hotel')   invQuery = invQuery.eq('hotel_code', loggedUser.code);
      if(loggedUser.role==='laundry') invQuery = invQuery.eq('laundry_code', loggedUser.code);
      const {data:sbInvs} = await invQuery;
      if(sbInvs && sbInvs.length>0){
        setInvoices(prev=>{
          const localNums = new Set(prev.map(i=>i.invoice_number));
          const newOnes = sbInvs.filter(i=>!localNums.has(i.invoice_number)).map(i=>({
            ...i, id:i.id||Date.now(),
            lineItems: i.line_items||[],
          }));
          return newOnes.length>0 ? [...newOnes,...prev] : prev;
        });
      }
      // Load partner requests
      const {data:sbPartners} = await supabase.from('partner_requests')
        .select('*')
        .or(`from_code.eq.${loggedUser.code},to_code.eq.${loggedUser.code}`);
      if(sbPartners && sbPartners.length>0){
        setPartnerRequests(prev=>{
          const localIds = new Set(prev.map(r=>`${r.from_code}_${r.to_code}`));
          const newOnes = sbPartners.filter(r=>!localIds.has(`${r.from_code}_${r.to_code}`));
          return newOnes.length>0 ? [...newOnes,...prev] : prev;
        });
      }
      // Load price lists
      const {data:sbPriceLists} = await supabase.from('price_lists')
        .select('*')
        .or(`laundry_code.eq.${loggedUser.code},hotel_code.eq.${loggedUser.code}`);
      if(sbPriceLists && sbPriceLists.length>0){
        const plObj={};
        sbPriceLists.forEach(pl=>{
          plObj[`${pl.laundry_code}_${pl.hotel_code}`]={items:pl.items||[],updatedAt:pl.updated_at};
        });
        setPriceLists(prev=>({...plObj,...prev}));
      }
      // Load hotel driver partners
      if(loggedUser.role==='hotel'){
        const {data:sbPartnerDrivers} = await supabase.from('hotel_driver_partners')
          .select('*').eq('hotel_id', loggedUser.code);
        if(sbPartnerDrivers && sbPartnerDrivers.length>0){
          setHotelDriverPartners(sbPartnerDrivers.map(p=>({
            hotel_id: loggedUser.id,
            hotel_code: p.hotel_id,
            driver_id: p.driver_id,
          })));
        }
      }
      // Load notifications
      if(loggedUser.role==='hotel'){
        const {data:sbNotifs} = await supabase.from('notifications')
          .select('*').eq('hotel_code', loggedUser.code).eq('read', false);
        if(sbNotifs && sbNotifs.length>0){
          setPriceNotifs(prev=>[...sbNotifs,...prev]);
        }
      }
      // Load driver requests
      if(loggedUser.role==='driver'||loggedUser.role==='laundry'){
        const drQuery = loggedUser.role==='driver'
          ? supabase.from('driver_requests').select('*').eq('driver_id',loggedUser.code)
          : supabase.from('driver_requests').select('*').eq('laundry_code',loggedUser.code);
        const {data:sbDriverReqs} = await drQuery;
        if(sbDriverReqs && sbDriverReqs.length>0){
          setDriverRequests(prev=>{
            const localIds=new Set(prev.map(r=>`${r.driver_id}_${r.laundry_code}`));
            const newOnes=sbDriverReqs.filter(r=>!localIds.has(`${r.driver_id}_${r.laundry_code}`));
            return newOnes.length>0?[...newOnes,...prev]:prev;
          });
        }
      }
    } catch(e){ console.error('Supabase load error:',e); }
  };
  const [regScreen,setRegScreen]=useState(null);
  const [adminScreen,setAdminScreen]=useState(
    ()=>window.location.hash==="#/admin"?"login":null
  );

  useEffect(()=>{
    const onHash=()=>{
      if(window.location.hash==="#/admin") setAdminScreen(s=>s||"login");
      else if(adminScreen==="login") setAdminScreen(null);
    };
    window.addEventListener("hashchange",onHash);
    return()=>window.removeEventListener("hashchange",onHash);
  },[]);
  const [vacantDrivers,setVacantDrivers]=useState([]);
  const [hotelDriverPartners,setHotelDriverPartners]=useState([]);

  window._logout=async ()=>{
    try { await supabase.auth.signOut(); } catch {}
    setUser(null);
    setRegScreen(null);
    try { localStorage.removeItem('cpg_session'); } catch {}
    if(window._supaChannel) supabase.removeChannel(window._supaChannel);
  };

  const handleLogin=async (loggedUser)=>{
    setAppLoading(true);
    setUser(loggedUser);
    try { localStorage.setItem('cpg_session', JSON.stringify(loggedUser)); } catch {}
    await loadFromSupabase(loggedUser);
    setAppLoading(false);
    // Real-time: watch orders changes
    if(window._supaChannel) supabase.removeChannel(window._supaChannel);
    const channel = supabase.channel('cpg_realtime_'+Date.now());
    channel.on('postgres_changes',{event:'INSERT',schema:'public',table:'orders'},(payload)=>{
        const o = payload.new;
        if(o.hotel_code===loggedUser.code || o.laundry_code===loggedUser.code || o.driver_id===loggedUser.code || o.driver_id===loggedUser.id){
          setOrders(prev=>{
            if(prev.some(x=>x.order_number===o.order_number)) return prev;
            return [{...o, id:o.id||Date.now(), custom_items:o.custom_items||[], lineItems:o.line_items||[]},...prev];
          });
        }
      })
      .on('postgres_changes',{event:'UPDATE',schema:'public',table:'orders'},(payload)=>{
        const o = payload.new;
        if(o.hotel_code===loggedUser.code || o.laundry_code===loggedUser.code || o.driver_id===loggedUser.code || o.driver_id===loggedUser.id){
          setOrders(prev=>{
            if(prev.some(x=>x.order_number===o.order_number)) return prev.map(x=>x.order_number===o.order_number?{...x,...o}:x);
            return [{...o, id:o.id||Date.now(), custom_items:o.custom_items||[], lineItems:o.line_items||[]},...prev];
          });
        }
      })
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'invoices'},(payload)=>{
        const inv = payload.new;
        if(inv.hotel_code===loggedUser.code || inv.laundry_code===loggedUser.code){
          setInvoices(prev=>{
            if(prev.some(x=>x.invoice_number===inv.invoice_number)) return prev;
            return [{...inv, id:inv.id||Date.now(), lineItems:inv.line_items||[]},...prev];
          });
        }
      })
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'driver_requests'},(payload)=>{
        const req = payload.new;
        if(req.driver_id===loggedUser.code || req.laundry_code===loggedUser.code){
          setDriverRequests(prev=>{
            if(prev.some(x=>x.driver_id===req.driver_id&&x.laundry_code===req.laundry_code&&x.status===req.status)) return prev;
            return [{...req, id:req.id||Date.now()},...prev];
          });
        }
      })
      .on('postgres_changes',{event:'UPDATE',schema:'public',table:'driver_requests'},(payload)=>{
        const req = payload.new;
        if(req.driver_id===loggedUser.code || req.laundry_code===loggedUser.code){
          setDriverRequests(prev=>prev.map(x=>
            (x.driver_id===req.driver_id&&x.laundry_code===req.laundry_code)
              ? {...x,...req} : x
          ));
        }
      });
    channel.subscribe();
    window._supaChannel = channel;
  };

  // Refresh-fix: if a session was restored from localStorage, reload its data once
  const sessionRestored = useRef(false);
  useEffect(()=>{
    if(user && !sessionRestored.current){
      sessionRestored.current = true;
      handleLogin(user);
    }
  },[]);



  const handleNewOrder=async form=>{
    const newOrder={
      hotel_id:user.id, hotel_name:user.name, hotel_code:user.code,
      laundry_id:form.laundry_id||user.laundry_id,
      laundry_name:form.laundry_name||user.laundry_name,
      laundry_code:form.laundry_code||user.laundry_code,
      driver_id:null, driver_name:null, status:"PENDING_LAUNDRY_APPROVAL",
      pickup_eta:null, delivery_eta:null, created_at:new Date().toISOString(), ...form,
    };
    // ჯერ Supabase — UI მხოლოდ დადასტურებული ჩაწერის შემდეგ
    const row = await sbWriteRow(supabase.from('orders').insert([{
        hotel_name: newOrder.hotel_name,
        hotel_code: newOrder.hotel_code,
        laundry_name: newOrder.laundry_name,
        laundry_code: newOrder.laundry_code,
        status: newOrder.status,
        pickup_date: form.pickup_date,
        sheets_count: form.sheets_count||0,
        sheets_unit: form.sheets_unit||'ც.',
        towels_kg: form.towels_kg||0,
        towels_unit: form.towels_unit||'კგ',
        covers_kg: form.covers_kg||0,
        covers_unit: form.covers_unit||'კგ',
        sheets_pieces: form.sheets_pieces||0,
        towels_pieces: form.towels_pieces||0,
        covers_pieces: form.covers_pieces||0,
        custom_items: form.custom_items||[],
        notes: form.notes||null,
        is_uncounted: form.is_uncounted||false,
        created_at: newOrder.created_at,
      }]).select().single(), 'შეკვეთის შენახვა');
    if (!row) return;
    const savedOrder={...newOrder, id:row.id, order_number:row.order_number};
    setOrders(p=>p.some(x=>x.order_number===savedOrder.order_number)?p:[savedOrder,...p]);
    // Notify laundry about new order
    setPriceNotifs(p=>[{
      id:Date.now(), forLaundry:form.laundry_code||user.laundry_code,
      hotelCode:user.code,
      msg:`📦 ახალი შეკვეთა — ${user.name}`,
      read:false, time:fmt24()
    },...p]);
    // Notify laundry about custom items without a price
    if(form.custom_items&&form.custom_items.length>0){
      const plKey=`${form.laundry_code}_${user.code}`;
      const pl=priceLists[plKey];
      const unpriced=form.custom_items.filter(ci=>!pl||!pl.items.find(it=>it.name===ci.name));
      if(unpriced.length>0){
        setPriceNotifs(p=>[{
          id:Date.now(),hotelCode:user.code,
          msg:`🔔 ${user.name}-მა დაამატა ახალი item-ი: ${unpriced.map(u=>u.name).join(", ")} — განსაზღვრე ფასი`,
          read:false,forLaundry:form.laundry_code,
          time:fmt24()
        },...p]);
      }
    }
  };

  const handleInvoice=async order=>{
    const key=`${order.laundry_code}_${order.hotel_code}`;
    const pl=priceLists[key];
    let lineItems=[];
    let sub=0;
    if(pl&&pl.items.length>0){
      const find=name=>pl.items.find(it=>it.name===name);
      const sheet=find("თეთრეული"); const towel=find("პირსახოცი"); const cover=find("გადასაფარებელი");
      if(sheet&&order.sheets_count>0){const amt=order.sheets_count*(parseFloat(sheet.price)||0);lineItems.push({name:"თეთრეული",qty:order.sheets_count,unit:sheet.unit||"ც.",price:sheet.price,total:amt});sub+=amt;}
      if(towel&&order.towels_kg>0){const amt=order.towels_kg*(parseFloat(towel.price)||0);lineItems.push({name:"პირსახოცი",qty:order.towels_kg,unit:towel.unit||"კგ",price:towel.price,total:amt});sub+=amt;}
      if(cover&&order.covers_kg>0){const amt=order.covers_kg*(parseFloat(cover.price)||0);lineItems.push({name:"გადასაფარებელი",qty:order.covers_kg,unit:cover.unit||"კგ",price:cover.price,total:amt});sub+=amt;}
      if(order.custom_items){order.custom_items.forEach(ci=>{const pi=find(ci.name);const pr=pi?parseFloat(pi.price)||0:0;const amt=ci.qty*pr;lineItems.push({name:ci.name,qty:ci.qty,unit:ci.unit,price:pr||"?",total:amt});sub+=amt;});}
    } else {
      sub=(order.sheets_count*2)+(order.towels_kg*5)+(order.covers_kg*4);
      lineItems=[
        {name:"თეთრეული",qty:order.sheets_count,unit:"ც.",price:2,total:order.sheets_count*2},
        {name:"პირსახოცი",qty:order.towels_kg,unit:"კგ",price:5,total:order.towels_kg*5},
        {name:"გადასაფარებელი",qty:order.covers_kg,unit:"კგ",price:4,total:order.covers_kg*4},
      ];
    }
    const tax=0; const now2=new Date();
    const newInv={
      order_id:order.id, order_number:order.order_number,
      hotel_id:order.hotel_id, hotel_name:order.hotel_name,
      hotel_code:order.hotel_code,
      laundry_id:order.laundry_id, laundry_name:order.laundry_name,
      laundry_code:order.laundry_code,
      lineItems, subtotal:sub, tax, total:sub+tax,
      status:"გადაუხდელია", paid_at:null,
      created_at:now2.toLocaleDateString("ka-GE"), date:now2,
    };
    const rowInv = await sbWriteRow(supabase.from('invoices').insert([{
        order_number: order.order_number,
        hotel_name: order.hotel_name,
        hotel_code: order.hotel_code,
        laundry_name: order.laundry_name,
        laundry_code: order.laundry_code,
        line_items: lineItems,
        subtotal: sub, tax, total: sub+tax,
        status: "გადაუხდელია",
      }]).select().single(), 'ინვოისის შენახვა');
    if (!rowInv) return;
    const savedInv={...newInv, id:rowInv.id, invoice_number:rowInv.invoice_number};
    setInvoices(p=>p.some(x=>x.invoice_number===savedInv.invoice_number)?p:[savedInv,...p]);
  };

  const handleDeleteOrder=async (id)=>{
    const o=orders.find(x=>x.id===id);
    if(!o) return;
    const okDel = await sbWrite(supabase.from('orders').delete().eq('order_number', o.order_number), 'შეკვეთის წაშლა');
    if (okDel === false) return;
    setOrders(p=>p.filter(x=>x.id!==id));
    // Notify laundry
    setPriceNotifs(p=>[{
      id:Date.now(), forLaundry:o.laundry_code,
      hotelCode:o.hotel_code||o.hotel_id,
      msg:`❌ ${o.hotel_name}-მა გააუქმა შეკვეთა #${o.order_number}`,
      read:false, time:fmt24()
    },...p]);
    // Notify driver if assigned
    if(o.driver_id){
      setDriverRequests(p=>[...p,{
        id:Date.now()+1, driver_id:o.driver_id,
        laundry_code:o.laundry_code, laundry_name:"სისტემა",
        msg:`❌ შეკვეთა #${o.order_number} გაუქმდა სასტუმროს მიერ`,
        status:"cancelled_order", read:false,
        time:fmt24()
      }]);
    }
  };

  const handleStatus=async (id,status,extra={})=>{
    const oCur = orders.find(x=>x.id===id);
    if(!oCur) return;
    const okSt = await sbWrite(
      supabase.from('orders')
        .update({status, updated_at:new Date().toISOString(), ...extra})
        .filter('order_number', 'eq', oCur.order_number),
      'სტატუსის განახლება');
    if (okSt === false) return;
    setOrders(prev=>prev.map(o=>o.id===id?{...o,status,...extra}:o));

    if(status==="COMPLETED"||status==="PARTIALLY_DELIVERED"){
      const o = orders.find(x=>x.id===id);
      if(!o) return;
      const order={...o,...extra,status};

      // Build invoice
      const prevCount = invoices.filter(inv=>inv.order_id===id).length;
      const suffix = prevCount>0 ? String.fromCharCode(65+prevCount) : "";

      let lineItems=[], sub=0;
      const confirmedItems = extra.confirmed_items||order.ready_items;
      const key=`${order.laundry_code}_${order.hotel_code}`;
      const pl=priceLists[key];

      if(status==="PARTIALLY_DELIVERED"||extra.confirmed_items){
        if(!confirmedItems||confirmedItems.length===0) return;
        confirmedItems.forEach(ci=>{
          const qty=ci.ready!=null?ci.ready:ci.qty;
          if(!qty||qty===0) return;
          const pr=pl?.items?parseFloat(pl.items.find(it=>it.name===ci.name)?.price||0):ci.unit==="ც."?2:5;
          const amt=qty*(pr||0);
          lineItems.push({name:ci.name,qty,unit:ci.unit,price:pr||"?",total:amt});
          sub+=amt;
        });
      } else {
        if(prevCount>0) return; // already has invoice
        if(pl&&pl.items.length>0){
          const find=name=>pl.items.find(it=>it.name===name);
          const sheet=find("თეთრეული"); const towel=find("პირსახოცი"); const cover=find("გადასაფარებელი");
          if(sheet&&order.sheets_count>0){const amt=order.sheets_count*(parseFloat(sheet.price)||0);lineItems.push({name:"თეთრეული",qty:order.sheets_count,unit:sheet.unit||"ც.",price:sheet.price,total:amt});sub+=amt;}
          if(towel&&order.towels_kg>0){const amt=order.towels_kg*(parseFloat(towel.price)||0);lineItems.push({name:"პირსახოცი",qty:order.towels_kg,unit:towel.unit||"კგ",price:towel.price,total:amt});sub+=amt;}
          if(cover&&order.covers_kg>0){const amt=order.covers_kg*(parseFloat(cover.price)||0);lineItems.push({name:"გადასაფარებელი",qty:order.covers_kg,unit:cover.unit||"კგ",price:cover.price,total:amt});sub+=amt;}
          if(order.custom_items){order.custom_items.forEach(ci=>{const pi=find(ci.name);const pr=pi?parseFloat(pi.price)||0:0;const amt=ci.qty*pr;lineItems.push({name:ci.name,qty:ci.qty,unit:ci.unit,price:pr||"?",total:amt});sub+=amt;});}
        } else {
          sub=(order.sheets_count*2)+(order.towels_kg*5)+(order.covers_kg*4);
          lineItems=[
            {name:"თეთრეული",qty:order.sheets_count,unit:"ც.",price:2,total:order.sheets_count*2},
            {name:"პირსახოცი",qty:order.towels_kg,unit:"კგ",price:5,total:order.towels_kg*5},
            {name:"გადასაფარებელი",qty:order.covers_kg,unit:"კგ",price:4,total:order.covers_kg*4},
          ];
        }
      }

      const now2=new Date();
      const orderNum=suffix?`${order.order_number}-${suffix}`:order.order_number;
      const newInv={
        order_id:order.id, order_number:orderNum,
        hotel_id:order.hotel_id, hotel_name:order.hotel_name, hotel_code:order.hotel_code,
        laundry_id:order.laundry_id, laundry_name:order.laundry_name, laundry_code:order.laundry_code,
        lineItems, subtotal:sub, tax:0, total:sub,
        status:"გადაუხდელია", paid_at:null,
        created_at:now2.toLocaleDateString("ka-GE"), date:now2,
      };
      const rowInv2 = await sbWriteRow(supabase.from('invoices').insert([{
          order_number:orderNum,
          hotel_name:order.hotel_name, hotel_code:order.hotel_code,
          laundry_name:order.laundry_name, laundry_code:order.laundry_code,
          line_items:lineItems, subtotal:sub, tax:0, total:sub,
          status:"გადაუხდელია",
        }]).select().single(), 'ინვოისის შენახვა');
      if (!rowInv2) return;
      const savedInv={...newInv, id:rowInv2.id, invoice_number:rowInv2.invoice_number};
      setInvoices(p=>p.some(x=>x.invoice_number===savedInv.invoice_number)?p:[savedInv,...p]);
    }
  };

  const handleRegHotel=async data=>{
    if(data.company_id){
      const {data:existing}=await supabase.rpc('company_id_lookup',{p_company_id:data.company_id});
      if(existing&&existing.length>0){alert(`❌ ეს საიდენტიფიკაციო კოდი უკვე რეგისტრირებულია.\n\nორგანიზაცია: ${existing[0].name||existing[0].code}`);return;}
    }
    const { data: code, error: codeErr } = await supabase.rpc('next_code', { p_role:'hotel' });
    if(codeErr || !code){ alert("❌ კოდის გენერაცია ვერ მოხერხდა. სცადეთ თავიდან."); return; }
    if(!data.client_type){ alert("❌ გთხოვთ აირჩიოთ ორგანიზაციის ტიპი (სასტუმრო ან კლინიკა)."); return; }
    const newUser={id:Date.now(),name:data.hotel_name,code,role:"hotel",
      company_name:data.company_name,company_id:data.company_id,
      contact:data.contact,phone:data.phone,email:data.email,
      city:data.city,address:data.address,rooms_count:data.rooms_count||"",
      laundry_code:null,laundry_id:null,laundry_name:null,
      sub_status:"trial", trial_end:new Date(Date.now()+30*24*60*60*1000).toISOString(),
      lat:41.69+Math.random()*0.04,lng:44.78+Math.random()*0.04};
    setHotels(p=>[...p,newUser]);
    if((data.password||"").length<6){ setHotels(p=>p.filter(h=>h.code!==code)); alert("❌ პაროლი უნდა იყოს მინიმუმ 6 სიმბოლო."); return; }
    let _authId;
    try {
      const { data:_au, error:_ae } = await supabase.auth.signUp({ email: code.toLowerCase()+"@cpg.local", password: data.password });
      if(_ae || !_au || !_au.user) throw (_ae || new Error("signup failed"));
      _authId = _au.user.id;
    } catch(_e){
      setHotels(p=>p.filter(h=>h.code!==code));
      console.error('signUp hotel error:',_e);
      alert("❌ რეგისტრაცია ვერ შესრულდა: "+((_e&&_e.message)||"სცადეთ თავიდან")+". მონაცემი არ შენახულა.");
      return;
    }
    try {
      const { error } = await supabase.from('users').insert([{
        code, role:"hotel", name:data.hotel_name, auth_id:_authId,
        phone:data.phone, email:data.email,
        city:data.city, address:data.address, contact:data.contact,
        company_name:data.company_name, company_id:data.company_id||"",
        rooms_count:data.rooms_count||"", client_type:data.client_type||"hotel",
        sub_status:"trial", trial_end:newUser.trial_end,
      }]);
      if(error){
        setHotels(p=>p.filter(h=>h.code!==code));
        console.error('Supabase reg hotel error:',error);
        alert(`❌ რეგისტრაცია ვერ შესრულდა.\n\n${error.message||error.details||"სცადეთ თავიდან."}\n\nმონაცემი არ შენახულა.`);
        return;
      }
    } catch(e){
      setHotels(p=>p.filter(h=>h.code!==code));
      console.error('Supabase reg hotel error:',e);
      alert(`❌ რეგისტრაცია ვერ შესრულდა (კავშირის შეცდომა).\n\nმონაცემი არ შენახულა. სცადეთ თავიდან.`);
      return;
    }
    const invP = new URLSearchParams(window.location.search);
    const invCode = invP.get("invite");
    if(invCode) {
      const invReq = {id:Date.now(), from_code:invCode, to_code:code, status:"accepted", created_at:new Date().toISOString()};
      setPartnerRequests(p=>[...p, invReq]);
      window.history.replaceState({}, "", window.location.pathname);
    }
    setRegScreen(null);
    alert(`✅ რეგისტრაცია წარმატებულია!\n\nშენი კოდი: ${code}\n${invCode?`\n🤝 ავტომატურად დაუკავშირდი ${invCode}-ს!`:""}.\n\nეს კოდი შეინახე — შესასვლელად დაგჭირდება.`);
  };

  const handleRegLaundry=async data=>{
    if(data.company_id){
      const {data:existing}=await supabase.rpc('company_id_lookup',{p_company_id:data.company_id});
      if(existing&&existing.length>0){alert(`❌ ეს საიდენტიფიკაციო კოდი უკვე რეგისტრირებულია.\n\nორგანიზაცია: ${existing[0].name||existing[0].code}`);return;}
    }
    const { data: code, error: codeErr } = await supabase.rpc('next_code', { p_role:'laundry' });
    if(codeErr || !code){ alert("❌ კოდის გენერაცია ვერ მოხერხდა. სცადეთ თავიდან."); return; }
    const newUser={id:Date.now(),name:data.name,code,role:"laundry",
      company_name:data.company_name,company_id:data.company_id,
      contact:data.contact,phone:data.phone,email:data.email,
      city:data.city,address:data.address,
      sub_status:"trial", trial_end:new Date(Date.now()+30*24*60*60*1000).toISOString()};
    setLaundries(p=>[...p,newUser]);
    if((data.password||"").length<6){ setLaundries(p=>p.filter(l=>l.code!==code)); alert("❌ პაროლი უნდა იყოს მინიმუმ 6 სიმბოლო."); return; }
    let _authId;
    try {
      const { data:_au, error:_ae } = await supabase.auth.signUp({ email: code.toLowerCase()+"@cpg.local", password: data.password });
      if(_ae || !_au || !_au.user) throw (_ae || new Error("signup failed"));
      _authId = _au.user.id;
    } catch(_e){
      setLaundries(p=>p.filter(l=>l.code!==code));
      console.error('signUp laundry error:',_e);
      alert("❌ რეგისტრაცია ვერ შესრულდა: "+((_e&&_e.message)||"სცადეთ თავიდან")+". მონაცემი არ შენახულა.");
      return;
    }
    try {
      const { error } = await supabase.from('users').insert([{
        code, role:"laundry", name:data.name, auth_id:_authId,
        phone:data.phone, email:data.email,
        city:data.city, address:data.address, contact:data.contact,
        company_name:data.company_name, company_id:data.company_id||"",
        sub_status:"trial", trial_end:newUser.trial_end,
      }]);
      if(error){
        setLaundries(p=>p.filter(l=>l.code!==code));
        console.error('Supabase reg laundry error:',error);
        alert(`❌ რეგისტრაცია ვერ შესრულდა.\n\n${error.message||error.details||"სცადეთ თავიდან."}\n\nმონაცემი არ შენახულა.`);
        return;
      }
    } catch(e){
      setLaundries(p=>p.filter(l=>l.code!==code));
      console.error('Supabase reg laundry error:',e);
      alert(`❌ რეგისტრაცია ვერ შესრულდა (კავშირის შეცდომა).\n\nმონაცემი არ შენახულა. სცადეთ თავიდან.`);
      return;
    }
    setRegScreen(null);
    alert(`✅ რეგისტრაცია წარმატებულია!\n\nშენი კოდი: ${code}\n\nეს კოდი გაუზიარე სასტუმროებს და მძღოლებს.`);
  };

  const handleRegDriver=async (data,driversList)=>{
    const allUsers=[...hotels,...laundries,...driversList];
    const dupes=checkDuplicates(data,allUsers);
    if(dupes.length>0){
      const msg=dupes.map(d=>`• ${d.user.name} (${d.user.code}): ${d.fields.join(", ")}`).join("\n");
      const go=window.confirm(`⚠️ მსგავსი მონაცემები უკვე არსებობს:\n\n${msg}\n\nAdmin-ს ეცნობება. გსურს გაგრძელება?`);
      if(!go) return;
    }
    const { data: code, error: codeErr } = await supabase.rpc('next_code', { p_role:'driver' });
    if(codeErr || !code){ alert("❌ კოდის გენერაცია ვერ მოხერხდა. სცადეთ თავიდან."); return; }
    const allUsers2=[...hotels,...laundries,...driversList];
    const dupeFlag=checkDuplicates(data,allUsers2).length>0;
    const trialEnd=new Date(Date.now()+30*24*60*60*1000).toISOString();
    setDriversList(p=>[...p,{id:Date.now(),name:data.name,code,role:"driver",
      id_number:data.id_number,phone:data.phone,email:data.email,
      laundry_code:null,available:true,
      sub_status:"trial", trial_end:trialEnd,
      suspected_abuse:dupeFlag}]);
    if((data.password||"").length<6){ setDriversList(p=>p.filter(d=>d.code!==code)); alert("❌ პაროლი უნდა იყოს მინიმუმ 6 სიმბოლო."); return; }
    let _authId;
    try {
      const { data:_au, error:_ae } = await supabase.auth.signUp({ email: code.toLowerCase()+"@cpg.local", password: data.password });
      if(_ae || !_au || !_au.user) throw (_ae || new Error("signup failed"));
      _authId = _au.user.id;
    } catch(_e){
      setDriversList(p=>p.filter(d=>d.code!==code));
      console.error('signUp driver error:',_e);
      alert("❌ რეგისტრაცია ვერ შესრულდა: "+((_e&&_e.message)||"სცადეთ თავიდან")+". მონაცემი არ შენახულა.");
      return;
    }
    try {
      const { error } = await supabase.from('users').insert([{
        code, role:"driver", name:data.name, auth_id:_authId,
        phone:data.phone, email:data.email,
        sub_status:"trial", trial_end:trialEnd,
      }]);
      if(error){
        setDriversList(p=>p.filter(d=>d.code!==code));
        console.error('Supabase reg driver error:',error);
        alert(`❌ რეგისტრაცია ვერ შესრულდა.\n\n${error.message||error.details||"სცადეთ თავიდან."}\n\nმონაცემი არ შენახულა.`);
        return;
      }
    } catch(e){
      setDriversList(p=>p.filter(d=>d.code!==code));
      console.error('Supabase reg driver error:',e);
      alert(`❌ რეგისტრაცია ვერ შესრულდა (კავშირის შეცდომა).\n\nმონაცემი არ შენახულა. სცადეთ თავიდან.`);
      return;
    }
    setRegScreen(null);
    alert(`✅ რეგისტრაცია წარმატებულია!\n\nშენი კოდი: ${code}\n\nეს კოდი შეინახე.`);
  };

  const handleSendPartnerRequest=async (fromUser, toCode)=>{
    const toHotel   = hotels.find(h=>h.code===toCode.trim().toUpperCase());
    const toLaundry = laundries.find(l=>l.code===toCode.trim().toUpperCase());
    // Also search Supabase if not found locally
    let target = toHotel || toLaundry;
    if(!target){
      try {
        const {data} = await supabase.from('directory').select('*').eq('code',toCode.trim().toUpperCase()).maybeSingle();
        if(data) target = data;
      } catch(e){}
    }
    if (!target) return { error:"კოდი ვერ მოიძებნა" };
    const exists = partnerRequests.find(r=>
      ((r.from_code===fromUser.code && r.to_code===target.code) ||
       (r.from_code===target.code && r.to_code===fromUser.code)) &&
      r.status==="pending"
    );
    if (exists) return { error:"მოთხოვნა უკვე გაგზავნილია" };
    const newReq = {
      id:Date.now(),
      from_code:fromUser.code, from_name:fromUser.name, from_role:fromUser.role,
      to_code:target.code, to_name:target.name, to_role:target.role,
      status:"pending",
    };
    const okPr = await sbWrite(supabase.from('partner_requests').insert([{
      from_code:fromUser.code, from_name:fromUser.name, from_role:fromUser.role,
      to_code:target.code, to_name:target.name, to_role:target.role,
      status:"pending",
    }]), 'პარტნიორობის მოთხოვნა');
    if (!okPr) return { error:'ვერ გაიგზავნა — სცადეთ თავიდან' };
    setPartnerRequests(p=>[...p, newReq]);
    return { ok:true, to_name:target.name };
  };

  const handlePartnerResponse=async (reqId, accept)=>{
    const req0 = partnerRequests.find(r=>r.id===reqId);
    if (req0) {
      const okResp = await sbWrite(supabase.from('partner_requests')
        .update({status:accept?"accepted":"rejected"})
        .eq('from_code', req0.from_code)
        .eq('to_code', req0.to_code)
        .eq('status','pending'), 'მოთხოვნაზე პასუხი');
      if (!okResp) return;
    }
    setPartnerRequests(p=>p.map(r=>r.id===reqId?{...r,status:accept?"accepted":"rejected"}:r));
    if (accept) {
      const req = partnerRequests.find(r=>r.id===reqId);
      if (!req) return;
      const hotelCode   = req.from_role==="hotel" ? req.from_code : req.to_code;
      const laundryCode = req.from_role==="laundry" ? req.from_code : req.to_code;
      const laundry = laundries.find(l=>l.code===laundryCode);
      if (laundry) {
        setHotels(p=>p.map(h=>h.code===hotelCode?{...h,
          laundry_code:laundryCode, laundry_id:laundry.id, laundry_name:laundry.name
        }:h));
        if (user?.code===hotelCode) {
          user.laundry_code=laundryCode; user.laundry_id=laundry.id; user.laundry_name=laundry.name;
        }
      }
    }
  };

  const shell = content => (
    <ThemeCtx.Provider value={{dark:darkMode, setDark:setDarkMode}}>
      <AppShell>{content}</AppShell>
    </ThemeCtx.Provider>
  );

  if (adminScreen==="login") return shell(
    <AdminLoginScreen onLogin={()=>{setAdminScreen("panel");loadFromSupabase({role:'admin',code:'ADMIN',id:0});}} onBack={()=>setAdminScreen(null)}/>
  );
  if (adminScreen==="panel") return shell(
    <AdminPanel
      hotels={hotels} setHotels={setHotels}
      laundries={laundries} setLaundries={setLaundries}
      drivers={driversList} setDrivers={setDriversList}
      orders={orders} invoices={invoices}
      onBack={()=>{setAdminScreen(null);window.location.hash="";}}
    />
  );

  if (regScreen==="hotel")   return shell(<HotelRegisterForm   onBack={()=>setRegScreen(null)} onRegister={handleRegHotel}/>);
  if (appLoading) return shell(
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",flexDirection:"column",gap:12}}>
      <div style={{fontSize:32}}>⏳</div>
      <p style={{fontSize:15,color:"#6B7280",fontWeight:600}}>იტვირთება...</p>
    </div>
  );
  if (appLoading) return shell(
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",flexDirection:"column",gap:12}}>
      <div style={{fontSize:32}}>⏳</div>
      <p style={{fontSize:15,color:"#6B7280",fontWeight:600}}>იტვირთება...</p>
    </div>
  );
  if (regScreen==="laundry") return shell(<LaundryRegisterForm onBack={()=>setRegScreen(null)} onRegister={handleRegLaundry}/>);
  if (regScreen==="driver")  return shell(<DriverRegisterForm  onBack={()=>setRegScreen(null)} onRegister={d=>handleRegDriver(d,driversList)}/>);

  if (!user) return shell(
    <LoginPage
      laundries={laundries} hotels={hotels} drivers={driversList}
      onLogin={handleLogin}
      onRegisterHotel={()=>setRegScreen("hotel")}
      onRegisterLaundry={()=>setRegScreen("laundry")}
      onRegisterDriver={()=>setRegScreen("driver")}
      onAdminLogin={()=>setAdminScreen("login")}
      darkMode={darkMode} setDarkMode={setDarkMode}
    />
  );

  const handleAddDriverPartner = async (driverId) => {
    const driver = driversList.find(d=>d.id===driverId);
    const driverCode = driver?.code||String(driverId);
    const okAdd = await sbWrite(supabase.from('hotel_driver_partners').insert([{
      hotel_id: user.code,
      driver_id: driverCode,
    }]), 'მძღოლის დამატება');
    if (!okAdd) return;
    setHotelDriverPartners(p=>[...p,{hotel_id:user.id, hotel_code:user.code, driver_id:driverId, driver_code:driverCode}]);
  };
  const handleRemoveDriverPartner = async (driverId) => {
    const driver = driversList.find(d=>d.id===driverId);
    const driverCode = driver?.code||String(driverId);
    const okRem = await sbWrite(supabase.from('hotel_driver_partners')
      .delete()
      .eq('hotel_id', user.code)
      .eq('driver_id', driverCode), 'მძღოლის მოხსნა');
    if (!okRem) return;
    setHotelDriverPartners(p=>p.filter(x=>!(x.hotel_id===user.id && x.driver_id===driverId)));
  };

  if (user.role==="hotel")   return shell(<HotelApp   user={user} orders={orders} invoices={invoices}
    hotels={hotels} laundries={laundries} drivers={driversList} vacantDrivers={vacantDrivers}
    hotelDriverPartners={hotelDriverPartners}
    onAddDriverPartner={handleAddDriverPartner}
    onRemoveDriverPartner={handleRemoveDriverPartner}
    onStatusChange={handleStatus} onNewOrder={handleNewOrder}
    partnerRequests={partnerRequests} onSendPartnerRequest={handleSendPartnerRequest}
    onPartnerResponse={handlePartnerResponse}
    priceLists={priceLists} priceNotifs={priceNotifs} setPriceNotifs={setPriceNotifs}
    onDeleteOrder={handleDeleteOrder}/>);
  if (user.role==="laundry") return shell(<LaundryApp user={user} orders={orders} drivers={driversList}
    hotels={hotels} laundries={laundries} invoices={invoices} onStatusChange={handleStatus}
    onCreateInvoice={handleInvoice} partnerRequests={partnerRequests}
    priceLists={priceLists} setPriceLists={setPriceLists}
    priceNotifs={priceNotifs} setPriceNotifs={setPriceNotifs}
    onAssignDriver={async driverId=>{
      const already=driverRequests.find(r=>r.driver_id===driverId&&r.laundry_code===user.code&&r.status==="pending");
      if(!already){
        const driver=driversList.find(d=>d.id===driverId);
        const driverCode=driver?.code||String(driverId);
        const newReq={id:Date.now(),driver_id:driverId,laundry_code:user.code,laundry_name:user.name,status:"pending"};
        const okReq = await sbWrite(supabase.from('driver_requests').insert([{
          driver_id:driverCode,
          laundry_code:user.code,
          laundry_name:user.name,
          status:"pending"
        }]), 'მძღოლის მოწვევა');
        if (!okReq) return;
        setDriverRequests(p=>[...p,newReq]);
      }
    }}
    driverRequests={driverRequests}
    onSendPartnerRequest={handleSendPartnerRequest} onPartnerResponse={handlePartnerResponse}/>);
  if (user.role==="driver")  return shell(<DriverApp  user={user} orders={orders} hotels={hotels} laundries={laundries} onStatusChange={handleStatus}
    driverRequests={driverRequests}
    onDriverRequestResponse={async (reqId,accept)=>{
      if(accept==="mark_read"){
        setDriverRequests(p=>p.map(r=>r.id===reqId?{...r,read:true}:r));
        return;
      }
      const req=driverRequests.find(r=>r.id===reqId);
      if(req){
        const okDr = await sbWrite(supabase.from('driver_requests')
          .update({status:accept?"accepted":"rejected"})
          .eq('driver_id',String(req.driver_id))
          .eq('laundry_code',req.laundry_code)
          .eq('status','pending'), 'მოწვევაზე პასუხი');
        if(!okDr) return;
        if(accept){
          // users.laundry_code: temporary sbWrite until bind_driver RPC (users-RLS stage)
          await sbWrite(supabase.from('users')
            .update({laundry_code:req.laundry_code})
            .eq('code',String(req.driver_id)), 'მძღოლის მიბმა');
        }
      }
      if(accept && req){
        setDriversList(p=>p.map(d=>String(d.id)===String(req.driver_id)?{...d,laundry_code:req.laundry_code}:d));
      }
      setDriverRequests(p=>p.map(r=>r.id===reqId?{...r,status:accept?"accepted":"rejected"}:r));
    }}/>);
  return null;
}